import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bot, FileDown, FileText, Loader2, Send, Plus, RotateCcw, Save, Trash2, Upload, ChevronDown, ChevronRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import Cuenta from "@/components/Cuenta";
import "@/rf.css";
import AnalisisCorporativo from "@/components/dashboard/AnalisisCorporativo";
import { ComoViene, MiniCurva, Pasos, ZonaCarga } from "@/components/dashboard/ac/Moderno";
import GrupoEconomico from "@/components/dashboard/ac/GrupoEconomico";
import { InmueblesPanel, ReferenciaModal, SateliteModal, aplicarRef, aplicarTasacion } from "@/components/dashboard/ac/InmueblesPanel";
import { InteligenciaCard } from "@/components/dashboard/ac/InteligenciaCard";
import { CapacidadPagoCard } from "@/components/dashboard/ac/CapacidadPagoCard";

const usd = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Math.round(Number(n));
  return (v < 0 ? "−" : "") + "US$ " + Math.abs(v).toLocaleString("es-PY");
};
const num = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("es-PY", { minimumFractionDigits: d, maximumFractionDigits: d }));

const COLOR = {
  verde: { box: "border-emerald-600 bg-emerald-50", txt: "text-emerald-700" },
  amarillo: { box: "border-yellow-600 bg-yellow-50", txt: "text-yellow-700" },
  naranja: { box: "border-orange-600 bg-orange-50", txt: "text-orange-700" },
  rojo: { box: "border-red-600 bg-red-50", txt: "text-red-700" },
};
const LINEA_V = {
  adecuada: { box: "border-emerald-600 bg-emerald-50", txt: "text-emerald-700", t: "LÍNEA ADECUADA" },
  baja: { box: "border-blue-600 bg-blue-50", txt: "text-blue-700", t: "HAY ESPACIO PARA AMPLIAR" },
  alta: { box: "border-red-600 bg-red-50", txt: "text-red-700", t: "LÍNEA POR ENCIMA DE LO RECOMENDADO" },
};
const OP = { ">=": "mínimo", "<=": "máximo", ">": "más de", "<": "menos de" };

function fmtValor(p, v) {
  if (v === null || v === undefined) return "no calculable";
  if (p.fmt === "usd") return usd(v);
  if (p.fmt === "bool") return v >= 1 ? "Sí" : "No";
  return num(v) + ({ x: "x", "%": "%", d: " días" }[p.fmt] || "");
}
function fmtRef(p) {
  if (p.fmt === "usd") return `${OP[p.op]} 0`;
  if (p.fmt === "bool") return "Sí";
  return `${OP[p.op]} ${num(p.ref)}${{ x: "x", "%": "%", d: " días" }[p.fmt] || ""}`;
}

/* ===== Resumen del balance: veredicto con medidor y tres gráficos que responden una pregunta cada uno =====
   Las barras crecen al entrar; tocando un año (o una columna) se resalta en los tres gráficos a la vez. */
const ARCO = 251.3;
const capital = (t) => { const s = String(t || "").toLowerCase(); return s.charAt(0).toUpperCase() + s.slice(1); };

/* Termómetro de Kanitz: velocímetro −7…+7 (rojo insolvente · amarillo penumbra · verde solvente). Mismo dibujo que el PDF. */
const KZ_COL = { rojo: "#b91c1c", amarillo: "#a16207", verde: "#15803d" };
const fmtKz = (v) => Number(v).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace("-", "−");
function KanitzGauge({ k }) {
  if (!k) return null;
  const ang = (v) => Math.PI - ((v - k.min) / (k.max - k.min)) * Math.PI;
  const pt = (v, r) => [100 + r * Math.cos(ang(v)), 100 - r * Math.sin(ang(v))];
  const arco = (v0, v1, color) => {
    const [x0, y0] = pt(v0, 80), [x1, y1] = pt(v1, 80);
    return <path key={color} d={`M ${x0} ${y0} A 80 80 0 0 1 ${x1} ${y1}`} stroke={color} strokeWidth="16" fill="none" />;
  };
  const [nx, ny] = pt(k.aguja, 58);
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-4" data-testid="ac-kanitz">
      <svg viewBox="0 0 200 112" className="w-[220px] max-w-full" role="img" aria-label={`Índice de solvencia ${fmtKz(k.fi)} ${k.zona}`}>
        {arco(k.min, -3, "#dc2626")}{arco(-3, 0, "#f59e0b")}{arco(0, k.max, "#16a34a")}
        {[k.min, -3, 0, k.max].map((v) => { const [x, y] = pt(v, 96); return <text key={v} x={x} y={y + 3} textAnchor="middle" fontSize="7" fill="#6b7280">{v > 0 ? `+${v}` : String(v).replace("-", "−")}</text>; })}
        <line x1="100" y1="100" x2={nx} y2={ny} stroke="#1f2937" strokeWidth="3" strokeLinecap="round" />
        <circle cx="100" cy="100" r="4.5" fill="#1f2937" />
        <text x="100" y="78" textAnchor="middle" fontSize="20" fontWeight="800" fill={KZ_COL[k.color]} data-testid="ac-kanitz-valor">{fmtKz(k.fi)}</text>
        <text x="100" y="90" textAnchor="middle" fontSize="8" fontWeight="700" fill={KZ_COL[k.color]}>{k.zona}</text>
      </svg>
      <div className="flex-1 min-w-[220px] text-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Índice de solvencia · Termómetro de Kanitz</div>
        <div className="text-xs text-slate-500 mt-1" data-testid="ac-kanitz-evolucion">{k.evolucion}</div>
        <p className="text-slate-700 mt-2">{k.lectura}</p>
        {k.aviso && <p className="mt-1 font-semibold text-amber-700" data-testid="ac-kanitz-aviso">{k.aviso}</p>}
        <p className="text-[11px] text-slate-400 mt-1">0,05·X1 + 1,65·X2 + 3,55·X3 − 1,06·X4 − 0,33·X5 · menos de −3 insolvente · −3 a 0 penumbra · más de 0 solvente</p>
      </div>
    </section>
  );
}


function VeredictoBalance({ ev, conclusion }) {
  const total = ev.total || 10, ok = ev.cumplidas || 0;
  return (
    <section className={`rf-veredicto rf-v-${ev.color} rf-entra`} data-testid="ac-dictamen">
      <div className="rf-v-txt">
        <div className="rf-v-chips"><span className="rf-v-riesgo">{ev.riesgo}</span><span className="rf-v-sub">Pasa {ok} de {total} controles de salud financiera</span></div>
        <h3 className="rf-v-titulo">{capital(ev.dictamen)}</h3>
        <p>{conclusion}</p>
      </div>
      <svg viewBox="0 0 200 120" className="rf-gauge" role="img" aria-label={`${ok} de ${total} controles cumplidos`}>
        <path d="M 20 100 A 80 80 0 0 1 180 100" className="rf-gauge-fondo" />
        <path d="M 20 100 A 80 80 0 0 1 180 100" className="rf-gauge-arco" strokeDasharray={ARCO} strokeDashoffset={ARCO * (1 - ok / total)} />
        <text x="100" y="92" textAnchor="middle" className="rf-gauge-num">{ok}</text>
        <text x="100" y="113" textAnchor="middle" className="rf-gauge-lab">de {total} controles</text>
      </svg>
    </section>
  );
}

/* Escala «redonda» para el eje: 4 tramos iguales (0, 8, 16, 24, 32). */
function escala(maximo) {
  const x = Math.max(maximo, 1e-9) / 4;
  const p = Math.pow(10, Math.floor(Math.log10(x)));
  const paso = [1, 2, 2.5, 5, 10].map((m) => m * p).find((v) => v >= x) || 10 * p;
  return { tope: paso * 4, ticks: [0, 1, 2, 3, 4].map((i) => paso * i) };
}

/* Composición del balance: barras apiladas por año (activo · patrimonio · deuda bancaria · financiamiento) con lectura automática. */
const PAL_AP = ["#0f5233", "#2f9e6a", "#d97706", "#9ca3af", "#1d4ed8", "#7c3aed", "#b91c1c"];
const PAL_SUAVE = ["#cfe3d8", "#b7d8c6", "#f3d9b1", "#e5e7eb", "#c7d2fe", "#ddd6fe", "#fecaca"];
const OSCURO = new Set(["#0f5233", "#2f9e6a", "#1d4ed8", "#7c3aed", "#b91c1c", "#d97706"]);
const mm1 = (v) => (v / 1e6).toLocaleString("es-PY", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const FONT = "Manrope, 'Segoe UI', Arial, sans-serif";
const colorSerie = (c, s, j) => (c.destacar ? (s.k === c.destacar ? (c.destacar === "ppe" ? "#0f5233" : "#b91c1c") : PAL_SUAVE[j % PAL_SUAVE.length]) : PAL_AP[j % PAL_AP.length]);
/* Apilado legible: valor 14 px solo si el segmento mide ≥ 24 px (nunca en «Otros»), total 16 px negrita, años 14 px; tablita de valores debajo. */
function Apilado({ c, anios, k }) {
  if (!c?.series?.length) return null;
  const n = anios.length, W = 360, H = 210, top = 30, base = 180, bw = Math.min(64, ((W - 40) / n) * 0.55);
  const tot = anios.map((_, i) => c.series.reduce((s, x) => s + Math.max(x.valores[i] || 0, 0), 0));
  const max = Math.max(...tot, 1e-9) * 1.15;
  const y = (v) => (v / max) * (base - top);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`ac-comp-${k}`} style={{ fontFamily: FONT }}>
      <div className="text-[13px] font-semibold text-[#0f5233]">{c.titulo} <span className="font-normal text-slate-400">· US$ millones</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto mt-1" role="img" aria-label={c.titulo} style={{ fontFamily: FONT }}>
        {anios.map((a, i) => {
          const cx = 20 + ((W - 40) / n) * (i + 0.5);
          let acc = 0;
          return (
            <g key={a}>
              {c.series.map((s, j) => { const v = Math.max(s.valores[i] || 0, 0), h = y(v), yy = base - acc - h, col = colorSerie(c, s, j); acc += h; return (
                <g key={s.k}><rect x={cx - bw / 2} y={yy} width={bw} height={h} fill={col} stroke="#fff" strokeWidth="0.8" />
                  {h >= 24 && s.k !== "otros" && <text x={cx} y={yy + h / 2 + 5} textAnchor="middle" fontSize="14" fill={OSCURO.has(col) ? "#fff" : "#1f2937"}>{mm1(v)}</text>}</g>); })}
              <text x={cx} y={base - acc - 6} textAnchor="middle" fontSize="16" fontWeight="700" fill="#1f2937">{mm1(tot[i])}</text>
              <text x={cx} y={base + 17} textAnchor="middle" fontSize="14" fill="#374151">{a}</text>
            </g>
          );
        })}
        <line x1="12" y1={base} x2={W - 12} y2={base} stroke="#9ca3af" strokeWidth="0.8" />
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-700">
        {c.series.map((s, j) => <span key={s.k}><i className="inline-block w-2.5 h-2.5 mr-1 rounded-sm" style={{ background: colorSerie(c, s, j) }} />{s.nombre}</span>)}
      </div>
      <table className="mt-2 w-full text-[11px]" data-testid={`ac-comp-${k}-tabla`}>
        <thead><tr className="bg-emerald-50 text-slate-700"><th className="px-2 py-1 text-left">US$ millones</th>{anios.map((a) => <th key={a} className="px-2 py-1 text-right">{a}</th>)}</tr></thead>
        <tbody>
          {c.series.map((s) => <tr key={s.k} className="border-t border-slate-100"><td className="px-2 py-0.5">{s.nombre}</td>{s.valores.map((v, i) => <td key={i} className="px-2 py-0.5 text-right tabular-nums">{mm1(v)}</td>)}</tr>)}
          <tr className="border-t border-emerald-200 font-semibold"><td className="px-2 py-0.5">Total</td>{tot.map((t, i) => <td key={i} className="px-2 py-0.5 text-right tabular-nums">{mm1(t)}</td>)}</tr>
        </tbody>
      </table>
      {c.lectura && <p className="mt-2 text-xs text-slate-700" data-testid={`ac-comp-${k}-lectura`}>{c.lectura}</p>}
    </div>
  );
}
function Composicion({ comp, anios, fuente }) {
  if (!comp) return null;
  return (
    <div className="space-y-2" data-testid="ac-composicion">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Cómo cambió el balance{fuente ? <span className="normal-case font-normal"> · fuente: {fuente}</span> : null}</div>
      <div className="grid gap-3 md:grid-cols-2">
        {["activo", "pn", "deuda_fin", "financiamiento"].map((k) => <Apilado key={k} k={k} c={comp[k]} anios={anios} />)}
      </div>
    </div>
  );
}


function GraficosBalance({ M, anios }) {
  const ys = anios.map(String);
  const n = ys.length;
  const [sel, setSel] = useState(n - 1);
  const s = Math.min(sel, n - 1);
  const g = (k) => ys.map((a) => Number(M[a]?.[k] ?? 0) || 0);
  const activo = g("activo"), pasivo = g("pasivo"), pn = g("pn"), ventas = g("ventas"), costos = g("costos"), ebitda = g("ebitda"), dban = g("deuda_fin");
  const maxAll = Math.max(...activo, ...pasivo, ...ventas, ...costos, 1);
  const div = maxAll >= 1e6 ? 1e6 : 1e3;
  const unidad = div === 1e6 ? "millones" : "miles";
  const f1 = (v) => (v / div).toLocaleString("es-PY", { maximumFractionDigits: 1 });
  const nf = (v, d = 1) => Number(v).toLocaleString("es-PY", { minimumFractionDigits: d, maximumFractionDigits: d });
  const del = (i, extra = 0) => ({ animationDelay: `${(0.12 * i + extra).toFixed(2)}s` });

  const e1 = escala(Math.max(...ys.map((_, i) => Math.max(activo[i], pasivo[i] + Math.max(pn[i], 0)))) / div);
  const e2 = escala(Math.max(...ventas, ...costos, ...ebitda) / div);
  const h = (v, e) => `${(Math.max(v, 0) / div / e.tope) * 100}%`;
  // funciones (no componentes): así un clic en un año no vuelve a montar las barras ni repite la animación
  const ticks = (e) => e.ticks.map((t) => (
    <div key={t} className="rf-tick" style={{ bottom: `${(t / e.tope) * 100}%`, borderTopColor: t ? undefined : "transparent" }}>
      <span>{t.toLocaleString("es-PY", { maximumFractionDigits: 1 })}</span>
    </div>
  ));

  // ¿puede pagar a los bancos? años de EBITDA para pagar la deuda bancaria
  const ratio = ys.map((_, i) => (dban[i] <= 0 ? 0 : ebitda[i] > 0 ? dban[i] / ebitda[i] : null));
  const finitos = ratio.filter((r) => r !== null);
  const yMax = Math.min(Math.max(5, Math.ceil(Math.max(0, ...finitos) + 1)), 12);
  const X = ys.map((_, i) => 40 + ((i + 0.5) * 350) / n);
  const Y = (v) => 190 - (Math.min(v, yMax) / yMax) * 170;
  const y4 = Y(4);
  let d = "", abierto = false;
  ratio.forEach((r, i) => {
    if (r === null) { abierto = false; return; }
    d += `${abierto ? "L" : "M"}${X[i].toFixed(1)},${Y(r).toFixed(1)} `;
    abierto = true;
  });
  const rS = ratio[s];
  const dpn = pn[s] > 0 ? pasivo[s] / pn[s] : null;
  const mEb = ventas[s] > 0 ? (ebitda[s] / ventas[s]) * 100 : null;

  const col = (i, label, children) => (
    <button key={ys[i]} type="button" className="rf-col" data-sel={i === s ? "true" : "false"} onClick={() => setSel(i)} aria-label={label}>{children}</button>
  );

  return (
    <div className="space-y-3">
      {n > 1 && (
        <div className="rf-anios">
          <span>Tocá un año para leerlo en los tres gráficos:</span>
          {ys.map((a, i) => <button key={a} type="button" className="rf-anio" aria-pressed={i === s} onClick={() => setSel(i)}>{a}</button>)}
        </div>
      )}
      <div className="rf-graficos" data-testid="ac-graficos">
        <section className="rf-g rf-entra" style={{ animationDelay: ".15s" }}>
          <div><h4>¿Cuánto de lo que tiene es suyo?</h4><p className="rf-g-sub">Activo = lo que debe + lo que es suyo (US$ {unidad})</p></div>
          <div className="rf-plot">
            {ticks(e1)}
            {ys.map((a, i) => (
              col(i, `${a}: activo US$ ${f1(activo[i])} ${unidad}, debe ${f1(pasivo[i])}, suyo ${f1(pn[i])}`, <>
                <span className="rf-val">{f1(activo[i])}</span>
                <span className="rf-barra" style={{ height: h(pn[i], e1), background: "var(--rf-suyo)", borderRadius: "8px 8px 0 0", ...del(i, 0.25) }} />
                <span className="rf-barra" style={{ height: h(pasivo[i], e1), background: "var(--rf-debe)", borderRadius: pn[i] > 0 ? 0 : "8px 8px 0 0", ...del(i) }} />
              </>)
            ))}
          </div>
          <div className="rf-ejes">{ys.map((a) => <span key={a}>{a}</span>)}</div>
          <div className="rf-ley"><span><i style={{ background: "var(--rf-suyo)" }} />Es suyo (patrimonio)</span><span><i style={{ background: "var(--rf-debe)" }} />Lo que debe (pasivo)</span></div>
          <div className={`rf-lee ${dpn === null || dpn > 2 ? "rf-mal" : ""}`}>
            {dpn === null ? <>En {ys[s]} debía más de lo que tenía: <b>patrimonio negativo</b>.</>
              : <>En {ys[s]} debía <b>{nf(dpn)} veces</b> lo que es suyo (sano: hasta 2).</>}
          </div>
        </section>

        <section className="rf-g rf-entra" style={{ animationDelay: ".25s" }}>
          <div><h4>¿Cuánto le queda de lo que vende?</h4><p className="rf-g-sub">Ventas, lo que le cuestan y lo que genera la operación (US$ {unidad})</p></div>
          <div className="rf-plot">
            {ticks(e2)}
            {ys.map((a, i) => (
              col(i, `${a}: ventas US$ ${f1(ventas[i])} ${unidad}, EBITDA ${f1(ebitda[i])}`, <>
                <span style={{ display: "flex", alignItems: "flex-end", gap: 4, width: "100%", height: "100%" }}>
                  <span style={{ flex: "3 1 0", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <span className="rf-val">{f1(ventas[i])}</span>
                    <span className="rf-barra" style={{ height: h(ventas[i] - costos[i], e2), background: "var(--rf-margen)", borderRadius: "8px 8px 0 0", ...del(i, 0.25) }} />
                    <span className="rf-barra" style={{ height: h(Math.min(costos[i], ventas[i]), e2), background: "var(--rf-costo)", borderRadius: ventas[i] > costos[i] ? 0 : "8px 8px 0 0", ...del(i) }} />
                  </span>
                  <span style={{ flex: "1 1 0", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <span className="rf-val" style={{ color: ebitda[i] < 0 ? "var(--rf-debe)" : "var(--rf-ebitda)" }}>{f1(ebitda[i])}</span>
                    <span className="rf-barra" style={{ height: h(ebitda[i], e2), background: "var(--rf-ebitda)", borderRadius: "6px 6px 0 0", ...del(i, 0.4) }} />
                  </span>
                </span>
              </>)
            ))}
          </div>
          <div className="rf-ejes">{ys.map((a) => <span key={a}>{a}</span>)}</div>
          <div className="rf-ley"><span><i style={{ background: "var(--rf-costo)" }} />Lo que le cuesta</span><span><i style={{ background: "var(--rf-margen)" }} />Margen</span><span><i style={{ background: "var(--rf-ebitda)" }} />EBITDA (lo que genera)</span></div>
          <div className={`rf-lee ${mEb === null || mEb < 4 ? "rf-mal" : ""}`}>
            En {ys[s]} vendió <b>US$ {f1(ventas[s])} {unidad}</b>{mEb !== null && <> y la operación generó <b>{nf(mEb)}%</b> de eso</>}.
          </div>
        </section>

        <section className="rf-g rf-entra" style={{ animationDelay: ".35s" }}>
          <div><h4>¿Puede pagar a los bancos?</h4><p className="rf-g-sub">Años de lo que genera que necesita para pagar su deuda bancaria</p></div>
          <svg viewBox="0 0 400 225" className="rf-svg" role="img"
            aria-label={`Deuda bancaria sobre EBITDA: ${ys.map((a, i) => `${a} ${ratio[i] === null ? "sin EBITDA" : nf(ratio[i])}`).join(", ")}`}>
            <rect x="40" y="20" width="350" height={Math.max(y4 - 20, 0)} fill="var(--rf-malo)" />
            <rect x="40" y={y4} width="350" height={190 - y4} fill="var(--rf-sano)" />
            <line x1="40" y1={y4} x2="390" y2={y4} stroke="var(--rf-debe)" strokeDasharray="4 4" />
            <text x="386" y={y4 - 6} textAnchor="end">más de 4 años: preocupa</text>
            <text x="386" y="183" textAnchor="end">zona sana</text>
            <line x1="40" y1="190" x2="390" y2="190" stroke="var(--rf-raya)" />
            {[0, 2, 4].concat(yMax > 6 ? [yMax] : []).map((t) => <text key={t} x="32" y={Y(t) + 4} textAnchor="end">{t}</text>)}
            {d && <path className="rf-traza" d={d.trim()} />}
            {ratio.map((r, i) => {
              const cy = r === null ? 30 : Y(r);
              const mal = r === null || r > 4;
              return (
                <g key={ys[i]} onClick={() => setSel(i)}>
                  <circle className="rf-pt" cx={X[i]} cy={cy} r={i === s ? 9 : 6} fill="var(--rf-card)" stroke={mal ? "var(--rf-debe)" : "var(--rf-linea)"} strokeWidth="3"
                    style={{ animationDelay: `${0.5 + 0.3 * i}s` }} />
                  <text x={X[i]} y={cy - 14} textAnchor="middle" className="rf-pt-v">{r === null ? "sin EBITDA" : r > yMax ? `${nf(r)}+` : nf(r)}</text>
                  <text x={X[i]} y="210" textAnchor="middle">{ys[i]}</text>
                </g>
              );
            })}
          </svg>
          <div className={`rf-lee ${rS === null || rS > 4 ? "rf-mal" : ""}`}>
            {rS === null ? <>En {ys[s]} la operación no generó plata (EBITDA cero o negativo): no se puede medir cuánto tardaría en pagar a los bancos.</>
              : dban[s] <= 0 ? <>En {ys[s]} no tenía deuda con bancos.</>
                : <>En {ys[s]} le hacían falta <b>{nf(rS)} años</b> de lo que genera para pagar a los bancos ({rS > 4 ? "más que el máximo sano de 4" : "dentro de lo sano, hasta 4"}).</>}
          </div>
        </section>
      </div>
    </div>
  );
}

/* Espera una tarea del servidor (IA, lectura de PDF). Si una consulta falla por un corte momentáneo (error 520/502 de
   Cloudflare, red), sigue intentando: la tarea sigue corriendo en el servidor. */
// «Leyendo el historial… página 3 de 12» con el avance que informa el servidor
const conAvance = (base, st) => (st?.total ? `${base} ${st.unidad || "parte"} ${Math.min((st.hechas || 0) + 1, st.total)} de ${st.total}…` : `${base.replace(/:$/, "")}…`);

async function esperarTarea(job, onProgreso, maxSeg = 900) {
  let fallos = 0;
  for (let t = 0; t < maxSeg; t += 3) {
    await new Promise((ok) => setTimeout(ok, 3000));
    let st;
    try {
      st = (await api.get(`/analisis-cliente/tarea/${job}`, { timeout: 30000 })).data;
      fallos = 0;
    } catch (err) {
      if (err.response?.status === 404 || ++fallos > 10) throw err;
      continue;
    }
    if (st.estado === "error") throw Object.assign(new Error(st.error), { response: { data: { detail: st.error } } });
    if (st.estado === "listo") return st.resultado;
    if (onProgreso) onProgreso(st);
  }
  throw Object.assign(new Error("timeout"), { response: { data: { detail: "Tardó demasiado. Probá de nuevo." } } });
}

/* Planillas modelo para empresas con otro sistema: se completan en Excel y se suben igual que el PDF. */
const MODELOS = {
  historial: ["Nro factura;Fecha factura;Vencimiento;Importe;Fecha de pago;Saldo;Observación",
    "001-001-0000123;05/09/2025;05/12/2025;12.500,00;10/12/2025;0;Venta",
    "001-001-0000150;20/09/2025;20/01/2026;30.000,00;;30.000,00;Venta"],
  pedidos: ["Nro pedido;Fecha;Producto;Vencimiento;Cantidad pendiente;Precio unitario;Importe pendiente",
    "P-1001;10/07/2026;Fertilizante 15-15-15;30/04/2027;32;680,00;21.760,00",
    "P-1002;20/09/2026;Semilla de maíz;30/08/2027;200;165,00;33.000,00"],
  pagares: ["Nro pagaré;Cliente;Emisión;Importe;Vencimiento;Portador;Firmado;Estado",
    "1001;CLIENTE S.A.;01/09/2025;50.000,00;30/04/2026;Nuestra empresa;SI;En poder",
    "1002;CLIENTE S.A.;01/09/2025;20.000,00;30/04/2026;;NO;Sin firmar",
    "1003;CLIENTE S.A.;01/09/2025;30.000,00;30/05/2026;Banco X;SI;En banco"],
};
function bajarModelo(tipo) {
  const blob = new Blob(["\ufeff" + MODELOS[tipo].join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = { pagares: "Modelo_pagares.csv", pedidos: "Modelo_pedidos_pendientes.csv" }[tipo] || "Modelo_historial_de_pagos.csv";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
const ACEPTA = ".pdf,.xlsx,.xls,.csv,application/pdf";

const GAR_HIST = ["Pagaré a sola firma", "Pagaré con codeudoría", "Pagarés de terceros", "Hipoteca 1er rango", "Hipoteca 2do rango", "Prenda", "Contrato de granos", "Fideicomiso", "Otra"];

/* externo: versión para otras empresas (sin datos del sistema de Tecnomyl; la deuda se carga a mano).
   fechaInforme: AAAA-MM-DD que figura en los informes (vacío = hoy). */
/* Dos formas de analizar: con balance (lo habitual) o cliente corporativo (sin balance ni pagarés, solo factura crédito).
   Las dos quedan montadas para no perder lo cargado al cambiar de pestaña. */
export default function AnalisisCliente({ externo = false, fechaInforme = "", abrirId = null, onAbierto }) {
  const [modo, setModo] = useState("balance");
  if (externo) return <AnalisisExterno fechaInforme={fechaInforme} />;
  return (
    <div className="acm space-y-4">
      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 text-sm shadow-sm" role="tablist" data-testid="ac-modo">
        {[["balance", "Con balance"], ["corporativo", "Cliente corporativo (sin balance ni pagarés)"]].map(([k, t]) => (
          <button key={k} type="button" role="tab" aria-selected={modo === k} onClick={() => setModo(k)}
            className={"rounded-lg px-3 py-1.5 font-medium transition-colors duration-200 " + (modo === k ? "bg-[#0f5233] text-white shadow-sm" : "text-slate-600 hover:bg-slate-50")}>{t}</button>
        ))}
      </div>
      <div className={modo === "balance" ? "" : "hidden"}><AnalisisBalance fechaInforme={fechaInforme} abrirId={abrirId} onAbierto={onAbierto} /></div>
      <div className={modo === "corporativo" ? "" : "hidden"}><AnalisisCorporativo /></div>
    </div>
  );
}

/* Página externa: la empresa sale del usuario autenticado (users.empresa, lo asigna el superadmin). Sus parámetros, criterio,
   vinculados, informes SET y análisis guardados quedan separados de Tecnomyl y de otras empresas en el backend. */
function AnalisisExterno({ fechaInforme = "" }) {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe({})); }, []);
  if (!me) return <div className="acm text-xs text-slate-500 p-3">Cargando…</div>;
  return (
    <div className="acm space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs shadow-sm" data-testid="ac-externo-empresa">
        {me.empresa ? <span className="text-emerald-800">Espacio de trabajo: <b data-testid="ac-externo-empresa-nombre">{me.empresa}</b> — parámetros, criterio, vinculados, informes SET y análisis guardados son solo de esta empresa.</span>
          : <span className="text-amber-800">Tu usuario no tiene empresa asignada: estás trabajando en el espacio de Tecnomyl. Pedile al administrador que te asigne una empresa.</span>}
      </div>
      <AnalisisBalance key={me.empresa || "tecnomyl"} externo fechaInforme={fechaInforme} />
    </div>
  );
}

function AnalisisBalance({ externo = false, fechaInforme = "", abrirId = null, onAbierto }) {
  const [cfg, setCfg] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [meta, setMeta] = useState({});          // codigo, tipo_sugerido, motivo, linea_actual
  const [ruc, setRuc] = useState("");
  const [comp, setComp] = useState(null);        // auditoría reputacional (compliance)
  const [intel, setIntel] = useState(null);      // inteligencia estratégica (hallazgos)
  const [intelBusy, setIntelBusy] = useState(false);
  const [compBusy, setCompBusy] = useState(false);
  const compHecha = useRef("");
  const [sb, setSb] = useState({ abierto: false, codigo: "", nombre: "" });   // cliente sin balance: grupo / SET antes de tener parsed
  const [tipo, setTipo] = useState("productor_vende");
  const [sector, setSector] = useState("agro_agricultura");   // sector / rubro: cambia umbrales y textos (agro = comportamiento histórico)
  const [sectores, setSectores] = useState([]);
  const [verUmbrales, setVerUmbrales] = useState(false);
  const esAgro = !sectores.length || (sectores.find((s) => s.k === sector)?.agro ?? true);
  const [moneda, setMoneda] = useState("USD");
  const [tc, setTc] = useState({});
  const [tcInfo, setTcInfo] = useState({});      // de dónde salió cada tipo de cambio (DNIT: fecha, compra, venta, promedio)
  const [tcMes, setTcMes] = useState(12);
  const [tcBase, setTcBase] = useState("compra");
  const [otorgada, setOtorgada] = useState("");
  const [sinLinea, setSinLinea] = useState(false);   // todavía no tiene línea: el sistema propone cuánto dar y qué garantía pedir
  const [perfil, setPerfil] = useState("");          // "" = usar el sugerido; el analista puede cambiarlo (queda registrado por qué)
  const [perfilMotivo, setPerfilMotivo] = useState("");
  const [vigencia, setVigencia] = useState("");   // AAAA-MM-DD: inicio de la línea (dura 1 año)
  const [solicitadas, setSolicitadas] = useState(null);
  const [extra, setExtra] = useState(EXTRA_VACIO);   // lo que carga el analista: zafra, bancos, vendedor
  const [ia, setIa] = useState(null);                // dictamen del analista IA
  const [iaBusy, setIaBusy] = useState(false);
  const [prestamo, setPrestamo] = useState(PRESTAMO_VACIO);   // ¿evaluar también préstamo en efectivo?
  const [chat, setChat] = useState([]);              // [{rol: "user"|"ia", texto}]
  const [chatBusy, setChatBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guardados, setGuardados] = useState([]);
  const [hist, setHist] = useState([]);
  const [verRef, setVerRef] = useState(false);
  const [edit, setEdit] = useState(null);        // copia editable de la configuración
  const fileRef = useRef();
  const pdfRef = useRef();
  const [verDatos, setVerDatos] = useState(false);
  const [progreso, setProgreso] = useState("");
  const [avance, setAvance] = useState(null);     // {pct, txt}: barra de progreso al subir y leer el balance
  const [decidido, setDecidido] = useState(false); // ya se guardó o se bajó un informe (paso 4)

  const cargarCfg = useCallback(async () => {
    try { setCfg((await api.get("/analisis-cliente/config")).data); } catch { /* sin acceso */ }
    try { setSectores((await api.get("/analisis-cliente/sectores")).data?.items || []); } catch { /* sin acceso */ }
  }, []);
  const cargarGuardados = useCallback(async () => {
    try { setGuardados((await api.get("/analisis-cliente/guardados")).data?.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { cargarCfg(); cargarGuardados(); }, [cargarCfg, cargarGuardados]);
  const monedaEmpresa = cfg?.propios?.empresa?.moneda;
  useEffect(() => { if (monedaEmpresa && !parsed) setMoneda(monedaEmpresa); }, [monedaEmpresa, parsed]);

    const payload = useCallback((p = parsed, t = tipo, m = moneda, c = tc, o = otorgada) => ({
    parsed: p, tipo: t, moneda: m, sector, ruc: ruc || null,
    tc: Object.fromEntries(Object.entries(c).filter(([, v]) => String(v).trim() !== "").map(([k, v]) => [k, aNumEs(v)])),
    linea_otorgada: !sinLinea && o ? aNumEs(o) || null : null, sin_linea: sinLinea, perfil: perfil || null, perfil_motivo: perfilMotivo || null,
    vigencia_desde: vigencia || null, solicitadas, extra: perfil ? { ...extra, perfil_usuario: "analista" } : extra, ia, codigo: meta.codigo || null, fecha_informe: fechaInforme || null,
    prestamo: prestamo.evaluar ? { evaluar: true, monto: aNumEs(prestamo.monto) || 0, plazo_meses: aNumEs(prestamo.plazo_meses) || 0, destino: prestamo.destino } : null,
  }), [parsed, tipo, moneda, tc, otorgada, sinLinea, vigencia, solicitadas, extra, ia, prestamo, meta.codigo, fechaInforme, sector, perfil, perfilMotivo, ruc]);

  /* Auditoría reputacional: automática (caché 30 días por RUC/nombre) al tener el nombre del cliente; el botón re-verifica */
  const verificarAntecedentes = useCallback(async (forzar = false) => {
    const nombre = (parsed?.cliente || "").trim();
    if (nombre.length < 3 && !ruc) return;
    setCompBusy(true);
    try {
      const d = (await api.post("/analisis-cliente/compliance", { nombre, ruc: ruc || null, forzar }, { timeout: 120000 })).data;
      setComp(d.resultado);
      if (forzar) { toast.success("Antecedentes verificados de nuevo"); if (res) analizar(); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo verificar antecedentes");
    } finally { setCompBusy(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed?.cliente, ruc, res]);
  const compClave = `${(parsed?.cliente || "").trim().toLowerCase()}|${ruc}`;
  useEffect(() => {
    if (!parsed?.cliente || compClave === compHecha.current) return;
    compHecha.current = compClave;
    const t = setTimeout(() => verificarAntecedentes(false), 600);
    return () => clearTimeout(t);
  }, [compClave, parsed?.cliente, verificarAntecedentes]);
  useEffect(() => { if (res?.compliance !== undefined && res?.compliance) setComp(res.compliance); }, [res]);
  useEffect(() => { if (res && "hallazgos_estrategicos" in res) setIntel(res.hallazgos_estrategicos); }, [res]);
  const investigar = useCallback(async () => {
    const nombre = (parsed?.cliente || "").trim();
    if (nombre.length < 3 && !ruc) return;
    setIntelBusy(true);
    try {
      const d = (await api.post("/analisis-cliente/inteligencia", { nombre, ruc: ruc || null, forzar: true, contexto: res?.narr?.conclusion || null }, { timeout: 240000 })).data;
      setIntel(d.resultado);
      toast.success("Investigación estratégica actualizada");
      if (res) analizar();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo investigar");
    } finally { setIntelBusy(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed?.cliente, ruc, res]);
  useEffect(() => { if (parsed?.ruc && !ruc) setRuc(String(parsed.ruc)); }, [parsed?.ruc, ruc]);

  const subir = async (e, esPdf = false) => {
    const fl = Array.from(e.target.files || []);
    if (!fl.length) return;
    const f = fl[0];
    setBusy(true);
    try {
      const fd = new FormData();
      if (esPdf) fl.forEach((x) => fd.append("files", x)); else fd.append("file", f);
      let r;
      // la subida ocupa el primer 30% de la barra; la lectura, el resto
      const subida = (ev) => { if (ev.total) setAvance({ pct: (ev.loaded / ev.total) * 30, txt: `Subiendo ${fl.length > 1 ? `${fl.length} archivos` : f.name}…` }); };
      setAvance({ pct: 0, txt: `Subiendo ${fl.length > 1 ? `${fl.length} archivos` : f.name}…` });
      if (esPdf && !/\.(xlsx?|xlsm)$/i.test(f.name)) {
        setProgreso("Subiendo el PDF…");
        // la lectura corre en segundo plano en el servidor: se consulta cada pocos segundos
        const j = (await api.post("/analisis-cliente/leer-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000, onUploadProgress: subida })).data;
        if (j.listo) { r = j.listo; } else {
        setAvance({ pct: 32, txt: "Preparando las páginas…" });
        r = await esperarTarea(j.job, (st) => {
          const t = st.total ? `Leyendo página ${Math.min(st.hechas + 1, st.total)} de ${st.total}…` : "Preparando las páginas…";
          setProgreso(t);
          setAvance({ pct: st.total ? 32 + (st.hechas / st.total) * 66 : 32, txt: t });
        });
        }
      } else {
        if (esPdf) { fd.delete("files"); fd.append("file", f); }
        r = (await api.post("/analisis-cliente/leer", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000, onUploadProgress: subida })).data;
      }
      setAvance({ pct: 100, txt: "Listo" });
      aplicarLectura(r, esPdf);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || (esPdf ? "No se pudo leer el PDF" : "No se pudo leer el Excel"));
    } finally { setBusy(false); setProgreso(""); setAvance(null); if (fileRef.current) fileRef.current.value = ""; if (pdfRef.current) pdfRef.current.value = ""; }
  };
  // archivos soltados en la zona de carga
  const soltarArchivos = (files, esPdf, error) => {
    if (error || !files.length) { toast.error(error || "Soltá un Excel o un PDF del balance."); return; }
    subir({ target: { files } }, esPdf);
  };

  // lo mismo para Excel, PDF o balance cargado a mano
  const aplicarLectura = (r, esPdf = false) => {
    {
      setParsed(r.parsed);
      setMeta({ codigo: r.codigo, sugerido: r.tipo_sugerido, motivo: r.tipo_sugerido_motivo, linea_actual: r.linea_actual,
        linea_origen: r.linea_origen, linea_dudosa: r.linea_dudosa, linea_sistema: r.linea_sistema });
      setTipo(r.tipo_sugerido || "productor_vende");
      const m = r.parsed.moneda_archivo === "PYG" ? "PYG" : "USD";
      setMoneda(m);
      setTc(Object.fromEntries((r.parsed.anios || []).map((a) => {
        const k = String(a);
        if (r.parsed.tc_archivo?.[k]) return [k, desdeNum(r.parsed.tc_archivo[k])];
        return [k, r.tc_dnit?.[k] ? desdeNum(r.tc_dnit[k].compra) : ""];
      })));
      setTcInfo(Object.fromEntries(Object.entries(r.tc_dnit || {}).filter(([k]) => !r.parsed.tc_archivo?.[k])));
      setOtorgada(r.linea_actual ? Math.round(r.linea_actual).toLocaleString("es-PY") : "");
      setSinLinea(false);
      setRes(null);
      setVigencia(r.vigencia?.iso_desde || "");
      setExtra({ ...EXTRA_VACIO, balance: { auditado: typeof r.parsed.auditado === "boolean" ? r.parsed.auditado : null, auditor: r.parsed.auditor || "" } });
      setIa(null); setChat([]); setPrestamo(PRESTAMO_VACIO); setDecidido(false);
      setSolicitadas(r.solicitadas || null);
      setVerDatos(esPdf);
      toast.success(r.parsed.manual ? `Balance cargado a mano: ${r.parsed.cliente} (${r.parsed.anios.join(", ")})` : `Leí ${r.parsed.cliente || "el archivo"} (${r.parsed.anios.join(", ")})`);
      (r.parsed.avisos || []).forEach((a) => toast.message(a));
    }
  };
  const [verManual, setVerManual] = useState(false);
  const usarManual = async (body) => {
    setBusy(true);
    try { aplicarLectura((await api.post("/analisis-cliente/balance-manual", body)).data); setVerManual(false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo usar ese balance"); }
    finally { setBusy(false); }
  };

  const aplicarHistorial = (h) => {
    setExtra((x) => {
      const zs = x.zonas || EXTRA_VACIO.zonas;
      const vacia = !zs.principal?.departamento && !zs.principal?.localidad;
      return { ...x, historial: { filas: h.filas, cliente: h.cliente, codigo: h.codigo, archivo: h.archivo, resumen: h.resumen },
        zonas: vacia && (h.localidad || h.departamento) ? { ...zs, principal: { departamento: h.departamento || "", localidad: h.localidad || "" } } : zs };
    });
    if (h.codigo) setMeta((m) => ({ ...m, codigo: m.codigo || h.codigo }));
    setRes(null);
    const r = h.resumen || {};
    toast.success(`Historial de ${h.cliente || "el cliente"}: ${r.facturas || 0} facturas, ${r.dias_prom ?? "—"} días de atraso promedio`);
    if (h.columnas && Object.keys(h.columnas).length) toast.message(`Columnas que tomé: ${Object.entries(h.columnas).map(([k, v]) => `${v} → ${k.replace("_", " ")}`).join(" · ")}`);
    const n1 = (parsed?.cliente || "").toUpperCase().replace(/[^A-Z0-9]/g, ""), n2 = (h.cliente || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    const distintos = n1 && n2 && !n1.includes(n2.slice(0, 6)) && !n2.includes(n1.slice(0, 6));
    if (h.cliente && parsed && (!n1 || (distintos && n1.length < 8))) {
      // el balance trajo un nombre corto o raro (por ejemplo el logo del estudio contable): se usa el del sistema
      setParsed((p0) => ({ ...p0, cliente: h.cliente }));
      toast.success(`Nombre del cliente tomado del historial: ${h.cliente}`);
    } else if (distintos) {
      toast.error(`Ojo: el historial es de «${h.cliente}» y el balance de «${parsed.cliente}». Revisá que sean del mismo cliente.`);
    }
  };
  const [verHistManual, setVerHistManual] = useState(false);
  const usarHistManual = async (body) => {
    setBusy(true);
    try {
      aplicarHistorial((await api.post("/analisis-cliente/historial-manual", body)).data);
      setVerHistManual(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar el historial");
    } finally { setBusy(false); }
  };

  const histRef = useRef();
  const garRef = useRef();
  const subirGarantiasReales = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setProgreso("Leyendo el listado de garantías reales…");
    try {
      const fd = new FormData(); fd.append("file", f);
      const g = (await api.post("/analisis-cliente/garantias-reales", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 })).data;
      setExtra((x) => ({ ...x, garantias_reales: g, vigentes: (x.vigentes || []).filter((v) => v.auto !== "garantias_reales") }));
      setRes(null);
      const rs = g.resumen_garantias_reales || {};
      toast[rs.suma_a_linea_credito ? "success" : "warning"](`Garantías reales: ${rs.bloques} escritura(s) · ${String(rs.calificacion_garantia || "").replace("_", " ").toLowerCase()} · ejecutable ${usd0(rs.cobertura_ejecutable_neta_usd)}`);
      if (g.cliente?.nombre && parsed?.cliente && g.cliente.nombre.slice(0, 12).toLowerCase() !== parsed.cliente.slice(0, 12).toLowerCase()) toast.error(`Ojo: el listado es de «${g.cliente.nombre}» y el balance de «${parsed.cliente}».`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el listado de garantías reales");
    } finally { setBusy(false); setProgreso(""); if (garRef.current) garRef.current.value = ""; }
  };
  const subirHistorial = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setProgreso("Leyendo el historial de compras…");
    try {
      const fd = new FormData(); fd.append("file", f);
      const j = (await api.post("/analisis-cliente/historial-compras", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
      const h = j.resultado || (await esperarTarea(j.job, (st) => setProgreso(conAvance("Leyendo el historial (escaneado o de otro sistema):", st))));
      aplicarHistorial(h);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el historial");
    } finally { setBusy(false); setProgreso(""); if (histRef.current) histRef.current.value = ""; }
  };

  const pedRef = useRef();
  const subirPedidos = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setProgreso("Leyendo los pedidos pendientes…");
    try {
      const fd = new FormData(); fd.append("file", f);
      const j = (await api.post("/analisis-cliente/pedidos", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
      const h = j.resultado || (await esperarTarea(j.job, (st) => setProgreso(conAvance("Leyendo los pedidos (formato de otro sistema):", st))));
      setExtra((x) => ({ ...x,
        pedidos: h.por_vto.map((v) => ({ vto: v.vto, monto: Number(v.monto).toLocaleString("es-PY", { maximumFractionDigits: 2 }) })),
        pedidos_doc: { archivo: h.archivo, cliente: h.cliente, total: h.total, pedidos: h.pedidos, lineas: h.lineas,
          detalle: (h.filas || []).slice(0, 80).map((r) => ({ pedido: r.pedido, producto: r.producto, vto: r.vto, pendiente: r.pendiente })) } }));
      setRes(null);
      toast.success(`${h.pedidos} pedidos pendientes por ${usd(h.total)}, cargados en ${h.por_vto.length} vencimiento(s)`);
      if (h.aviso) toast.error(h.aviso);
      const n1 = (parsed?.cliente || "").toUpperCase().replace(/[^A-Z0-9]/g, ""), n2 = (h.cliente || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      if (n1 && n2 && !n1.includes(n2.slice(0, 6)) && !n2.includes(n1.slice(0, 6))) toast.error(`Ojo: los pedidos son de «${h.cliente}» y el balance de «${parsed.cliente}».`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron leer los pedidos");
    } finally { setBusy(false); setProgreso(""); if (pedRef.current) pedRef.current.value = ""; }
  };

  const pagRef = useRef();
  const subirPagares = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setProgreso("Leyendo el listado de pagarés…");
    try {
      const fd = new FormData(); fd.append("file", f);
      const j = (await api.post("/analisis-cliente/pagares", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
      const h = j.resultado || (await esperarTarea(j.job, (st) => setProgreso(conAvance("Leyendo el listado (formato de otro sistema):", st))));
      setExtra((x) => ({ ...x, pagares: { filas: h.filas, cliente: h.cliente, archivo: h.archivo, resumen: h.resumen, tipo: x.pagares?.tipo || "Pagaré con codeudoría" } }));
      setRes(null);
      const r = h.resumen || {};
      toast.success(`${r.cantidad || 0} pagarés: ${usd(r.garantia)} firmados en nuestro poder, ${usd(r.sin_firma)} sin firmar`);
      if (h.aviso) toast.error(h.aviso);
      const n1 = (parsed?.cliente || "").toUpperCase().replace(/[^A-Z0-9]/g, ""), n2 = (h.cliente || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      if (n1 && n2 && !n1.includes(n2.slice(0, 6)) && !n2.includes(n1.slice(0, 6))) toast.error(`Ojo: el listado es de «${h.cliente}» y el balance de «${parsed.cliente}».`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el listado de pagarés");
    } finally { setBusy(false); setProgreso(""); if (pagRef.current) pagRef.current.value = ""; }
  };

  const editarDato = (anio, k, txt) => {
    const v = parseFloat(String(txt).replace(/\./g, "").replace(",", "."));
    setParsed((p) => ({ ...p, datos: { ...p.datos, [anio]: { ...p.datos[anio], [k]: Number.isFinite(v) ? v : 0 } } }));
    setRes(null);
  };

  const traerTc = async () => {
    if (!parsed) return;
    setBusy(true);
    try {
      const items = (await api.get("/analisis-cliente/tipo-cambio", { params: { anios: parsed.anios.join(","), mes: tcMes } })).data.items || {};
      const nuevo = { ...tc };
      let n = 0;
      parsed.anios.forEach((a) => {
        const it = items[String(a)];
        if (!it) return;
        const v = tcBase === "venta" ? it.venta : tcBase === "promedio" ? it.promedio_anual : it.compra;
        if (v) { nuevo[String(a)] = desdeNum(v); n += 1; }
      });
      setTc(nuevo); setTcInfo(items); setRes(null);
      const faltan = parsed.anios.filter((a) => !items[String(a)]);
      if (n) toast.success(`Tipo de cambio de la DNIT cargado para ${n} año(s)`);
      if (faltan.length) toast.error(`La DNIT no tiene publicado el cierre de: ${faltan.join(", ")}. Cargalo a mano.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo traer el tipo de cambio");
    } finally { setBusy(false); }
  };

  const analizar = async (p) => {
    setBusy(true);
    try {
      const body = { ...(p || payload()), ia: null };
      const r = (await api.post("/analisis-cliente/calcular", body)).data;
      setRes(r);
      setHist(r.historial || []);
      setIa(null); setChat([]);
      pedirIa(body);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo analizar");
    } finally { setBusy(false); }
  };

  const pedirIa = async (body) => {
    setIaBusy(true);
    try {
      const j = (await api.post("/analisis-cliente/ia", { ...(body || payload()), ia: null }, { timeout: 60000 })).data;
      const d = await esperarTarea(j.job, null, 420);
      setIa(d.dictamen);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "El analista IA no respondió");
    } finally { setIaBusy(false); }
  };

  const preguntar = async (texto) => {
    const q = (texto || "").trim();
    if (!q || chatBusy) return;
    const previos = chat;
    setChat([...previos, { rol: "user", texto: q }]);
    setChatBusy(true);
    try {
      const j = (await api.post("/analisis-cliente/chat", { payload: payload(), mensajes: previos, pregunta: q }, { timeout: 60000 })).data;
      const d = await esperarTarea(j.job, null, 300);
      setChat((c) => [...c, { rol: "ia", texto: d.respuesta }]);
    } catch (err) {
      setChat((c) => [...c, { rol: "ia", texto: formatApiErrorDetail(err.response?.data?.detail) || "No pude responder ahora. Probá de nuevo." }]);
    } finally { setChatBusy(false); }
  };

  const bajarPdf = async (formato) => {
    setBusy(true);
    try {
      const r = (await api.post("/analisis-cliente/pdf", { ...payload(), formato })).data;
      if (r.url) window.open(r.url, "_blank");
      setDecidido(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar el PDF");
    } finally { setBusy(false); }
  };

  const guardar = async () => {
    setBusy(true);
    try {
      await api.post("/analisis-cliente/guardar", payload());
      setDecidido(true);
      toast.success("Análisis guardado");
      cargarGuardados();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const abrir = async (id) => {
    setBusy(true);
    try {
      const d = (await api.get(`/analisis-cliente/guardados/${id}`)).data.payload;
      setParsed(d.parsed); setTipo(d.tipo); setMoneda(d.moneda);
      setTc(Object.fromEntries(Object.entries(d.tc || {}).map(([k, v]) => [String(k), desdeNum(v)])));
      setOtorgada(d.linea_otorgada ? Math.round(d.linea_otorgada).toLocaleString("es-PY") : "");
      setSinLinea(!!d.sin_linea);
      setVigencia(d.vigencia_desde || ""); setSolicitadas(d.solicitadas || null);
      setExtra({ ...EXTRA_VACIO, ...(d.extra || {}) }); setIa(d.ia || null); setChat([]);
      setPrestamo(d.prestamo?.evaluar ? { evaluar: true, monto: desdeNum(d.prestamo.monto), plazo_meses: String(d.prestamo.plazo_meses || ""), destino: d.prestamo.destino || "" } : PRESTAMO_VACIO);
      setMeta({ codigo: d.codigo || "", sugerido: null, motivo: "", linea_actual: null });
      const r = (await api.post("/analisis-cliente/calcular", d)).data;
      setRes(r); setHist(r.historial || []);
      if (!d.ia) pedirIa(d);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir");
    } finally { setBusy(false); }
  };
  useEffect(() => { if (abrirId) { abrir(abrirId); onAbierto?.(); } }, [abrirId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const borrar = async (id) => {
    if (!window.confirm("¿Eliminar este análisis guardado?")) return;
    try { await api.delete(`/analisis-cliente/guardados/${id}`); cargarGuardados(); } catch { toast.error("No se pudo eliminar"); }
  };

  const guardarHist = async () => {
    if (!res?.codigo) { toast.error("No pude identificar el código del cliente en el archivo"); return; }
    try {
      const r = (await api.put(`/analisis-cliente/historial/${res.codigo}`, { items: hist })).data;
      setHist(r.items);
      toast.success("Historial de líneas guardado");
      analizar();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el historial");
    }
  };

  const abrirRef = () => {
    if (!cfg) return;
    setEdit(JSON.parse(JSON.stringify({ tipos: cfg.tipos, linea_pct: cfg.linea_pct, factores: cfg.factores, comportamiento: cfg.comportamiento || {}, prudencia: cfg.prudencia || {}, perfiles: cfg.perfiles || null, agro: cfg.agro || null, zafra: cfg.zafra || null, capacidad: cfg.capacidad || null, propios: cfg.propios || {} })));
    setVerRef((v) => !v);
  };
  const guardarRef = async () => {
    try {
      await api.put("/analisis-cliente/config", {
        pruebas: Object.fromEntries(Object.entries(edit.tipos).map(([k, v]) => [k, v.pruebas])),
        linea_pct: edit.linea_pct, factores: edit.factores, comportamiento: edit.comportamiento, prudencia: edit.prudencia, perfiles: edit.perfiles || {}, agro: edit.agro || {}, zafra: edit.zafra || {}, capacidad: edit.capacidad || {}, propios: edit.propios || {},
      });
      toast.success("Referencias guardadas");
      await cargarCfg();
      if (parsed) analizar();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron guardar las referencias");
    }
  };
  const restaurarRef = async () => {
    if (!window.confirm("¿Volver a las referencias originales?")) return;
    await api.delete("/analisis-cliente/config");
    await cargarCfg();
    setVerRef(false);
    toast.success("Referencias restauradas");
  };

  const propios = cfg?.propios || {};
  const tiposTodos = {
    ...(cfg?.tipos || {}),
    ...Object.fromEntries((propios.tipos_extra || []).map((t) => [t.k, { nombre: `${t.nombre} (propio)`, descripcion: t.descripcion || `Tipo propio de la empresa: se calcula como «${cfg?.tipos?.[t.base]?.nombre || t.base}» con sus propias pruebas.` }])),
  };
  const M = useMemo(() => res?.M || {}, [res]);
  const anios = useMemo(() => res?.anios || [], [res]);
  const ult = anios.length ? M[String(anios[anios.length - 1])] : null;
  const inp = "h-9 rounded border border-slate-200 px-3 text-sm";

  const kpi = useMemo(() => (ult ? [
    { t: "Ventas del año", n: ult.ventas, f: usd, v: usd(ult.ventas), s: ult.crec_ventas !== null && ult.crec_ventas !== undefined ? `${ult.crec_ventas >= 0 ? "+" : ""}${num(ult.crec_ventas, 1)}% vs. año anterior` : "", mal: false },
    { t: "Ganancia neta", n: ult.neto, f: usd, v: usd(ult.neto), s: `Margen ${num(ult.margen_neto, 1)}% (por cada 100 vendidos quedan ${num(ult.margen_neto, 1)})`, mal: ult.neto < 0 },
    { t: "Deuda sobre patrimonio", n: ult.endeudamiento ?? undefined, f: (x) => `${num(x)}x`, v: ult.endeudamiento !== null ? `${num(ult.endeudamiento)}x` : "—", s: "(debe esta cantidad de veces lo que es suyo)", mal: (ult.endeudamiento || 99) > 2 },
    { t: "Caja si paga todo", n: ult.generacion_caja, f: usd, v: usd(ult.generacion_caja), s: "(cobrando y vendiendo stock, ¿le sobra o le falta?)", mal: ult.generacion_caja < 0 },
  ] : []).map((k, i) => ({ ...k, serie: anios.map((a) => M[String(a)]?.[["ventas", "neto", "endeudamiento", "generacion_caja"][i]]) })), [ult, M, anios]);

  return (
    <div className="acm space-y-4" data-testid="analisis-cliente">
      <div className="flex flex-wrap items-center gap-3">
        <Pasos paso={res ? (decidido ? 4 : 3) : parsed ? 2 : 1} />
        {busy && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> trabajando…</span>}
      </div>
      {/* 1. Subir */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5 space-y-3">
        <h2 className="acm-titulo acm-paso-titulo"><span>1</span> Estados contables del cliente</h2>
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" onChange={(e) => subir(e, false)} className="hidden" data-testid="ac-file" />
        <input ref={pdfRef} type="file" accept=".pdf,application/pdf,.xls,.xlsx" multiple onChange={(e) => subir(e, true)} className="hidden" data-testid="ac-file-pdf" />
        <ZonaCarga busy={busy} avance={avance || (progreso ? { pct: null, txt: progreso } : null)} leido={!!parsed} onArchivos={soltarArchivos}
          onElegirExcel={() => fileRef.current?.click()} onElegirPdf={() => pdfRef.current?.click()} />
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setVerManual(!verManual)} className="gap-1.5" data-testid="ac-manual"
            title="Escribir las cifras del balance a mano (sin Excel ni PDF), o corregir lo que se leyó">
            <Plus className="h-4 w-4 text-[#0f5233]" /> {parsed ? "Corregir a mano" : "Cargar a mano"}
          </Button>
          <input ref={histRef} type="file" accept={ACEPTA} onChange={subirHistorial} className="hidden" data-testid="ac-file-hist" />
          {parsed && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => histRef.current?.click()} className="gap-1.5" data-testid="ac-upload-hist"
              title="Facturas del cliente con vencimiento, fecha de pago (o saldo) e importe. Sirve el PDF del sistema o una planilla Excel/CSV de cualquier sistema.">
              <FileText className="h-4 w-4 text-[#0f5233]" /> Historial de pagos (PDF o Excel)
            </Button>
          )}
          {parsed && <button type="button" onClick={() => bajarModelo("historial")} className="text-xs text-slate-500 underline" title="Planilla para completar si tu sistema exporta otro formato">Planilla modelo</button>}
          <input ref={garRef} type="file" accept=".pdf,application/pdf" onChange={subirGarantiasReales} className="hidden" data-testid="ac-file-garantias" />
          {parsed && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => garRef.current?.click()} className="gap-1.5" data-testid="ac-upload-garantias"
              title="PDF «Créditos - Listado de Garantías Reales» del sistema: lee cada escritura, los rangos preferentes (bancos, cerealeras) y calcula cuánto queda realmente ejecutable">
              <FileText className="h-4 w-4 text-[#0f5233]" /> Garantías reales (PDF){extra.garantias_reales ? " ✓" : ""}
            </Button>
          )}
          {parsed && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setVerHistManual(!verHistManual)} className="gap-1.5" data-testid="ac-hist-manual"
              title="Escribir las facturas del cliente a mano (fecha, vencimiento, importe, cuándo pagó o cuánto debe)">
              <Plus className="h-4 w-4 text-[#0f5233]" /> {extra.historial?.filas?.length ? "Corregir historial a mano" : "Historial a mano"}
            </Button>
          )}
          {verHistManual && <div className="basis-full"><HistorialManual hist={extra.historial} monedaDef={extra.historial?.filas?.length ? "USD" : monedaEmpresa} cliente={parsed?.cliente} codigo={meta.codigo} busy={busy} onUsar={usarHistManual} onCerrar={() => setVerHistManual(false)} /></div>}
          {verManual && <div className="basis-full"><BalanceManual parsed={parsed} monedaDef={monedaEmpresa} busy={busy} onUsar={usarManual} onCerrar={() => setVerManual(false)} /></div>}
          {parsed && <span className="text-sm text-slate-600"><b>{parsed.cliente || "Cliente"}</b> · ejercicios {parsed.anios.join(", ")}</span>}
          {parsed && (
            <span className="flex items-center gap-1 text-xs text-slate-500">Código
              <input value={meta.codigo || ""} onChange={(e) => setMeta({ ...meta, codigo: e.target.value.replace(/\D/g, "") })} placeholder="ej. 100565"
                className="h-7 w-24 rounded border border-slate-200 px-2 text-xs" title="Código del cliente en el sistema (para traer su deuda, cobros e historial de líneas)" data-testid="ac-codigo" />
            </span>
          )}
        </div>
        {extra.historial?.filas?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900" data-testid="ac-hist-cargado">
            📒 <b>Historial de compras cargado</b>: {extra.historial.cliente} {extra.historial.codigo ? `(${extra.historial.codigo})` : ""} · {extra.historial.resumen?.facturas} facturas
            desde {String(extra.historial.resumen?.desde || "").slice(0, 4)}
            {extra.historial.resumen?.pausas?.length ? ` (con pausas; relación continua desde ${String(extra.historial.resumen.relacion_continua_desde || "").slice(0, 4)})` : ""}
            {extra.historial.resumen?.abierto ? ` · abierto hoy ${usd(extra.historial.resumen.abierto.total)} (vencido ${usd(extra.historial.resumen.abierto.vencido)})` : ""} · atraso promedio {extra.historial.resumen?.dias_prom} días · {extra.historial.resumen?.patron}
            <button type="button" onClick={() => { setExtra({ ...extra, historial: null }); setRes(null); }} className="ml-auto text-slate-500 hover:text-red-600">Quitar</button>
          </div>
        )}
        {parsed?.avisos?.map((a) => <p key={a} className="text-xs text-amber-700">⚠️ {a}</p>)}
        {parsed?.correcciones?.length > 0 && (
          <details className="text-xs text-slate-500" data-testid="ac-correcciones">
            <summary className="cursor-pointer select-none">✔ Balance leído y completado: el sistema arregló solo {parsed.correcciones.length} detalle(s) (subtotales en 0 o mal escritos en el original). Ver</summary>
            <ul className="mt-1 list-disc pl-5">{parsed.correcciones.map((c) => <li key={c}>{c}</li>)}</ul>
          </details>
        )}

        {parsed?.fuente === "pdf" && (
          <div className="rounded-md border border-slate-200" data-testid="ac-revisar">
            <button type="button" onClick={() => setVerDatos(!verDatos)} aria-expanded={verDatos} className="acm-plegable flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs font-medium text-slate-700">
              <ChevronRight className="acm-chev h-4 w-4" />
              Revisar valores leídos del PDF (en guaraníes, tal como figuran en el balance)
            </button>
            {verDatos && (
              <div className="acm-abre overflow-x-auto px-3 pb-3">
                <div className="mb-2 flex items-center gap-2 text-xs text-slate-600">
                  Cliente:
                  <input value={parsed.cliente || ""} onChange={(e) => setParsed({ ...parsed, cliente: e.target.value })} className={inp + " w-64"} data-testid="ac-nombre" />
                  RUC:
                  <input value={ruc} onChange={(e) => setRuc(e.target.value)} placeholder="80012345-6" className={inp + " w-36"} data-testid="ac-ruc" />
                </div>
                <table className="text-xs">
                  <thead><tr className="text-slate-500"><th className="py-1 pr-3 text-left font-medium">Línea</th>{parsed.anios.map((a) => <th key={a} className="px-1 text-right font-medium">{a}</th>)}</tr></thead>
                  <tbody>
                    {Object.entries(parsed.etiquetas || {}).map(([k, et]) => (
                      <tr key={k} className="border-t border-slate-100">
                        <td className="py-0.5 pr-3 text-slate-700">{et}</td>
                        {parsed.anios.map((a) => (
                          <td key={a} className="px-1">
                            <input value={parsed.datos[a]?.[k] != null ? Math.round(parsed.datos[a][k]).toLocaleString("es-PY") : ""}
                              onChange={(e) => editarDato(a, k, e.target.value)} inputMode="numeric"
                              className={inp + " w-36 text-right " + (parsed.datos[a]?.[k] == null ? "bg-amber-50" : "")} data-testid={`ac-dato-${k}-${a}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-[11px] text-slate-500">Si algún número no coincide con el balance, corregilo acá y apretá «Analizar». Al elegir Guaraníes abajo, cargá el tipo de cambio de cada año.</p>
              </div>
            )}
          </div>
        )}

        {!parsed && (
          <div className="pt-2 border-t border-slate-100" data-testid="ac-sin-balance">
            {!sb.abierto ? (
              <button type="button" className="text-xs text-emerald-800 underline" onClick={() => setSb({ ...sb, abierto: true })} data-testid="ac-sin-balance-btn">
                ¿Cliente sin balance propio? Cargá sus declaraciones SET (Marangatu), Informconf o informes de inmuebles
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-end gap-2 text-xs">
                  <label className="text-[10px] text-slate-500">Código del cliente<input value={sb.codigo} onChange={(e) => setSb({ ...sb, codigo: e.target.value.replace(/\D/g, "") })} className={inp + " block w-28"} placeholder="ej. 100565" data-testid="ac-sb-codigo" /></label>
                  <label className="text-[10px] text-slate-500">Nombre / razón social<input value={sb.nombre} onChange={(e) => setSb({ ...sb, nombre: e.target.value })} className={inp + " block w-64"} data-testid="ac-sb-nombre" /></label>
                  <span className="text-slate-500">Al confirmar estados financieros SET o IVA/IRE, usalos como balance desde la tarjeta «Declaraciones SET».</span>
                </div>
                {(sb.codigo || sb.nombre.trim()) && <GrupoEconomico cliente={sb.nombre.trim()} codigo={sb.codigo} esperar={esperarTarea} onSector={(s) => setMeta((m) => ({ ...m, sector_sugerido: s }))} onBalance={(r) => { aplicarLectura(r); setMeta((m) => ({ ...m, codigo: m.codigo || sb.codigo })); }} />}
              </div>
            )}
          </div>
        )}

        {parsed && (
          <div className="pt-2 border-t border-slate-100">
            <GrupoEconomico cliente={parsed.cliente} codigo={meta.codigo} esperar={esperarTarea} onBalance={(r) => aplicarLectura(r)} onSector={(s) => setMeta((m) => ({ ...m, sector_sugerido: s }))} />
          </div>
        )}

        {parsed && (
          <div className="grid gap-3 md:grid-cols-2 pt-1">
            <div>
              <div className="acm-titulo acm-paso-titulo mb-2"><span>2</span> El cliente</div>
              <label className="block text-[11px] text-slate-500 mb-1">Tipo de cliente (cambia las pruebas y las referencias)</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inp + " w-full"} data-testid="ac-tipo">
                {Object.entries(tiposTodos).map(([k, v]) => <option key={k} value={k}>{v.nombre}</option>)}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">{tiposTodos[tipo]?.descripcion}</p>
              {meta.sugerido && <p className="text-[11px] text-emerald-700 mt-0.5">Sugerencia del sistema: <b>{cfg?.tipos?.[meta.sugerido]?.nombre}</b> — {meta.motivo}</p>}
              <label className="block text-[11px] text-slate-500 mb-1 mt-2">Sector / rubro del cliente (cambia umbrales, plazo, garantías y textos)</label>
              <select value={sector} onChange={(e) => setSector(e.target.value)} className={inp + " w-full"} data-testid="ac-sector">
                {(sectores.length ? sectores : [{ k: "agro_agricultura", nombre: "Agro – agricultura" }]).map((s) => <option key={s.k} value={s.k}>{s.nombre}</option>)}
              </select>
              {meta.sector_sugerido && meta.sector_sugerido.sector !== sector && (
                <p className="text-[11px] text-emerald-700 mt-0.5" data-testid="ac-sector-sugerido">Sector sugerido: <b>{meta.sector_sugerido.nombre}</b> — {meta.sector_sugerido.motivo}
                  <button type="button" className="ml-1 underline" onClick={() => setSector(meta.sector_sugerido.sector)} data-testid="ac-sector-usar">usar</button></p>
              )}
              {sectores.find((s) => s.k === sector) && (
                <SectorInfo s={sectores.find((s) => s.k === sector)} abierto={verUmbrales} setAbierto={setVerUmbrales} onGuardado={cargarCfg} />
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Moneda del archivo</label>
                <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className={inp} data-testid="ac-moneda">
                  <option value="USD">Dólares (US$)</option>
                  <option value="PYG">Guaraníes (Gs.)</option>
                </select>
              </div>
              {moneda === "PYG" && parsed.anios.map((a) => {
                const it = tcInfo[String(a)];
                return (
                  <div key={a}>
                    <label className="block text-[11px] text-slate-500 mb-1">Tipo de cambio {a} (Gs. por US$)</label>
                    <input value={tc[String(a)] || ""} onChange={(e) => setTc({ ...tc, [String(a)]: milesEs(e.target.value) })} inputMode="decimal" placeholder="Ej.: 7.300" className={inp + " w-32"} data-testid={`ac-tc-${a}`} />
                    {it && <p className="text-[10px] text-slate-500 mt-0.5 max-w-[11rem]">DNIT {it.fecha_txt}: compra {desdeNum(it.compra)} · venta {desdeNum(it.venta)}{it.promedio_anual ? ` · prom. ${desdeNum(it.promedio_anual)}` : ""}</p>}
                  </div>
                );
              })}
              {moneda === "PYG" && (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid="ac-tc-dnit">
                  <div className="text-[11px] text-slate-500 mb-1">Traer de la DNIT</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <select value={tcMes} onChange={(e) => setTcMes(parseInt(e.target.value, 10))} className={inp + " h-8 text-xs"} title="Mes de cierre del ejercicio">
                      {["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"].map((m, i) => <option key={m} value={i + 1}>Cierre {m}</option>)}
                    </select>
                    <select value={tcBase} onChange={(e) => setTcBase(e.target.value)} className={inp + " h-8 text-xs"}>
                      <option value="compra">Compra al cierre</option>
                      <option value="venta">Venta al cierre</option>
                      <option value="promedio">Promedio del año</option>
                    </select>
                    <Button size="sm" variant="outline" disabled={busy} onClick={traerTc} className="h-8">Traer</Button>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Línea de crédito otorgada hoy (US$)</label>
                <input value={sinLinea ? "" : otorgada} disabled={sinLinea} onChange={(e) => setOtorgada(milesEs(e.target.value))} inputMode="decimal" placeholder={sinLinea ? "Sin línea aún" : "Ej.: 50.000"} className={inp + " w-40 disabled:bg-slate-100"} data-testid="ac-otorgada" />
                <label className="mt-1 flex items-center gap-1.5 text-[12px] text-slate-700"><input type="checkbox" checked={sinLinea} onChange={(e) => setSinLinea(e.target.checked)} data-testid="ac-sin-linea" /> Sin línea aún</label>
                <label className="block text-[11px] text-slate-500 mt-2 mb-1">Perfil del cliente {res?.perfil ? <span className="text-slate-400">· sugerido: {res.perfil.sugerido_nombre}</span> : null}</label>
                <select value={perfil} onChange={(e) => setPerfil(e.target.value)} className="h-8 w-full rounded border border-slate-200 px-2 text-xs" data-testid="ac-perfil">
                  <option value="">Usar el sugerido</option><option value="top">TOP</option><option value="normal">Normal</option><option value="condicionado">Condicionado</option><option value="nuevo">Nuevo</option><option value="nuevo_estrategico">Nuevo estratégico (TOP de mercado)</option>
                </select>
                {perfil ? <input value={perfilMotivo} onChange={(e) => setPerfilMotivo(e.target.value)} placeholder={perfil === "nuevo_estrategico" ? "Motivo (obligatorio)" : "Por qué cambiás el perfil"} className="mt-1 h-8 w-full rounded border border-slate-200 px-2 text-xs" data-testid="ac-perfil-motivo" /> : null}
                {res?.perfil ? <p className="text-[10px] text-slate-500 mt-0.5" data-testid="ac-perfil-razon">{res.perfil.nombre}: {(res.perfil.razones || []).join("; ")} · participación máx. {res.perfil.prov_pct}%</p> : null}
                {meta.linea_actual && !sinLinea ? <p className="text-[10px] text-slate-500 mt-0.5 max-w-[16rem]" title={meta.linea_origen || ""}>tomada del sistema{meta.linea_origen ? `: ${meta.linea_origen}` : ""}</p> : null}
                {meta.linea_dudosa && !sinLinea ? (
                  <p className="text-[10px] text-red-700 mt-0.5 max-w-[16rem]" data-testid="ac-linea-dudosa">
                    ⚠ En el sistema figura una línea de US$ {Math.round(meta.linea_sistema || 0).toLocaleString("es-PY")} ({meta.linea_origen}). Parece mal cargada y no la usé: escribí la línea real o corregí el anexo de líneas.
                  </p>
                ) : null}
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">La línea empieza el (dura 1 año)</label>
                <input type="date" value={vigencia} onChange={(e) => setVigencia(e.target.value)} className={inp + " w-40"} data-testid="ac-vigencia" />
                <p className="text-[10px] text-slate-500 mt-0.5">{vigencia ? `Vigencia: ${fmtFecha(vigencia)} al ${fmtFecha(finVigencia(vigencia))}` : "Si la dejás vacía: desde el 1 de octubre en curso"}</p>
              </div>
              <Button disabled={busy} onClick={() => analizar()} className="bg-[#0f5233] hover:bg-[#0c3f27] gap-1.5" data-testid="ac-analizar">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Analizar
              </Button>
            </div>
            {solicitadas && (
              <SolicitadasPanel sol={solicitadas} setSol={setSolicitadas} esc={res?.linea?.escenarios} otorgada={sinLinea ? 0 : aNumEs(otorgada) || 0} busy={busy}
                onUsar={(m) => {
                  const nuevo = { pagare_deudor: false, codeudoria: false, terceros: { on: false }, hipoteca: { on: false }, prenda: { on: false }, granos: { on: false }, ...m };
                  setSolicitadas(nuevo);
                  analizar({ ...payload(), solicitadas: nuevo });
                }} />
            )}
            <DatosAnalista extra={extra} setExtra={setExtra} esAgro={esAgro} />
            <input ref={pagRef} type="file" accept={ACEPTA} onChange={subirPagares} className="hidden" data-testid="ac-file-pagares" />
            {(propios.campos_extra || []).length > 0 && (
              <CamposEmpresa campos={propios.campos_extra} val={extra.campos || {}} setVal={(v) => { setExtra({ ...extra, campos: v }); setRes(null); }} />
            )}
            <VigentesPanel items={extra.vigentes} setItems={(v) => setExtra({ ...extra, vigentes: v })} propias={propios.garantias_extra || []}
              pagares={extra.pagares} setPagares={(v) => { setExtra({ ...extra, pagares: v }); setRes(null); }}
              pres={res?.pagares || extra.pagares?.resumen} subir={() => pagRef.current?.click()} busy={busy} />
            <DeudaPanel d={extra.deuda} setD={(v) => { setExtra({ ...extra, deuda: v }); setRes(null); }} externo={externo} />
            <InmueblesPanel items={extra.inmuebles} setItems={(v) => { setExtra({ ...extra, inmuebles: v }); setRes(null); }} tc={aNumEs(Object.values(tc || {}).filter((v) => String(v).trim() !== "").pop() || "") || null} />
            <input ref={pedRef} type="file" accept={ACEPTA} onChange={subirPedidos} className="hidden" data-testid="ac-file-pedidos" />
            <PedidosPanel items={extra.pedidos} setItems={(v) => setExtra({ ...extra, pedidos: v, ...(v.length ? {} : { pedidos_doc: null }) })} sistema={res?.pedidos_sistema || 0}
              doc={extra.pedidos_doc} subir={() => pedRef.current?.click()} busy={busy} />
            <PrestamoPanel p={prestamo} setP={setPrestamo} />
          </div>
        )}
      </div>

      {/* 2. Resultado */}
      {res && ult && (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-2xl bg-white p-6" data-testid="ac-acciones">
            <div className="mr-auto min-w-0 w-full xl:w-auto">
              <h2 className="tm-titulo text-slate-900 break-words" data-testid="ac-cliente-titulo">{(() => { const t = String(res.cliente || ""); const ws = t.split(" "); if (ws.length < 2) return t; const k = Math.ceil(ws.length / 2); return <>{ws.slice(0, k).join(" ")}<br />{ws.slice(k).join(" ")}</>; })()}</h2>
              {res.garantia_estado || res.ia?.riesgo || res.dictamen?.titulo ? <span className="inline-flex items-center gap-2 mt-3 rounded-full bg-[#E9F0E7] px-3.5 py-1.5 text-xs font-bold text-[#24331F]"><span className="h-2 w-2 rounded-full bg-[#007336]" />{res.garantia_estado || res.ia?.riesgo || res.dictamen?.titulo}</span> : null}
              <div className="text-xs text-slate-500 mt-2">{res.tipo_nombre} · ejercicio {anios[anios.length - 1]}{res.sector ? <span data-testid="ac-sector-usado"> · sector <b>{res.sector.nombre}</b> (liquidez ≥ {res.sector.umbrales.liquidez_min}, pasivo/PN ≤ {res.sector.umbrales.pasivo_pn_max}, deuda fin./EBITDA ≤ {res.sector.umbrales.deuda_fin_ebitda_max}, margen neto ≥ {res.sector.umbrales.margen_neto_ref}%)</span> : " · sector Agro – agricultura (umbrales por defecto)"}</div></div>
            <Button size="sm" variant="outline" disabled={busy} onClick={guardar} className="gap-1.5 rounded-full h-11 px-5" data-testid="ac-guardar"><Save className="h-4 w-4 text-[#0f5233]" /> Guardar análisis</Button>
            <Button size="sm" disabled={busy} onClick={() => bajarPdf("director")} className="gap-1.5 rounded-full h-11 px-5 tm-negra" data-testid="ac-pdf-director"><FileDown className="h-4 w-4" /> Resumen para el director (1 hoja)</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => bajarPdf("comercial")} className="gap-1.5 rounded-full h-11 px-5" data-testid="ac-pdf-comercial" title="Explicado en simple para el equipo comercial: cuánto se le puede vender, por qué no más y por qué cada garantía"><FileDown className="h-4 w-4 text-[#0f5233]" /> Resumen para comerciales</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => bajarPdf("general")} className="gap-1.5 rounded-full h-11 px-5" data-testid="ac-pdf-general"><FileDown className="h-4 w-4 text-red-600" /> Informe general</Button>
          </div>
          <div className="rf" key={`k-${res.cliente}-${anios.join("-")}`}>
            <div className="rf-kpis">
              {kpi.map((k, i) => (
                <div key={k.t} className="rf-kpi" style={{ animationDelay: `${0.08 * i + 0.1}s` }}>
                  <div className="rf-kpi-t">{k.t}</div>
                  <div className="flex items-end justify-between gap-2">
                    <div className="rf-kpi-v" style={{ color: k.mal ? "var(--rf-debe)" : "var(--rf-texto)" }}>{k.n !== undefined && k.n !== null ? <Cuenta valor={k.n} fmt={k.f} /> : k.v}</div>
                    <MiniCurva valores={k.serie} mal={k.mal} />
                  </div>
                  <div className="rf-kpi-n">{k.s}</div>
                </div>
              ))}
            </div>
          </div>

          {res.decision_directorio?.excepcion && (
            <div className="rounded-lg border-2 border-red-400 bg-red-50 px-4 py-3 text-sm text-red-900" data-testid="ac-excepcion-banner">
              <div className="text-[11px] font-bold uppercase tracking-wide text-red-700">Línea autorizada por excepción</div>
              <b>{res.decision_directorio.leyenda}</b> <span className="text-red-800">{res.decision_directorio.detalle}</span>
            </div>
          )}
          {(res.avisos || []).filter((a) => /línea/i.test(a)).map((a) => (
            <div key={a} className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="ac-aviso-linea">⚠ {a}</div>
          ))}
          <KanitzGauge k={res.kanitz} />
          <InteligenciaCard h={intel} busy={intelBusy} onInvestigar={investigar} cliente={parsed?.cliente} />
          <div className="grid items-start gap-4 xl:grid-cols-2">
            <ComoViene cp={res.comportamiento} tol={cfg?.comportamiento?.tolerancia || 30} />
            <AnalistaIA ia={ia} busy={iaBusy} res={res} onPedir={() => pedirIa()} />
          </div>
          {res.garantia_capacidad?.puntos?.length > 0 && <GarantiaCapacidadCard g={res.garantia_capacidad} />}
          {res.capacidad_real && <CapacidadRealCard c={res.capacidad_real} />}
          <ComoNosPaga cp={res.comportamiento} zf={res.zafra} />
          <Cronograma cr={res.cronograma} />
          <ContextoSector ctx={res.contexto_agro} refin={res.refinanciacion} auditado={res.auditado} auditor={res.auditor} zonas={res.zonas} />
          {res.prestamo && <PrestamoCard pr={res.prestamo} pia={ia?.prestamo} />}
          {(res.mejoras?.length > 0 || ia?.mejoras?.length > 0) && <MejorasCard sis={res.mejoras || []} ia={ia?.mejoras || []} />}
          <ChatIA chat={chat} busy={chatBusy} onAsk={preguntar} disabled={!res} />

          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Detalle del cálculo automático</div>
          <div className="rf space-y-4" key={`${res.cliente}-${anios.join("-")}`}>
            <VeredictoBalance ev={res.ev} conclusion={res.narr.conclusion} />
            <ComplianceCard comp={comp} ajuste={res.ev.compliance} busy={compBusy} onVerificar={() => verificarAntecedentes(true)} ruc={ruc} setRuc={setRuc} />
            {res.scoring_zafra && <ScoringZafraCard res={res} payload={payload} setExtra={setExtra} />}
            {res.garantias_reales && <GarantiasRealesCard g={res.garantias_reales} onUsarSNC={(detalle) => { setExtra((x) => ({ ...x, vigentes: [...(x.vigentes || []), { tipo: "Hipoteca", inmueble: 0, valor: 0, detalle, auto: "snc" }] })); toast.success("Agregada a garantías vigentes: completá tasación y monto en «Garantías vigentes»"); }} />}
            {res.inmuebles_detalle?.items?.length > 0 && <InmueblesResumenCard d={res.inmuebles_detalle} manuales={extra.inmuebles || []} tc={aNumEs(Object.values(tc || {}).filter((v) => String(v).trim() !== "").pop() || "") || null} busy={busy}
              onGuardar={(cambios) => { const mk = (m) => `${m.finca_matricula || ""}|${m.padron_catastro || ""}`; const nuevos = cambios.map(({ i, it }) => ({ titular: i.titular, finca_matricula: i.finca_matricula, padron_catastro: i.padron_catastro, distrito: i.distrito, departamento: i.departamento, superficie_ha: i.superficie_ha, uso_cultivo: i.uso_cultivo, hipoteca_gravamen: i.hipoteca_gravamen, acreedor: i.acreedor, gravamen_usd: i.gravamen_usd, catastro_verificado: i.catastro_verificado, ...((extra.inmuebles || []).find((m) => mk(m) === mk(i)) || {}), ...it })); const claves = new Set(nuevos.map(mk)); const ex = { ...extra, inmuebles: [...(extra.inmuebles || []).filter((m) => !claves.has(mk(m))), ...nuevos] }; setExtra(ex); analizar({ ...payload(), extra: ex }); }}
              onFijar={(i) => { const v = window.prompt(`Pegá el pin de Google Maps, coordenadas "lat, lng" o el N° de Padrón / Cta. Cte. para ${i.finca_matricula || i.padron_catastro || "el inmueble"}:`); if (!v) return; const esCoord = /-?\d{1,2}\.\d+\s*,\s*-?\d{1,3}\.\d+|^http/.test(v); const nuevo = { titular: i.titular, finca_matricula: i.finca_matricula, padron_catastro: esCoord ? i.padron_catastro : v, distrito: i.distrito, departamento: i.departamento, superficie_ha: i.superficie_ha, valor_usd: i.valor_usd, uso_cultivo: i.uso_cultivo, hipoteca_gravamen: i.hipoteca_gravamen, acreedor: i.acreedor, gravamen_usd: i.gravamen_usd, catastro_verificado: i.catastro_verificado, ...(esCoord ? (v.startsWith("http") ? { maps_url: v } : { coordenadas: v }) : {}) }; setExtra((x) => ({ ...x, inmuebles: [...(x.inmuebles || []).filter((m) => (m.finca_matricula || "") !== (i.finca_matricula || "") || (m.padron_catastro || "") !== (i.padron_catastro || "")), nuevo] })); setRes(null); toast.success("Ubicación fijada: volvé a analizar para verla en el PDF"); }} />}
            {res.planilla_tm?.capacidad_pago_archivo?.length > 0 && <CapacidadPagoCard p={res.planilla_tm} anios={res.anios} />}
            <GraficosBalance M={M} anios={anios} />
          </div>
          <Composicion comp={res.composicion} anios={anios} fuente={res.fuente_balance} />

          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-emerald-50 text-slate-700">
                <th className="px-3 py-2 text-left">Evolución en números</th>
                {anios.map((a) => <th key={a} className="px-3 py-2 text-right">{a}</th>)}
                <th className="px-3 py-2 text-right">Cambio último año</th>
              </tr></thead>
              <tbody>
                {[["Ventas", "ventas"], ["Ganancia neta", "neto"], ["Patrimonio (lo que tiene menos lo que debe)", "pn"], ["Deuda total", "pasivo"]].map(([n, k]) => {
                  const v = anios.map((a) => M[String(a)][k]);
                  const ch = v.length > 1 && v[v.length - 2] > 0 ? (v[v.length - 1] / v[v.length - 2] - 1) * 100 : null;
                  return (
                    <tr key={k} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">{n}</td>
                      {v.map((x, i) => <td key={i} className="px-3 py-1.5 text-right tabular-nums">{usd(x)}</td>)}
                      <td className="px-3 py-1.5 text-right">{ch === null ? "—" : `${ch >= 0 ? "+" : ""}${num(ch, 1)}%`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-sm font-semibold text-emerald-700 mb-1">Lo bueno</div>
              <ul className="text-sm text-slate-700 space-y-1 list-disc pl-4">
                {res.narr.fortalezas.length ? res.narr.fortalezas.map((t) => <li key={t}>{t[0].toUpperCase() + t.slice(1)}.</li>) : <li className="list-none text-slate-400">Sin puntos fuertes destacados.</li>}
              </ul>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-sm font-semibold text-red-700 mb-1">Lo que preocupa</div>
              <ul className="text-sm text-slate-700 space-y-1 list-disc pl-4">
                {res.narr.alertas.length ? res.narr.alertas.map((t) => <li key={t}>{t[0].toUpperCase() + t.slice(1)}.</li>) : <li className="list-none text-slate-400">Sin alertas.</li>}
              </ul>
            </div>
          </div>
          {res.narr.calidad_ganancia && <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{res.narr.calidad_ganancia}</div>}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-sm font-semibold text-[#0f5233] mb-1">Qué recomendamos hacer</div>
            <ol className="text-sm text-slate-700 space-y-1 list-decimal pl-5">{res.narr.condiciones.map((t) => <li key={t}>{t}</li>)}</ol>
          </div>

          {/* Pruebas */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <div className="px-3 py-2 text-sm font-semibold text-slate-700 border-b border-slate-100">Detalle de las pruebas ({res.tipo_nombre})</div>
            <table className="w-full text-xs">
              <tbody>
                {res.ev.pruebas.map((p) => (
                  <tr key={p.k} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2"><div className="font-semibold text-slate-800">{p.nombre}</div><div className="text-[11px] text-slate-500">{p.explicacion}</div></td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap">{fmtValor(p, p.valor)}</td>
                    <td className="px-3 py-2 text-center text-slate-500 whitespace-nowrap">{fmtRef(p)}</td>
                    <td className={`px-3 py-2 text-center font-bold ${p.cumple ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{p.cumple ? "SÍ" : "NO"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100">
              Proyección de caja: si cobra el 90% de lo que le deben, vende el 80% del stock y paga todo lo que vence en el año, la caja queda en <b>{usd(ult.generacion_caja)}</b>;
              si además el negocio crece 5% y los acreedores renuevan 15% más, el {ult.superavit_deficit < 0 ? "faltante" : "sobrante"} de financiamiento sería de <b>{usd(Math.abs(ult.superavit_deficit))}</b>.
            </p>
          </div>

          {/* Línea de crédito */}
          <LineaCredito res={res} hist={hist} setHist={setHist} guardarHist={guardarHist} />
          {res.participacion && (
            <ParticipacionCard p={res.participacion} cfg={extra.participacion} busy={busy}
              aplicar={(v) => { const ex = { ...extra, participacion: v }; setExtra(ex); analizar({ ...payload(), extra: ex }); }} />
          )}
          <DecisionDirectorio d={res.decision_directorio} cfg={extra.decision} busy={busy}
            aplicar={(v) => { const ex = { ...extra, decision: v }; setExtra(ex); analizar({ ...payload(), extra: ex }); }} />
        </>
      )}

      {/* Referencias */}
      <CriterioEmpresa externo={externo} />
      <EstiloRedaccion />

      {cfg && (
        <div className="rounded-lg border border-slate-200 bg-white">
          <button onClick={abrirRef} aria-expanded={verRef} className="acm-plegable w-full min-h-[52px] px-4 py-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="ac-ref-toggle">
            <ChevronRight className="acm-chev h-4 w-4 text-[#0f5233]" /> Referencias y reglas de la línea (editables)
            {cfg.personalizado && <span className="text-[10px] font-normal text-amber-600">personalizadas</span>}
          </button>
          {verRef && edit && (
            <div className="acm-abre px-4 pb-4 space-y-4">
              {Object.entries(edit.tipos).map(([k, t]) => (
                <div key={k}>
                  <div className="text-xs font-semibold text-[#0f5233] mb-1">{t.nombre}</div>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {t.pruebas.map((p, i) => (
                      <label key={p.k} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="flex-1">{cfg.meta[p.k]?.[0]} ({OP[p.op]})</span>
                        <input type="number" step="any" value={p.v} className="h-7 w-20 rounded border border-slate-200 px-2 text-right text-xs"
                          onChange={(e) => { const c = JSON.parse(JSON.stringify(edit)); c.tipos[k].pruebas[i].v = e.target.value === "" ? 0 : parseFloat(e.target.value); setEdit(c); }} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <div className="text-xs font-semibold text-[#0f5233] mb-1">Línea sugerida: % del patrimonio según el dictamen</div>
                <div className="grid gap-2 sm:grid-cols-4">
                  {Object.entries(edit.linea_pct).map(([c, v]) => (
                    <div key={c} className="text-[11px] text-slate-600 border border-slate-100 rounded p-2">
                      <div className="font-semibold capitalize mb-1">{c}</div>
                      {[["confianza", "En confianza %"], ["tope", "Tope máximo %"]].map(([f, l]) => (
                        <label key={f} className="flex items-center justify-between gap-2 mb-1">{l}
                          <input type="number" step="any" value={v[f]} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs"
                            onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.linea_pct[c][f] = parseFloat(e.target.value) || 0; setEdit(cc); }} />
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-[#0f5233] mb-1">Cuánto cuenta cada garantía (%)</div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(edit.factores).map(([g, v]) => (
                    <label key={g} className="flex items-center gap-2 text-[11px] text-slate-600">
                      <span className="flex-1">{g}</span>
                      <input type="number" step="any" value={v} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs"
                        onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.factores[g] = parseFloat(e.target.value) || 0; setEdit(cc); }} />
                    </label>
                  ))}
                </div>
              </div>
              {edit.comportamiento && (
                <div>
                  <div className="text-xs font-semibold text-[#0f5233] mb-1">Cómo se mide el comportamiento de pago <span className="font-normal text-slate-500">(pesos en %, deben sumar 100)</span></div>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                    {[["palabra", "Cumple su palabra"], ["puntualidad", "Puntualidad ajustada"], ["causa", "Causa del atraso"], ["termina", "Termina pagando"], ["tolerancia", "Atraso normal del sector (días)"]].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="flex-1">{l}</span>
                        <input type="number" step="any" value={edit.comportamiento[k] ?? ""} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs"
                          onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.comportamiento[k] = parseFloat(e.target.value) || 0; setEdit(cc); }} />
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {edit.perfiles && (
                <div data-testid="ac-ref-perfiles" className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-semibold text-[#0f5233]">Perfiles de cliente <span className="font-normal text-slate-500">(participación máxima en sus proveedores y techo por historial, por perfil)</span></div>
                    <button type="button" className="text-[11px] text-sky-700 underline" data-testid="ac-perfiles-default"
                      onClick={() => { const cc = JSON.parse(JSON.stringify(edit)); cc.perfiles = { perfiles: { top: { nombre: "TOP", prov_pct: 50, pico_factor: 1.2 }, normal: { nombre: "Normal", prov_pct: 30 }, condicionado: { nombre: "Condicionado", prov_pct: 15 }, nuevo: { nombre: "Nuevo", prov_pct: 10, tope_fijo: 50000 }, nuevo_estrategico: { nombre: "Nuevo estratégico", prov_pct: 30, tope_fijo: 200000 } }, umbrales: { top_anios: 5, top_compras: 1000000, top_pruebas_min: 5, cond_pruebas: 4, cond_vencido_dias: 60, nuevo_meses: 12 } }; setEdit(cc); }}>Restaurar valores por defecto</button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {Object.entries(edit.perfiles.perfiles || {}).map(([k, v]) => (
                      <div key={k} className="rounded border border-slate-200 p-2 text-[11px] text-slate-600">
                        <div className="font-semibold text-slate-800 mb-1">{v.nombre}</div>
                        {[["prov_pct", "% máx. de sus proveedores", "%"], ["pico_factor", "Factor sobre el pico pagado (24 m)", "×"], ["tope_fijo", "Tope fijo (US$)", ""]].filter(([kk]) => v[kk] !== undefined).map(([kk, l, u]) => (
                          <label key={kk} className="flex items-center gap-2 mb-1"><span className="flex-1">{l}</span>
                            <input type="number" step="any" value={v[kk] ?? ""} className="h-7 w-24 rounded border border-slate-200 px-2 text-right text-xs" data-testid={`ac-perfil-${k}-${kk}`}
                              onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.perfiles.perfiles[k][kk] = parseFloat(e.target.value) || 0; setEdit(cc); }} />{u}</label>
                        ))}
                      </div>
                    ))}
                    <div className="rounded border border-slate-200 p-2 text-[11px] text-slate-600">
                      <div className="font-semibold text-slate-800 mb-1">Umbrales para sugerir TOP</div>
                      {[["top_anios", "Años de relación continua (mín.)"], ["top_compras", "Compras promedio por año (US$, mín.)"], ["nuevo_meses", "Meses de relación para dejar de ser Nuevo"], ["cond_pruebas", "Condicionado si cumple ≤ pruebas"], ["cond_vencido_dias", "Condicionado si vencido > días"]].map(([kk, l]) => (
                        <label key={kk} className="flex items-center gap-2 mb-1"><span className="flex-1">{l}</span>
                          <input type="number" step="any" value={edit.perfiles.umbrales?.[kk] ?? ""} className="h-7 w-24 rounded border border-slate-200 px-2 text-right text-xs" data-testid={`ac-perfil-umbral-${kk}`}
                            onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.perfiles.umbrales = cc.perfiles.umbrales || {}; cc.perfiles.umbrales[kk] = parseFloat(e.target.value) || 0; setEdit(cc); }} /></label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {edit.prudencia && (
                <div data-testid="ac-ref-prudencia">
                  <div className="text-xs font-semibold text-[#0f5233] mb-1">Prudencia: no ser el proveedor principal <span className="font-normal text-slate-500">(el máximo prudente es el menor de estos topes)</span></div>
                  <label className="flex items-center gap-2 text-[11px] text-slate-600 mb-1">
                    <input type="checkbox" checked={!!edit.prudencia.activa} onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.prudencia.activa = e.target.checked ? 1 : 0; setEdit(cc); }} /> Aplicar estos topes
                  </label>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[["prov_pct", "Como mucho este % de lo que el cliente debe a todos sus proveedores"], ["crec_pct", "Sobre lo que más nos compró en un año, dejar crecer este %"], ["nuevo_pct", "Cliente nuevo: arrancar con este % del máximo por patrimonio"],
                      ["part_top", "Cliente TOP: podemos llegar a este % de sus proveedores"], ["part_bueno", "Buen cliente: hasta este %"],
                      ["part_normal", "Cliente normal: hasta este %"], ["part_riesgo", "Cliente de riesgo: hasta este %"]].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="flex-1">{l}</span>
                        <input type="number" step="any" value={edit.prudencia[k] ?? ""} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs"
                          onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.prudencia[k] = parseFloat(e.target.value) || 0; setEdit(cc); }} />%
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <PropiosEditor v={edit.propios || {}} setV={(pv) => setEdit({ ...edit, propios: pv })} tipos={cfg.tipos} meta={cfg.meta} />
              {edit.capacidad && (
                <div data-testid="ac-ref-capacidad">
                  <div className="text-xs font-semibold text-[#0f5233] mb-1">Capacidad real vs. declarada <span className="font-normal text-slate-500">(cuando el cliente declara menos de lo que vende)</span></div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[["tope_ajuste", "Tope del ajuste (veces lo que da el balance)", ""], ["umbral", "Ajustar solo si las pruebas superan lo declarado en (veces)", ""],
                      ["min_fuentes", "Pruebas independientes mínimas", ""], ["insumos_productor_pct", "Productor: los insumos son este % de lo que vende", "%"],
                      ["insumos_reventa_pct", "Revendedor: lo que compra es este % de lo que vende", "%"]].map(([k, l, u]) => (
                      <label key={k} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="flex-1">{l}</span>
                        <input type="number" step="any" value={edit.capacidad[k] ?? ""} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs"
                          onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.capacidad[k] = parseFloat(e.target.value) || 0; setEdit(cc); }} />{u}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {edit.agro && (
                <div data-testid="ac-ref-agro">
                  <div className="text-xs font-semibold text-[#0f5233] mb-1">Por hectárea y préstamo en dinero <span className="font-normal text-slate-500">(costos de referencia en US$/ha: actualizalos cada zafra)</span></div>
                  <label className="flex items-center gap-2 text-[11px] text-slate-600 mb-1">
                    <input type="checkbox" checked={!!edit.agro.activa} onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.agro.activa = e.target.checked ? 1 : 0; setEdit(cc); }} /> Aplicar techo por hectárea y descuento por atraso de 31 a 60 días
                  </label>
                  <div className="overflow-x-auto">
                    <table className="text-[11px] text-slate-600">
                      <thead><tr><th className="pr-3 text-left font-medium">US$ por ha</th>{["soja", "maiz", "trigo", "arroz"].map((c) => <th key={c} className="px-1 font-medium">{{ soja: "Soja", maiz: "Maíz", trigo: "Trigo", arroz: "Arroz" }[c]}</th>)}</tr></thead>
                      <tbody>
                        {[["insumos_ha", "Insumos"], ["efectivo_ha", "Efectivo (cosecha y flete)"]].map(([g, l]) => (
                          <tr key={g}><td className="pr-3">{l}</td>{["soja", "maiz", "trigo", "arroz"].map((c) => (
                            <td key={c} className="px-1 py-0.5"><input type="number" step="any" value={edit.agro[g]?.[c] ?? ""} className="h-7 w-20 rounded border border-slate-200 px-2 text-right text-xs"
                              onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.agro[g][c] = parseFloat(e.target.value) || 0; setEdit(cc); }} /></td>
                          ))}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-1 grid gap-2 sm:grid-cols-3">
                    {[["part_pct", "Financiamos como mucho este % de lo que gasta en insumos por hectárea"], ["efectivo_pct", "El préstamo en dinero no pasa de este % de la línea"], ["pen_31_60", "Con atrasos de 31 a 60 días, bajar el máximo este %"]].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="flex-1">{l}</span>
                        <input type="number" step="any" value={edit.agro[k] ?? ""} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs"
                          onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.agro[k] = parseFloat(e.target.value) || 0; setEdit(cc); }} />%
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {edit.zafra && (
                <div data-testid="ac-ref-zafra" className="mt-3 rounded-md border border-slate-200 p-3">
                  <div className="mb-2 text-xs font-semibold text-slate-700">Scoring de pago vs. zafra (extracto de cuentas a cobrar)</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {[["recupero_min", "Recupero mínimo para «Moroso técnico rentable» / «Cumplidor puntual»", "%"], ["recupero_tolerado", "Recupero tolerado (debajo = riesgo de incobrabilidad)", "%"],
                      ["antiguedad_min", "Antigüedad mínima para «Moroso técnico rentable»", "años"], ["atraso_min", "Atraso normal de zafra: desde", "días"], ["atraso_max", "Atraso normal de zafra: hasta", "días"],
                      ["soja_desde", "Liquidación de soja: mes desde", "mes"], ["soja_hasta", "Liquidación de soja: mes hasta", "mes"], ["zafrina_desde", "Zafriña/maíz/trigo: mes desde", "mes"], ["zafrina_hasta", "Zafriña/maíz/trigo: mes hasta", "mes"]].map(([k, l, u]) => (
                      <label key={k} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="flex-1">{l}</span>
                        <input type="number" step="any" value={edit.zafra[k] ?? ""} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs" data-testid={`ac-zafra-cfg-${k}`}
                          onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.zafra[k] = parseFloat(e.target.value) || 0; setEdit(cc); }} />{u}
                      </label>
                    ))}
                    {[["vto_soja", "Vencimiento sugerido soja (dd/mm)"], ["vto_zafrina", "Vencimiento sugerido zafriña (dd/mm)"]].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="flex-1">{l}</span>
                        <input value={edit.zafra[k] ?? ""} maxLength={5} className="h-7 w-16 rounded border border-slate-200 px-2 text-right text-xs" data-testid={`ac-zafra-cfg-${k}`}
                          onChange={(e) => { const cc = JSON.parse(JSON.stringify(edit)); cc.zafra[k] = e.target.value; setEdit(cc); }} />
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={guardarRef} className="bg-[#0f5233] hover:bg-[#0c3f27]" data-testid="ac-ref-guardar">Guardar referencias</Button>
                <Button size="sm" variant="ghost" onClick={restaurarRef} className="gap-1 text-slate-500"><RotateCcw className="h-4 w-4" /> Volver a las originales</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Guardados */}
      <div className="rounded-lg border border-slate-200 bg-white" data-testid="ac-guardados">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">Análisis guardados <span className="text-xs font-normal text-slate-400">({guardados.length})</span></div>
        {guardados.length === 0 ? <p className="px-4 py-5 text-sm text-slate-400 text-center">Todavía no guardaste ningún análisis.</p> : (
          <div className="divide-y divide-slate-100">
            {guardados.map((g) => (
              <div key={g.id} className="px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${COLOR[g.color]?.box || ""} ${COLOR[g.color]?.txt || ""}`}>{g.dictamen}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{g.cliente}</div>
                  <div className="text-[11px] text-slate-500">Ejercicio {g.anio} · {g.puntaje} pruebas · línea sugerida {usd(g.linea_sugerida)} · {g.created_by_name} · {(g.created_at || "").slice(0, 10)}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => abrir(g.id)} className="gap-1"><RotateCcw className="h-4 w-4" /> Abrir</Button>
                <button onClick={() => borrar(g.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const EXTRA_VACIO = {
  zafra: { cultivo: "", hectareas: "", rendimiento: "", precio: "", comprometido: "" },
  bancos: { deuda_bancos: "", deuda_otros: "", informconf: "", embargos: false, nota: "" },
  vendedor: { antiguedad: "", recomienda: "", opinion: "" },
  balance: { auditado: null, auditor: "" },
  zonas: { principal: { departamento: "", localidad: "" }, otras: [] },
  vigentes: [],
  pedidos: [],
  inmuebles: [],
};
const VIG_TIPOS = ["Pagaré con codeudoría", "Pagaré a sola firma", "Pagarés de terceros", "Hipoteca", "Prenda", "Contrato de granos", "Fideicomiso", "Otra"];
const POR_VTO = ["Pagaré con codeudoría", "Pagaré a sola firma", "Pagarés de terceros"];
const sumaVto = (vs) => (vs || []).reduce((a, v) => a + (aNumEs(v.monto) || 0), 0);

/* Lista editable de vencimientos (fecha + monto) */
function ListaVtos({ items, onChange, etiqueta = "vencimiento" }) {
  const lista = items || [];
  const upd = (i, k, v) => onChange(lista.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="w-full space-y-1 pl-2">
      {lista.map((v, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <span className="text-slate-500">Vence</span>
          <input type="date" value={v.vto || ""} onChange={(e) => upd(i, "vto", e.target.value)} className={inpA} />
          <span className="text-slate-500">US$</span>
          <input value={milesEs(v.monto ?? "")} onChange={(e) => upd(i, "monto", milesEs(e.target.value))} inputMode="decimal" placeholder="0" className={inpA + " w-28 text-right"} />
          {v.detalle !== undefined && <input value={v.detalle || ""} onChange={(e) => upd(i, "detalle", e.target.value)} placeholder="Detalle" className={inpA + " w-48"} />}
          <button type="button" onClick={() => onChange(lista.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...lista, { vto: "", monto: "" }])} className="text-[#0f5233] hover:underline">+ Agregar {etiqueta}</button>
    </div>
  );
}

/* Deuda actual del cliente con nosotros (opcional): reemplaza al dato del sistema */
function DeudaPanel({ d, setD, externo }) {
  const x = d || {};
  const n = (k, ph) => (
    <input value={milesEs(x[k] ?? "")} onChange={(e) => setD({ ...x, [k]: milesEs(e.target.value) })} inputMode="decimal" placeholder={ph} className={inpA + " text-right w-32"} />
  );
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-1.5" data-testid="ac-deuda">
      <div className="font-semibold text-[#0f5233]">Deuda actual del cliente con nosotros <span className="font-normal text-slate-500">
        (opcional{externo ? "; si cargás el historial con lo abierto, se toma de ahí" : ": reemplaza al dato del sistema"})</span></div>
      <div className="flex flex-wrap items-center gap-2">
        <span>Total US$</span>{n("total", "0")}
        <span>de eso vencido US$</span>{n("vencido", "0")}
        {(x.total || x.vencido) && <button type="button" onClick={() => setD({})} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

/* Pedidos pendientes de entrega: editables y por vencimiento (reemplazan al dato del sistema) */
function PedidosPanel({ items, setItems, sistema, doc, subir, busy }) {
  const lista = items || [];
  const [ver, setVer] = useState(false);
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs text-slate-700" data-testid="ac-pedidos">
      <div className="font-semibold text-[#0f5233]">Pedidos pendientes de entrega <span className="font-normal text-slate-500">(cargalos por vencimiento: si cargás algo acá, reemplaza al dato del sistema)</span></div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={subir} className="h-8 gap-1.5" data-testid="ac-upload-pedidos"><Upload className="h-4 w-4" /> Cargar pedidos desde PDF o Excel</Button>
        <button type="button" onClick={() => bajarModelo("pedidos")} className="text-slate-500 underline">Planilla modelo</button>
        <span className="text-slate-500">El «Listado de pedidos de ventas» con «solamente productos pendientes de entrega»: se agrupa solo por vencimiento.</span>
      </div>
      {doc && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-slate-700">
          📄 De «{doc.archivo}»: <b>{doc.pedidos}</b> pedidos ({doc.lineas} productos) por <b>{usd(doc.total)}</b>.
          {doc.detalle?.length > 0 && <button type="button" onClick={() => setVer(!ver)} className="ml-2 text-[#0f5233] underline">{ver ? "Ocultar detalle" : "Ver detalle"}</button>}
          {ver && (
            <table className="mt-1.5 w-full text-[11px]">
              <thead><tr className="text-left text-slate-500"><th className="py-0.5">Pedido</th><th>Producto</th><th>Vence</th><th className="text-right">Pendiente</th></tr></thead>
              <tbody>{doc.detalle.map((r, i) => (
                <tr key={i} className="border-t border-emerald-100"><td className="py-0.5">{r.pedido}</td><td>{r.producto}</td><td>{fdm(r.vto)}</td><td className="text-right tabular-nums">{usd(r.pendiente)}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
      {sistema > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-slate-600">
          Según el sistema: <b>{usd(sistema)}</b> sin fecha de vencimiento.
          {lista.length === 0 && <button type="button" onClick={() => setItems([{ vto: "", monto: Math.round(sistema).toLocaleString("es-PY") }])} className="text-[#0f5233] hover:underline">Cargarlo por vencimiento</button>}
        </div>
      )}
      <ListaVtos items={lista} onChange={setItems} etiqueta="pedido por vencimiento" />
      {lista.length > 0 && <div className="text-slate-600">Total pedidos cargados: <b>{usd(sumaVto(lista))}</b></div>}
    </div>
  );
}

/* Garantías que el cliente YA nos dio (firmadas o inscriptas): suman a la línea */
const fdm = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : iso || "");
const MESES_C = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const mesTxt = (m) => (m && m.length >= 7 ? `${MESES_C[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(0, 4)}` : m);

/* Listado de pagarés del sistema: agrupado por vencimiento, firmados vs. sin firmar y lo que falta emitir (los devueltos no se muestran) */
function PagaresListado({ pagares, setPagares, pres, subir, busy, hayManual }) {
  const [verSin, setVerSin] = useState(true);
  if (!pagares?.filas?.length) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={subir} className="gap-1.5" data-testid="ac-upload-pagares"><Upload className="h-4 w-4" /> Cargar listado de pagarés (PDF o Excel)</Button>
        <button type="button" onClick={() => bajarModelo("pagares")} className="text-xs text-slate-500 underline">Planilla modelo</button>
        <span className="text-slate-500">El «Listado de pagarés registrados» del sistema: suma solo lo firmado que está en nuestro poder.</span>
      </div>
    );
  }
  const r = pres || {};
  const pe = r.pendientes;
  const colS = { bien: "text-emerald-700", ojo: "text-amber-700", mal: "text-red-700", info: "text-slate-600" };
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5 space-y-2" data-testid="ac-pagares">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">📄 Listado de pagarés</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px]">{pagares.archivo} · {r.cantidad ?? pagares.filas.length} pagarés</span>
        <span className="text-slate-500">Los firmados cuentan como</span>
        <select value={pagares.tipo || "Pagaré con codeudoría"} onChange={(e) => setPagares({ ...pagares, tipo: e.target.value })} className={inpA}>
          <option>Pagaré con codeudoría</option><option>Pagaré a sola firma</option>
        </select>
        <button type="button" onClick={subir} disabled={busy} className="text-[#0f5233] hover:underline">Cambiar archivo</button>
        <button type="button" onClick={() => setPagares(null)} className="text-slate-400 hover:text-red-600" title="Quitar el listado"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1"><span className="text-[10px] text-slate-500 block">En nuestro poder (firmados)</span><b className="text-sm text-[#0f5233]" data-testid="ac-pag-garantia">{usd(r.garantia_cuenta ?? r.garantia)}</b>{r.en_banco > 0 && <span className="text-[10px] text-slate-500"> · {usd(r.en_banco)} en bancos</span>}</span>
        <span className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1"><span className="text-[10px] text-slate-500 block">Sin firmar (no cuentan)</span><b className="text-sm text-amber-800">{usd(r.sin_firma)}</b>{r.max_dias_sin_firma > 0 && <span className="text-[10px] text-amber-800"> · hasta {r.max_dias_sin_firma} días</span>}</span>
        {pe?.hay_deuda && <span className={"rounded-md border px-2.5 py-1 " + (pe.falta > 0.5 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50")}><span className="text-[10px] text-slate-500 block">Faltan emitir (facturas abiertas)</span><b className={"text-sm " + (pe.falta > 0.5 ? "text-red-700" : "text-[#0f5233]")} data-testid="ac-pag-faltan">{usd(pe.falta)}</b></span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="text-slate-500 border-b border-slate-200">
            <th className="py-1 pr-2 text-left font-medium">Vencimiento</th><th className="px-2 text-right font-medium">Pagarés</th><th className="px-2 text-right font-medium">Total</th>
            <th className="px-2 text-right font-medium">Firmados</th><th className="px-2 text-right font-medium">Sin firmar</th>
          </tr></thead>
          <tbody>
            {(r.por_vto || []).map((g) => (
              <tr key={g.vto} className={"border-b border-slate-100 " + (g.vencido ? "text-slate-500" : "")}>
                <td className="py-1 pr-2">{fdm(g.vto)}{g.vencido ? " · vencido" : ""}</td>
                <td className="px-2 text-right tabular-nums">{g.cantidad}</td>
                <td className="px-2 text-right tabular-nums font-semibold">{usd(g.total)}</td>
                <td className="px-2 text-right tabular-nums text-emerald-700">{g.firmado ? usd(g.firmado) : "—"}{g.cuenta != null && g.cuenta < g.firmado - 0.5 ? <span className="block text-[10px] text-amber-700">cuenta {usd(g.cuenta)}</span> : null}</td>
                <td className="px-2 text-right tabular-nums text-amber-700">{g.sin_firma ? usd(g.sin_firma) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(r.sin_firmar || []).length > 0 && (
        <div>
          <button type="button" onClick={() => setVerSin(!verSin)} className="flex items-center gap-1 font-semibold text-amber-800">{verSin ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Pagarés sin firmar ({r.sin_firmar.length})</button>
          {verSin && (
            <ul className="mt-1 space-y-0.5 pl-5 list-disc">
              {r.sin_firmar.map((f) => (
                <li key={f.nro}>Nº {f.nro} · {usd(f.valor)} · vence {fdm(f.vto)} · {f.estado_txt.toLowerCase()}{f.portador ? ` (${f.portador})` : ""} · <b className={f.dias_sin_firma > 30 ? "text-red-700" : "text-amber-800"}>{f.dias_sin_firma} días sin firma</b> desde la emisión ({fdm(f.emision)})</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {pe?.hay_deuda && pe.meses?.length > 0 && (
        <div className="overflow-x-auto">
          <div className="font-semibold text-slate-600 mb-0.5">Facturas abiertas vs. pagarés emitidos (por mes)</div>
          <table className="text-[11px]">
            <thead><tr className="text-slate-500"><th className="pr-3 text-left font-medium">Mes</th><th className="px-2 text-right font-medium">Facturas abiertas</th><th className="px-2 text-right font-medium">Pagarés emitidos</th><th className="px-2 text-right font-medium">Falta emitir</th></tr></thead>
            <tbody>
              {pe.meses.map((m) => (
                <tr key={m.mes}><td className="pr-3">{mesTxt(m.mes)}</td><td className="px-2 text-right tabular-nums">{usd(m.deuda)}</td><td className="px-2 text-right tabular-nums">{usd(m.pagares)}</td>
                  <td className={"px-2 text-right tabular-nums font-semibold " + (m.falta > 0.5 ? "text-red-700" : "text-slate-400")}>{m.falta > 0.5 ? usd(m.falta) : "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!pe && <p className="text-[10px] text-slate-500">Al «Analizar» se cruza con las facturas abiertas para ver si faltan pagarés por emitir.</p>}
      {pe && !pe.hay_deuda && <p className="text-[10px] text-slate-500">No hay facturas abiertas por vencimiento (cargá el historial de compras con lo abierto) para ver si faltan pagarés por emitir.</p>}
      {(r.senales || []).length > 0 && <ul className="space-y-0.5">{r.senales.map(([n, t]) => <li key={t} className={colS[n] || ""}>• {t}</li>)}</ul>}
      {hayManual && <p className="text-[11px] text-amber-700">Ojo: también hay pagarés cargados a mano abajo. Si son los mismos del listado, borralos para no contarlos dos veces.</p>}
    </div>
  );
}

function PropiosEditor({ v, setV, tipos, meta }) {
  const lst = (k) => v[k] || [];
  const setL = (k, arr) => setV({ ...v, [k]: arr });
  const upd = (k, i, campo, val) => setL(k, lst(k).map((x, j) => (j === i ? { ...x, [campo]: val } : x)));
  const quitar = (k, i) => setL(k, lst(k).filter((_, j) => j !== i));
  const emp = v.empresa || {};
  const ip = "h-7 rounded border border-slate-200 px-2 text-xs";
  const tit = "text-xs font-semibold text-[#0f5233] mb-1";
  const del = (k, i) => <button type="button" onClick={() => quitar(k, i)} className="text-slate-400 hover:text-red-600" aria-label="Quitar"><Trash2 className="h-3.5 w-3.5" /></button>;
  return (
    <div className="rounded-md border border-emerald-100 bg-emerald-50/40 p-3 space-y-4" data-testid="ac-ref-propios">
      <div>
        <div className="text-sm font-semibold text-[#0f5233]">Parámetros propios de tu empresa</div>
        <p className="text-[11px] text-slate-500">Opcional. Si no cargás nada, el análisis funciona igual que siempre. Se guardan con «Guardar referencias».</p>
      </div>
      <div>
        <div className={tit}>Moneda y plazos</div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
          <label className="flex items-center gap-2">Moneda en que cargás por defecto
            <select value={emp.moneda || ""} onChange={(e) => setV({ ...v, empresa: { ...emp, moneda: e.target.value } })} className={ip}>
              <option value="">Dólares (como siempre)</option><option value="USD">Dólares (US$)</option><option value="PYG">Guaraníes (Gs.)</option>
            </select>
          </label>
          <label className="flex items-center gap-2">Plazo habitual de venta
            <input type="number" min="0" max="720" value={emp.plazo_dias ?? ""} onChange={(e) => setV({ ...v, empresa: { ...emp, plazo_dias: e.target.value } })} className={ip + " w-20 text-right"} /> días
          </label>
          <span className="text-slate-400">El atraso que se tolera se ajusta en «Cómo se mide el comportamiento de pago».</span>
        </div>
      </div>
      <div>
        <div className={tit}>Tipos de cliente propios <span className="font-normal text-slate-500">(parten de un tipo existente y tienen sus propias pruebas)</span></div>
        {lst("tipos_extra").map((t, i) => (
          <div key={i} className="mb-2 rounded border border-slate-200 bg-white p-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
              <input value={t.nombre || ""} onChange={(e) => upd("tipos_extra", i, "nombre", e.target.value)} placeholder="Nombre (ej.: Cooperativa)" className={ip + " w-48"} />
              <span>se calcula como</span>
              <select value={t.base || ""} onChange={(e) => setL("tipos_extra", lst("tipos_extra").map((x, j) => (j === i ? { ...x, base: e.target.value, pruebas: JSON.parse(JSON.stringify(tipos[e.target.value]?.pruebas || [])) } : x)))} className={ip}>
                {Object.entries(tipos).map(([k, x]) => <option key={k} value={k}>{x.nombre}</option>)}
              </select>
              <input value={t.descripcion || ""} onChange={(e) => upd("tipos_extra", i, "descripcion", e.target.value)} placeholder="Qué clientes son y qué pesa más" className={ip + " flex-1 min-w-[180px]"} />
              {del("tipos_extra", i)}
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(t.pruebas || []).map((p, j) => (
                <label key={p.k} className="flex items-center gap-2 text-[11px] text-slate-600">
                  <span className="flex-1">{meta[p.k]?.[0]} ({OP[p.op]})</span>
                  <input type="number" step="any" value={p.v} className={ip + " w-20 text-right"}
                    onChange={(e) => upd("tipos_extra", i, "pruebas", t.pruebas.map((q, n) => (n === j ? { ...q, v: e.target.value === "" ? 0 : parseFloat(e.target.value) } : q)))} />
                </label>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setL("tipos_extra", [...lst("tipos_extra"), { nombre: "", base: "reventa", descripcion: "", pruebas: JSON.parse(JSON.stringify(tipos.reventa?.pruebas || [])) }])}
          className="text-xs text-[#0f5233] hover:underline">+ Tipo de cliente</button>
      </div>
      <div>
        <div className={tit}>Garantías propias <span className="font-normal text-slate-500">(aparecen en «Garantías que YA tenemos» y suman a la línea con su %)</span></div>
        {lst("garantias_extra").map((g, i) => (
          <div key={i} className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
            <input value={g.nombre || ""} onChange={(e) => upd("garantias_extra", i, "nombre", e.target.value)} placeholder="Nombre (ej.: Warrant)" className={ip + " w-40"} />
            <span>cuenta</span>
            <input type="number" min="0" max="100" value={g.factor ?? ""} onChange={(e) => upd("garantias_extra", i, "factor", e.target.value)} className={ip + " w-16 text-right"} />%
            <label className="flex items-center gap-1"><input type="checkbox" checked={!!g.real} onChange={(e) => upd("garantias_extra", i, "real", e.target.checked)} /> es garantía real (habilita préstamo en dinero)</label>
            <input value={g.explica || ""} onChange={(e) => upd("garantias_extra", i, "explica", e.target.value)} placeholder="Por qué cuenta ese % (sale en el informe)" className={ip + " flex-1 min-w-[180px]"} />
            {del("garantias_extra", i)}
          </div>
        ))}
        <button type="button" onClick={() => setL("garantias_extra", [...lst("garantias_extra"), { nombre: "", factor: 50, real: false, explica: "" }])} className="text-xs text-[#0f5233] hover:underline">+ Garantía</button>
      </div>
      <div>
        <div className={tit}>Datos extra del cliente <span className="font-normal text-slate-500">(se completan en cada análisis; los lee el analista IA y salen en el informe general)</span></div>
        {lst("campos_extra").map((c, i) => (
          <div key={i} className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
            <input value={c.nombre || ""} onChange={(e) => upd("campos_extra", i, "nombre", e.target.value)} placeholder="Nombre (ej.: Socios activos)" className={ip + " w-48"} />
            <select value={c.tipo || "texto"} onChange={(e) => upd("campos_extra", i, "tipo", e.target.value)} className={ip}>
              <option value="texto">Texto</option><option value="numero">Número</option><option value="si_no">Sí / No</option>
            </select>
            <input value={c.ayuda || ""} onChange={(e) => upd("campos_extra", i, "ayuda", e.target.value)} placeholder="Ayuda (opcional)" className={ip + " flex-1 min-w-[160px]"} />
            {del("campos_extra", i)}
          </div>
        ))}
        <button type="button" onClick={() => setL("campos_extra", [...lst("campos_extra"), { nombre: "", tipo: "texto", ayuda: "" }])} className="text-xs text-[#0f5233] hover:underline">+ Dato</button>
      </div>
    </div>
  );
}

function CamposEmpresa({ campos, val, setVal }) {
  const set = (k, v) => setVal({ ...val, [k]: v });
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs text-slate-700" data-testid="ac-campos-empresa">
      <div className="font-semibold text-[#0f5233]">Datos que pide tu empresa <span className="font-normal text-slate-500">(los lee el analista IA y salen en el informe general)</span></div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {campos.map((c) => (
          <label key={c.k} className="flex items-center gap-2" title={c.ayuda || ""}>
            <span className="flex-1">{c.nombre}</span>
            {c.tipo === "si_no" ? (
              <select value={val[c.k] === true ? "si" : val[c.k] === false ? "no" : ""} onChange={(e) => set(c.k, e.target.value === "" ? "" : e.target.value === "si")} className={inpA}>
                <option value="">—</option><option value="si">Sí</option><option value="no">No</option>
              </select>
            ) : (
              <input value={val[c.k] ?? ""} onChange={(e) => set(c.k, c.tipo === "numero" ? milesEs(e.target.value) : e.target.value)} inputMode={c.tipo === "numero" ? "decimal" : undefined}
                className={inpA + (c.tipo === "numero" ? " w-32 text-right" : " w-48")} />
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

function VigentesPanel({ items, setItems, pagares, setPagares, pres, subir, busy, propias = [] }) {
  const lista = items || [];
  const upd = (i, k, v) => setItems(lista.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const n = (i, k, x, ph, w = "w-28") => (
    <input value={milesEs(x[k] ?? "")} onChange={(e) => upd(i, k, milesEs(e.target.value))} inputMode="decimal" placeholder={ph} className={inpA + " text-right " + w} />
  );
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs text-slate-700" data-testid="ac-vigentes">
      <div className="font-semibold text-[#0f5233]">Garantías que YA tenemos de este cliente <span className="font-normal text-slate-500">(firmadas o inscriptas a nuestro favor: suman a la línea)</span></div>
      {setPagares && <PagaresListado pagares={pagares} setPagares={setPagares} pres={pres} subir={subir} busy={busy}
        hayManual={lista.some((x) => ["Pagaré con codeudoría", "Pagaré a sola firma"].includes(x.tipo))} />}
      {lista.map((x, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
          <select value={x.tipo || ""} onChange={(e) => upd(i, "tipo", e.target.value)} className={inpA}>
            {VIG_TIPOS.map((t) => <option key={t}>{t}</option>)}
            {propias.map((g) => <option key={g.nombre} value={g.nombre}>{g.nombre} (propia · {g.factor}%)</option>)}
          </select>
          {x.tipo === "Hipoteca" ? (
            <>
              <span>Valor del inmueble US$</span>{n(i, "inmueble", x, "Ej.: 800.000")}
              <span>Inscripta a nuestro favor por US$</span>{n(i, "valor", x, "Ej.: 300.000")}
              <span>Rango</span>
              <select value={x.rango || 1} onChange={(e) => upd(i, "rango", parseInt(e.target.value, 10))} className={inpA}>
                <option value={1}>1°</option><option value={2}>2°</option><option value={3}>3°</option>
              </select>
              {(x.rango || 1) > 1 && <><span>Ya debe a otro acreedor US$</span>{n(i, "gravamenes", x, "0")}</>}
            </>
          ) : POR_VTO.includes(x.tipo) && (x.vencimientos || []).length > 0 ? (
            <span>Total por vencimientos: <b>{usd(sumaVto(x.vencimientos))}</b></span>
          ) : (
            <><span>{x.tipo === "Pagarés de terceros" ? "Valor nominal US$" : "Valor US$"}</span>{n(i, "valor", x, "Ej.: 100.000")}</>
          )}
          <input value={x.detalle || ""} onChange={(e) => upd(i, "detalle", e.target.value)} placeholder="Detalle (finca, matrícula, de quién son los pagarés…)" className={inpA + " flex-1 min-w-[180px]"} />
          <button type="button" onClick={() => setItems(lista.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
          {POR_VTO.includes(x.tipo) && <ListaVtos items={x.vencimientos} onChange={(v) => upd(i, "vencimientos", v)} etiqueta={x.tipo === "Pagarés de terceros" ? "pagaré de tercero por vencimiento" : "pagaré por vencimiento"} />}
        </div>
      ))}
      <button type="button" onClick={() => setItems([...lista, { tipo: "Pagaré con codeudoría", valor: "", rango: 1 }])} className="text-[#0f5233] hover:underline">+ Agregar una garantía que ya tenemos</button>
    </div>
  );
}
const DEPARTAMENTOS = ["Alto Paraná", "Itapúa", "Canindeyú", "Caaguazú", "Amambay", "San Pedro", "Misiones", "Ñeembucú", "Concepción", "Caazapá", "Guairá", "Paraguarí", "Cordillera", "Central", "Campo 9 (E. Estigarribia)", "Chaco Central / Norte"];

/* Dónde trabaja el cliente (zona principal y otras): para un revendedor, el riesgo de sus clientes depende de la zona */
function ZonasActuacion({ zonas, setZonas }) {
  const z = zonas || { principal: {}, otras: [] };
  const pr = z.principal || {};
  const otras = z.otras || [];
  const setPr = (k, v) => setZonas({ ...z, principal: { ...pr, [k]: v } });
  const setOt = (i, k, v) => setZonas({ ...z, otras: otras.map((o, j) => (j === i ? { ...o, [k]: v } : o)) });
  const selDep = (val, on) => (
    <select value={val || ""} onChange={(e) => on(e.target.value)} className={inpA}>
      <option value="">Departamento…</option>{DEPARTAMENTOS.map((d) => <option key={d}>{d}</option>)}
    </select>
  );
  return (
    <div className="px-3 pb-2 text-xs text-slate-700 space-y-1.5" data-testid="ac-zonas">
      <div className="font-semibold">📍 Zonas donde trabaja <span className="font-normal text-slate-500">(si revende: de la zona depende cómo le pagan sus clientes)</span></div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-16 text-slate-500">Principal</span>
        {selDep(pr.departamento, (v) => setPr("departamento", v))}
        <input value={pr.localidad || ""} onChange={(e) => setPr("localidad", e.target.value)} placeholder="Localidad (ej.: Santa Rosa del Monday Norte)" className={inpA + " w-72"} />
      </div>
      {otras.map((o, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-slate-500">Otra</span>
          {selDep(o.departamento, (v) => setOt(i, "departamento", v))}
          <input value={o.localidad || ""} onChange={(e) => setOt(i, "localidad", e.target.value)} placeholder="Localidad" className={inpA + " w-56"} />
          <input value={o.pct || ""} onChange={(e) => setOt(i, "pct", e.target.value.replace(/[^\d]/g, ""))} placeholder="%" className={inpA + " w-14 text-right"} /> % de sus ventas
          <button type="button" onClick={() => setZonas({ ...z, otras: otras.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {otras.length < 8 && <button type="button" onClick={() => setZonas({ ...z, otras: [...otras, { departamento: "", localidad: "", pct: "" }] })} className="text-[#0f5233] hover:underline">+ Agregar otra zona donde vende</button>}
    </div>
  );
}
// color del veredicto del analista IA sobre la cabecera oscura
const VER_TONO = { "Aprobar": "#b5f23d", "Aprobar con condiciones": "#fde68a", "Reducir línea": "#fdba74", "No aprobar": "#fca5a5" };
const inpA = "h-8 rounded border border-slate-200 px-2 text-xs";

/* Lo que mira el analista y no está en el sistema: zafra, bancos/Informconf, opinión del vendedor */
const ZONAS = ["Alto Paraná", "Itapúa", "Canindeyú", "Caaguazú", "Amambay", "San Pedro", "Misiones", "Ñeembucú", "Campo 9 (E. Estigarribia)", "Chaco Central / Norte", "Otra"];
const PRESTAMO_VACIO = { evaluar: false, monto: "", plazo_meses: "", destino: "" };

function PrestamoPanel({ p, setP }) {
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="ac-prestamo">
      <label className="flex items-center gap-2 font-semibold text-[#0f5233]">
        <input type="checkbox" checked={p.evaluar} onChange={(e) => setP({ ...p, evaluar: e.target.checked })} data-testid="ac-prestamo-check" />
        💵 Evaluar también un préstamo en efectivo <span className="font-normal text-slate-500">(además del insumo que ya damos)</span>
      </label>
      {p.evaluar && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5">Monto pedido US$ <input value={p.monto} onChange={(e) => setP({ ...p, monto: milesEs(e.target.value) })} inputMode="decimal" placeholder="Ej.: 150.000" className={inpA + " w-28 text-right"} /></span>
          <span className="flex items-center gap-1.5">Plazo <input value={p.plazo_meses} onChange={(e) => setP({ ...p, plazo_meses: e.target.value.replace(/\D/g, "") })} inputMode="numeric" placeholder="12" className={inpA + " w-14 text-right"} /> meses</span>
          <input value={p.destino} onChange={(e) => setP({ ...p, destino: e.target.value })} placeholder="¿Para qué lo necesita? (ej.: flete, alquiler de tierra)" className={inpA + " flex-1 min-w-[220px]"} />
        </div>
      )}
    </div>
  );
}

function CuposCard({ cu }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700" data-testid="ac-cupos">
      <div className="font-semibold text-[#0f5233] mb-1">🌱 Cómo se usa la línea <span className="font-normal text-slate-500">— el dinero sale de la misma línea, no se suma</span></div>
      <div className="grid gap-2 md:grid-cols-2">
        <div className="rounded-md border border-[#0f5233] bg-emerald-50 p-2">
          <div className="text-[11px] text-slate-500">Cupo Insumos Aprobado <span className="text-slate-400">(semillas, fertilizantes, defensivos)</span></div>
          <div className="text-lg font-bold text-[#0f5233]" data-testid="ac-cupo-insumos">{usd(cu.insumos)}</div>
        </div>
        <div className={"rounded-md border p-2 " + (cu.habilitado ? "border-slate-200" : "border-amber-300 bg-amber-50")}>
          <div className="text-[11px] text-slate-500">Cupo Préstamo Dinero <span className="text-slate-400">(cosecha y flete)</span></div>
          <div className="text-lg font-bold text-[#0f5233]" data-testid="ac-cupo-efectivo">{usd(cu.efectivo)}</div>
          {cu.habilitado && cu.limita_txt && <div className="text-[10px] text-slate-500">lo limita {cu.limita_txt}</div>}
        </div>
      </div>
      {!cu.habilitado && (
        <p className="mt-1.5 text-[11px] text-amber-800"><b>Préstamo en efectivo no habilitado:</b> requiere contrato/prenda de granos o regularización de liquidación pendiente. {cu.bloqueos.map((b) => b[0].toUpperCase() + b.slice(1)).join(". ")}.</p>
      )}
      {cu.nota_liquidacion && <p className="mt-1 text-[10px] text-slate-500">{cu.nota_liquidacion}</p>}
    </div>
  );
}

/* Qué puede hacer el cliente para mejorar sus números (del analista IA si lo hay; si no, del sistema) */
/* Balance escrito a mano: una columna por año; los totales (activo, pasivo, patrimonio, EBITDA, neto) los calcula el sistema */
const FILA_HIST = { factura: "", fecha_factura: "", vto: "", valor: "", fecha_pago: "", saldo: "", obs: "VENTA NORMAL" };

function HistorialManual({ hist, cliente, codigo, monedaDef, busy, onUsar, onCerrar }) {
  const fmt = (v) => (v || v === 0 ? Math.round(v).toLocaleString("es-PY") : "");
  const [moneda, setMoneda] = useState(monedaDef || "USD");
  const [tc, setTc] = useState("");
  const [filas, setFilas] = useState(() => {
    const f = (hist?.filas || []).map((x) => ({ factura: x.factura || "", fecha_factura: x.fecha_factura || "", vto: x.vto || "", valor: fmt(x.valor),
      fecha_pago: x.fecha_pago || "", saldo: x.saldo ? fmt(x.saldo) : "", obs: x.obs || "VENTA NORMAL" }));
    return f.length ? f : [{ ...FILA_HIST }, { ...FILA_HIST }, { ...FILA_HIST }];
  });
  const set = (i, k, v) => setFilas((fs) => fs.map((f, j) => (j === i ? { ...f, [k]: k === "valor" || k === "saldo" ? milesEs(v) : v } : f)));
  const validas = filas.filter((f) => f.vto && f.valor && (f.fecha_pago || f.saldo));
  const inp = "h-8 w-full rounded border border-slate-200 px-2 text-xs";
  const usar = () => onUsar({ cliente: hist?.cliente || cliente || "", codigo: hist?.codigo || codigo || "", moneda, tc: moneda === "PYG" ? Number(String(tc).replace(/\D/g, "")) || null : null,
    filas: validas.map((f) => ({ ...f, saldo: f.saldo || (f.fecha_pago ? "0" : f.valor), monto: null })) });
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="ac-historial-manual">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <b className="text-[#0f5233]">Historial de compras a mano</b>
        <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className="h-8 rounded border border-slate-200 px-2 text-sm">
          <option value="USD">Dólares (US$)</option><option value="PYG">Guaraníes (Gs.)</option>
        </select>
        {moneda === "PYG" && <input value={tc} onChange={(e) => setTc(milesEs(e.target.value))} placeholder="Gs. por dólar" className="h-8 w-32 rounded border border-slate-200 px-2 text-right text-sm" aria-label="Tipo de cambio" />}
        <button type="button" onClick={onCerrar} className="ml-auto text-xs text-slate-500 underline">Cerrar</button>
      </div>
      <p className="text-[11px] text-slate-500">Una fila por factura. Si ya la pagó, poné la fecha de pago (y el saldo si pagó solo una parte). Si sigue abierta, dejá la fecha de pago vacía y poné lo que debe.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500">
            <th className="py-1 pr-1">Nro factura</th><th className="pr-1">Fecha factura</th><th className="pr-1">Vencimiento *</th><th className="pr-1">Importe *</th>
            <th className="pr-1">Fecha de pago</th><th className="pr-1">Saldo que debe</th><th className="pr-1">Tipo</th><th />
          </tr></thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1 pr-1"><input value={f.factura} onChange={(e) => set(i, "factura", e.target.value)} className={`${inp} min-w-[90px]`} aria-label="Nro factura" /></td>
                <td className="pr-1"><input type="date" value={f.fecha_factura} onChange={(e) => set(i, "fecha_factura", e.target.value)} className={inp} aria-label="Fecha factura" /></td>
                <td className="pr-1"><input type="date" value={f.vto} onChange={(e) => set(i, "vto", e.target.value)} className={inp} aria-label="Vencimiento" /></td>
                <td className="pr-1"><input value={f.valor} onChange={(e) => set(i, "valor", e.target.value)} inputMode="decimal" className={`${inp} min-w-[100px] text-right tabular-nums`} aria-label="Importe" /></td>
                <td className="pr-1"><input type="date" value={f.fecha_pago} onChange={(e) => set(i, "fecha_pago", e.target.value)} className={inp} aria-label="Fecha de pago" /></td>
                <td className="pr-1"><input value={f.saldo} onChange={(e) => set(i, "saldo", e.target.value)} inputMode="decimal" className={`${inp} min-w-[100px] text-right tabular-nums`} aria-label="Saldo" /></td>
                <td className="pr-1">
                  <select value={f.obs} onChange={(e) => set(i, "obs", e.target.value)} className={inp} aria-label="Tipo">
                    {["VENTA NORMAL", "INTERES", "REFINANCIACION"].map((o) => <option key={o} value={o}>{o.charAt(0) + o.slice(1).toLowerCase()}</option>)}
                  </select>
                </td>
                <td><button type="button" onClick={() => setFilas(filas.filter((_, j) => j !== i))} className="px-1 text-slate-400 hover:text-red-600" aria-label="Quitar fila">✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setFilas([...filas, { ...FILA_HIST }])} className="text-xs text-[#0f5233] underline">+ Factura</button>
        <span className="text-xs text-slate-500">{validas.length} facturas completas</span>
        <Button size="sm" disabled={busy || !validas.length || (moneda === "PYG" && !tc)} onClick={usar} className="ml-auto bg-[#0f5233] hover:bg-[#0c3f27]">Usar este historial</Button>
      </div>
    </div>
  );
}

function BalanceManual({ parsed, monedaDef, busy, onUsar, onCerrar }) {
  const [grupos, setGrupos] = useState([]);
  const anioHoy = new Date().getFullYear();
  const [cliente, setCliente] = useState(parsed?.cliente || "");
  const [moneda, setMoneda] = useState(parsed?.moneda_archivo ? (parsed.moneda_archivo === "PYG" ? "PYG" : "USD") : monedaDef || "USD");
  const [anios, setAnios] = useState((parsed?.anios || [anioHoy - 2, anioHoy - 1]).map(String));
  const [val, setVal] = useState(() => {
    const o = {};
    const n = (d, k) => (typeof d[k] === "number" ? d[k] : 0);
    Object.entries(parsed?.datos || {}).forEach(([a, d0]) => {
      // Las cuentas que el lector separa más fino (préstamos, previsiones, inversiones…) van a "Otros", así los totales no cambian.
      const d = { ...d0, ingresos_netos: d0.ingresos_netos ?? d0.ingresos };
      d.otros_ac = n(d, "ac") - n(d, "disp") - n(d, "cxc") - n(d, "inv");
      d.otros_anc = n(d, "anc") - n(d, "ppe");
      d.dd_cp = n(d, "pc") - n(d, "dc_cp") - n(d, "df_cp");
      d.dd_lp = n(d, "pnc") - n(d, "dc_lp") - n(d, "df_lp");
      o[a] = Object.fromEntries(Object.entries(d).filter(([, v]) => typeof v === "number").map(([k, v]) => [k, Math.round(v).toLocaleString("es-PY")]));
    });
    return o;
  });
  useEffect(() => { api.get("/analisis-cliente/balance-manual/campos").then((r) => setGrupos(r.data.grupos || [])).catch(() => {}); }, []);
  const set = (a, k, v) => setVal((x) => ({ ...x, [a]: { ...(x[a] || {}), [k]: milesEs(v) } }));
  const inp = "h-8 w-full min-w-[110px] rounded border border-slate-200 px-2 text-right text-xs tabular-nums";
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="ac-balance-manual">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <b className="text-[#0f5233]">Balance a mano</b>
        <input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Nombre del cliente" className="h-8 w-64 rounded border border-slate-200 px-2 text-sm" />
        <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className="h-8 rounded border border-slate-200 px-2 text-sm">
          <option value="USD">Dólares (US$)</option><option value="PYG">Guaraníes (Gs.)</option>
        </select>
        <button type="button" onClick={() => setAnios([...anios, String(Number(anios[anios.length - 1] || anioHoy - 1) + 1)].slice(-5))} className="text-xs text-[#0f5233] underline">+ Año</button>
        {anios.length > 1 && <button type="button" onClick={() => setAnios(anios.slice(1))} className="text-xs text-slate-500 underline">Quitar el más viejo</button>}
        <button type="button" onClick={onCerrar} className="ml-auto text-xs text-slate-500 underline">Cerrar</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr><th className="py-1 text-left text-slate-500">Cuenta</th>{anios.map((a, i) => (
            <th key={i} className="px-1"><input value={a} onChange={(e) => setAnios(anios.map((x, j) => (j === i ? e.target.value.replace(/\D/g, "").slice(0, 4) : x)))} className="h-7 w-20 rounded border border-slate-200 text-center font-semibold" aria-label="Año" /></th>
          ))}</tr></thead>
          <tbody>
            {grupos.map((g) => (
              <Fragment key={g.titulo}>
                <tr><td colSpan={anios.length + 1} className="pt-2 pb-1 font-semibold text-[#0f5233]">{g.titulo}</td></tr>
                {g.campos.map((c) => (
                  <tr key={c.k} className="border-t border-slate-100">
                    <td className="py-1 pr-2 text-slate-700">{c.nombre}</td>
                    {anios.map((a) => <td key={a} className="px-1"><input value={(val[a] || {})[c.k] ?? ""} onChange={(e) => set(a, c.k, e.target.value)} inputMode="decimal" className={inp} aria-label={`${c.nombre} ${a}`} /></td>)}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy || !grupos.length} className="bg-[#0f5233] hover:bg-[#0c3f27]"
          onClick={() => onUsar({ cliente, moneda, anios, valores: Object.fromEntries(anios.map((a) => [a, val[a] || {}])) })}>Usar estos números</Button>
        <span className="text-[11px] text-slate-500">Activo, pasivo, patrimonio, EBITDA y ganancia se calculan solos. Si sabés el patrimonio o la ganancia del balance, cargalos abajo para comparar.</span>
      </div>
    </div>
  );
}

/* Capacidad real vs. declarada: pruebas que el cliente no controla contra lo que declara; ajuste con tope y nota para auditoría */
const CAP_EST = {
  ajustado: ["Ajustado por capacidad real", "border-amber-400 bg-amber-50 text-amber-900"],
  cuadra: ["Las pruebas cuadran con lo declarado", "border-emerald-400 bg-emerald-50 text-emerald-900"],
  sin_pruebas: ["Faltan pruebas", "border-slate-300 bg-slate-50 text-slate-700"],
  sin_ventas: ["Sin ventas declaradas", "border-slate-300 bg-slate-50 text-slate-700"],
};
function CapacidadRealCard({ c }) {
  const [tit, col] = CAP_EST[c.estado] || CAP_EST.sin_pruebas;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2" data-testid="ac-capacidad">
      <div className="text-sm font-semibold text-[#0f5233]">🔎 Capacidad real vs. declarada</div>
      <div className={`rounded-md border-2 px-3 py-2 text-sm ${col}`}><b>{tit}.</b> {c.texto}</div>
      {c.fuentes?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500"><th className="py-1">Prueba</th><th className="text-right">Ventas que indica</th><th className="text-right">vs. declarado</th></tr></thead>
            <tbody>
              <tr className="border-t border-slate-100"><td className="py-1">Ventas declaradas en el balance</td><td className="text-right tabular-nums">{usd(c.declaradas)}</td><td className="text-right">—</td></tr>
              {c.fuentes.map((f) => (
                <tr key={f.fuente} className="border-t border-slate-100"><td className="py-1">{f.fuente}</td><td className="text-right tabular-nums">{usd(f.ventas)}</td>
                  <td className="text-right tabular-nums">{f.veces ? `${num(f.veces, 1)} veces` : "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {c.aviso && <p className="text-xs text-amber-800">{c.aviso}</p>}
      {c.garantia && <p className="text-sm text-slate-800"><b>{c.garantia}</b></p>}
      {c.estado === "ajustado" && (
        <div className="grid gap-1 text-xs text-slate-600 border-t border-slate-100 pt-2">
          <span>⚠ {c.riesgo_tributario}</span>
          <span>{c.aclaracion}</span>
          {c.nota_fuentes && <span>Fuentes: {c.nota_fuentes}</span>}
        </div>
      )}
    </div>
  );
}

/* Decisión final del directorio: si autoriza más de lo que recomienda el análisis, queda la constancia (quién, cuándo, acta,
   motivo y condiciones) y la leyenda en los informes. */
const DEC_VACIA = { on: false, monto: "", aprobado_por: "", fecha: "", acta: "", motivo: "", condiciones: "" };
function DecisionDirectorio({ d, cfg, busy, aplicar }) {
  const [f, setF] = useState({ ...DEC_VACIA, ...(cfg || {}) });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const inp = "h-9 rounded border border-slate-200 px-2.5 text-sm";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="ac-decision">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-semibold text-[#0f5233]">🏛️ Decisión final del directorio</div>
        <span className="text-xs text-slate-500">Si el directorio autoriza una línea mayor a la recomendada, dejá la constancia acá: sale en los tres informes.</span>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!f.on} onChange={(e) => set("on", e.target.checked)} data-testid="ac-decision-on" /> El directorio autorizó una línea distinta a la recomendada
      </label>
      {f.on && (
        <div className="grid gap-2 md:grid-cols-2 text-xs text-slate-700">
          <label className="flex flex-col gap-1">Línea autorizada (US$)
            <input value={milesEs(f.monto ?? "")} onChange={(e) => set("monto", milesEs(e.target.value))} inputMode="decimal" className={inp + " text-right"} data-testid="ac-decision-monto" />
          </label>
          <label className="flex flex-col gap-1">Aprobado por (nombres y cargos)
            <input value={f.aprobado_por} onChange={(e) => set("aprobado_por", e.target.value)} placeholder="Ej.: J. Pérez (Presidente), M. Gómez (Gerente Comercial)" className={inp} />
          </label>
          <div className="flex gap-2">
            <label className="flex flex-1 flex-col gap-1">Fecha<input type="date" value={f.fecha} onChange={(e) => set("fecha", e.target.value)} className={inp} /></label>
            <label className="flex flex-1 flex-col gap-1">Acta Nº<input value={f.acta} onChange={(e) => set("acta", e.target.value)} className={inp} /></label>
          </div>
          <label className="flex flex-col gap-1">Motivo comercial
            <input value={f.motivo} onChange={(e) => set("motivo", e.target.value)} placeholder="Ej.: cliente estratégico para crecer en la zona" className={inp} />
          </label>
          <label className="flex flex-col gap-1 md:col-span-2">Condiciones que fijó el directorio
            <input value={f.condiciones} onChange={(e) => set("condiciones", e.target.value)} placeholder="Ej.: entregas parciales, revisión antes de la zafra" className={inp} />
          </label>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => aplicar(f.on ? { ...f, monto: aNumEs(f.monto) || 0 } : { on: false })} data-testid="ac-decision-aplicar">
          {f.on ? "Registrar la decisión" : "Quitar la decisión"}
        </Button>
        <span className="text-[11px] text-slate-500">Guardá el análisis después: queda quién lo cargó y cuándo.</span>
      </div>
      {d && (
        <div className={`rounded-md border-2 px-3 py-2 text-sm ${d.excepcion ? "border-red-400 bg-red-50 text-red-900" : "border-emerald-400 bg-emerald-50 text-emerald-900"}`}>
          {d.excepcion && <div className="font-bold">{d.leyenda}</div>}
          <div>{d.detalle}</div>
        </div>
      )}
    </div>
  );
}

/* Participación en sus proveedores: qué parte de lo que debe a todos sus proveedores somos, cuánto conviene ser según su
   perfil (TOP, bueno, normal, riesgo) y si con este cliente hay que crecer o no. El % se puede fijar a mano para este cliente. */
const PART_V = {
  crecer: "border-emerald-500 bg-emerald-50 text-emerald-800", mantener: "border-sky-400 bg-sky-50 text-sky-800",
  no_crecer: "border-red-400 bg-red-50 text-red-800", sin_dato: "border-slate-300 bg-slate-50 text-slate-700",
};
const PERFIL_C = { top: "bg-emerald-600", bueno: "bg-sky-600", normal: "bg-slate-500", riesgo: "bg-red-600", condicionado: "bg-amber-600", nuevo: "bg-sky-600", nuevo_estrategico: "bg-indigo-600" };
function ParticipacionCard({ p, cfg, busy, aplicar }) {
  const [on, setOn] = useState(!!cfg?.on);
  const [pct, setPct] = useState(cfg?.pct ?? p.usado_pct ?? 25);
  const ancho = (v) => `${Math.min(Math.max(v || 0, 0), 100)}%`;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="ac-participacion">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-semibold text-[#0f5233]">🤝 ¿Cuánto participamos en sus proveedores?</div>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold text-white ${PERFIL_C[p.perfil] || "bg-slate-500"}`}>{p.perfil_txt}</span>
        {p.perfil_razones?.length > 0 && <span className="text-xs text-slate-500">({p.perfil_razones.join("; ")})</span>}
      </div>
      <div className={`rounded-md border-2 px-3 py-2 ${PART_V[p.veredicto] || PART_V.sin_dato}`}>
        <div className="text-base font-extrabold">{p.titulo}</div>
        <p className="text-sm">{p.texto}</p>
      </div>
      {p.aviso && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">⚠ {p.aviso}</div>}
      {p.disponible && (
        <>
          <div className="space-y-1">
            <div className="flex flex-wrap justify-between gap-2 text-xs text-slate-600">
              <span>Debe a todos sus proveedores: <b>{usd(p.proveedores)}</b></span>
              <span>Nosotros hoy: <b>{num(p.pct_total, 0)}%</b> · recomendado: <b>hasta {num(p.recomendado_pct, 0)}%</b> · usado en el cálculo: <b>{num(p.usado_pct, 0)}%</b>{p.manual ? " (fijado para este cliente)" : ""}</span>
            </div>
            <div className="relative h-3 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
              <div className={`absolute inset-y-0 left-0 rounded-full ${p.pct_total > p.recomendado_pct ? "bg-red-500" : "bg-[#0f5233]"}`} style={{ width: ancho(p.pct_total), transition: "width .8s cubic-bezier(.22,1,.36,1)" }} />
              <div className="absolute inset-y-0 w-0.5 bg-amber-500" style={{ left: ancho(p.recomendado_pct) }} title="Recomendado" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500"><th className="py-1">Si participamos con…</th><th className="text-right">Sería</th><th className="text-right">Línea posible</th><th className="pl-3">La limita</th></tr></thead>
              <tbody>
                {p.escenarios.map((e) => (
                  <tr key={e.pct} className={"border-t border-slate-100 " + (e.recomendado ? "bg-emerald-50" : "")}>
                    <td className="py-1"><b>{num(e.pct, 0)}%</b>{e.recomendado ? " · recomendado" : ""}{e.usado ? " · en uso" : ""}</td>
                    <td className="text-right tabular-nums">{usd(e.monto)}</td>
                    <td className="text-right tabular-nums font-semibold">{usd(e.linea)}</td>
                    <td className="pl-3 text-slate-500">{e.limita}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2 text-xs text-slate-700">
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} /> Fijar el % para este cliente</label>
        {on && <><input type="number" min="1" max="100" step="1" value={pct} onChange={(e) => setPct(e.target.value)} className="h-8 w-20 rounded border border-slate-200 px-2 text-right" aria-label="Porcentaje de participación" /> %</>}
        <Button size="sm" variant="outline" disabled={busy} className="h-8" onClick={() => aplicar(on ? { on: true, pct: Number(pct) || 0 } : { on: false })}>Recalcular</Button>
        <span className="text-slate-500">Sin tildar se usa el % general de Referencias.</span>
      </div>
    </div>
  );
}

/* Más garantía no es más línea: la garantía protege si algo sale mal; lo que paga la deuda es la caja del negocio. */
function GarantiaCapacidadCard({ g }) {
  const [ver, setVer] = useState(false);
  const pide = g.pedida && g.pedida > g.tope + 0.5;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="ac-garantia-capacidad">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-semibold text-amber-900">🛡️ {g.titulo}</div>
        {pide && <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-semibold text-red-800">El comercial pide {usd(g.pedida)} · {g.veces.toFixed(1).replace(".", ",")} veces el máximo</span>}
      </div>
      <p className="text-sm text-amber-950">{g.resumen}</p>
      <ul className="list-disc pl-4 space-y-1 text-sm text-slate-700">
        {(ver ? g.puntos : g.puntos.slice(0, 3)).map((x) => <li key={x}>{x}</li>)}
      </ul>
      {g.puntos.length > 3 && <button type="button" onClick={() => setVer(!ver)} className="text-xs font-semibold text-amber-800 underline">{ver ? "Ver menos" : `Ver ${g.puntos.length - 3} más`}</button>}
      {g.camino?.length > 0 && (
        <div className="border-t border-amber-200 pt-2">
          <div className="text-xs font-semibold text-amber-900 mb-1">Para que sus números den más línea</div>
          <ul className="space-y-0.5 text-sm text-slate-700">{g.camino.map((x) => <li key={x}>→ {x}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function MejorasCard({ sis, ia }) {
  const usaIa = ia.length > 0;
  const items = usaIa
    ? ia.map((x) => ({ titulo: x.accion, detalle: [x.meta && `Meta: ${x.meta}`, x.por_que].filter(Boolean).join(" · ") }))
    : sis.map((x) => ({ area: x.area, titulo: x.accion, detalle: [x.problema, x.meta && `Meta: ${x.meta}`, x.impacto].filter(Boolean).join(" · ") }));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ac-mejoras">
      <div className="text-sm font-semibold text-[#0f5233] mb-0.5">📈 Qué puede hacer el cliente para mejorar</div>
      <p className="text-[11px] text-slate-500 mb-2">Según sus números y el momento del agro{usaIa ? " (analista IA)" : ""}. Primero lo que más pesa para su crédito: sirve para conversarlo con el cliente.</p>
      <ol className="space-y-2">
        {items.map((x, i) => (
          <li key={i} className="flex gap-2.5 text-sm">
            <span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-[#0f5233] text-[11px] font-bold text-white">{i + 1}</span>
            <div>
              <div className="text-slate-800">{x.area && <b>{x.area}: </b>}{x.titulo}</div>
              {x.detalle && <div className="text-xs text-slate-500">{x.detalle}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PrestamoCard({ pr, pia }) {
  const col = pr.califica === "No" ? "border-red-400 bg-red-50 text-red-800" : pr.califica.startsWith("Sí") ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2" data-testid="ac-prestamo-res">
      <div className="text-xs font-semibold text-[#0f5233]">💵 Préstamo en efectivo {pr.monto_pedido ? `— pedido ${usd(pr.monto_pedido)}` : ""}{pr.plazo_meses ? ` a ${pr.plazo_meses} meses` : ""}</div>
      <div className="flex flex-wrap items-center gap-4">
        <span className={"rounded-md border px-3 py-1.5 text-lg font-extrabold " + col}>{pr.califica}</span>
        <div><div className="text-[11px] text-slate-500">Hasta</div><div className="text-xl font-extrabold text-[#0f5233]">{usd(pr.monto_max)}</div></div>
        <p className="flex-1 min-w-[240px] text-sm text-slate-700">{pr.garantia}</p>
      </div>
      <ul className="text-sm text-slate-700 list-disc pl-4 space-y-0.5">{pr.razones.map((x) => <li key={x}>{x}</li>)}</ul>
      {pr.opciones?.length > 0 && pr.califica !== "No" && (
        <div className="flex flex-wrap gap-2 text-xs">{pr.opciones.map((o) => <span key={o.tipo} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">{o.tipo}: bien de {usd(o.valor)}</span>)}</div>
      )}
      {pia && <div className="text-sm text-slate-700 border-t border-slate-100 pt-2"><b>Analista IA:</b> {pia.califica} · hasta {usd(pia.monto_max)}. {pia.garantia} {pia.por_que}</div>}
    </div>
  );
}

function DatosAnalista({ extra, setExtra, esAgro = true }) {
  const [abierto, setAbierto] = useState(true);
  const set = (g, k, v) => setExtra({ ...extra, [g]: { ...(extra[g] || {}), [k]: v } });
  const z = extra.zafra || {}, b = extra.bancos || {}, v = extra.vendedor || {}, bal = extra.balance || {}, cr = extra.capacidad || {};
  const numIn = (g, k, val, ph, w = "w-24") => (
    <input value={milesEs(val ?? "")} onChange={(e) => set(g, k, milesEs(e.target.value))} inputMode="decimal" placeholder={ph} className={inpA + " text-right " + w} />
  );
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50" data-testid="ac-datos-analista">
      <button type="button" onClick={() => setAbierto(!abierto)} aria-expanded={abierto} className="acm-plegable flex w-full min-h-[44px] items-center gap-1.5 px-3 py-2 text-left text-xs font-semibold text-[#0f5233]">
        <ChevronRight className="acm-chev h-4 w-4" />
        Lo que mira el analista <span className="font-normal text-slate-500">(opcional, pero la IA decide mucho mejor con esto)</span>
      </button>
      {abierto && (
        <div className="flex flex-wrap items-center gap-3 px-3 pb-2 text-xs text-slate-700" data-testid="ac-auditado">
          <span className="font-semibold">📑 ¿El balance está auditado?</span>
          {[[true, "Sí, auditado"], [false, "No auditado"]].map(([val, l]) => (
            <label key={l} className="flex items-center gap-1"><input type="radio" name="ac-aud" checked={bal.auditado === val} onChange={() => set("balance", "auditado", val)} /> {l}</label>
          ))}
          {bal.auditado === true && <input value={bal.auditor || ""} onChange={(e) => set("balance", "auditor", e.target.value)} placeholder="Auditor (firma)" className={inpA + " w-48"} />}
          <span className="text-slate-500">{bal.auditado === false ? "Un balance sin auditar puede estar «arreglado»: el sistema le da 75% de confianza y pesa más la opinión del vendedor." : bal.auditado == null ? "Marcalo: si no está auditado, el sistema da menos en confianza." : "Balance auditado: el sistema lo toma con más peso."}</span>
        </div>
      )}
      {abierto && <ZonasActuacion zonas={extra.zonas} setZonas={(zz) => setExtra({ ...extra, zonas: zz })} />}
      {abierto && (
        <div className="grid gap-4 px-3 pb-3 lg:grid-cols-3 text-xs text-slate-700">
          {esAgro && <div className="space-y-1.5" data-testid="ac-zafra">
            <div className="font-semibold">🌾 Zafra</div>
            <select value={z.cultivo || ""} onChange={(e) => set("zafra", "cultivo", e.target.value)} className={inpA}>
              <option value="">Cultivo…</option>{["Soja", "Maíz", "Trigo", "Soja y maíz", "Arroz", "Ganadería", "Otro"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="flex items-center gap-1.5">{numIn("zafra", "hectareas", z.hectareas, "1.000")} hectáreas</div>
            <div className="flex items-center gap-1.5">{numIn("zafra", "rendimiento", z.rendimiento, "3,2")} t/ha esperadas</div>
            <div className="flex items-center gap-1.5">{numIn("zafra", "precio", z.precio, "380")} US$/t esperado</div>
            <div className="flex items-center gap-1.5">{numIn("zafra", "comprometido", z.comprometido, "0", "w-16")} % ya comprometido (acopio, otros)</div>
            <div className="pt-1 font-semibold text-slate-600">Gestión del productor <span className="font-normal text-slate-400">(opcional)</span></div>
            <select value={z.zona || ""} onChange={(e) => set("zafra", "zona", e.target.value)} className={inpA}>
              <option value="">Zona (departamento)…</option>{ZONAS.map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="flex items-center gap-1.5">{numIn("zafra", "rinde_zona", z.rinde_zona, "auto", "w-20")} kg/ha promedio de la zona <span className="text-slate-400">(vacío = referencia MAG/INBIO)</span></div>
            <div className="flex items-center gap-1.5">{numIn("zafra", "cubierto", z.cubierto, "0", "w-16")} % vendido por adelantado o cubierto</div>
            <div className="flex items-center gap-1.5">US$ {numIn("zafra", "costo_ha", z.costo_ha, "550", "w-20")} costo por ha (insumos, labores, flete)</div>
            <div className="flex items-center gap-1.5">{numIn("zafra", "alquilado", z.alquilado, "0", "w-16")} % alquilado a US$ {numIn("zafra", "alquiler_ha", z.alquiler_ha, "350", "w-20")} /ha</div>
            <div className="pt-1 font-semibold text-slate-600">Hectáreas por cultivo en el año <span className="font-normal text-slate-400">(techo por hectárea y cupo de dinero)</span></div>
            <div className="grid grid-cols-2 gap-1.5">
              {[["soja", "Soja"], ["maiz", "Maíz"], ["trigo", "Trigo"], ["arroz", "Arroz"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-1.5">
                  <input value={milesEs((z.ha_cultivos || {})[k] ?? "")} inputMode="decimal" placeholder="0" className={inpA + " text-right w-20"}
                    onChange={(e) => set("zafra", "ha_cultivos", { ...(z.ha_cultivos || {}), [k]: milesEs(e.target.value) })} /> ha {l}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-slate-400">Soja y maíz zafriña en la misma tierra: cargá las dos. Vacío = se usan cultivo y hectáreas de arriba.</p>
          </div>}
          <div className="space-y-1.5">
            <div className="font-semibold">🏦 Bancos y otros (Informconf)</div>
            <div className="flex items-center gap-1.5">US$ {numIn("bancos", "deuda_bancos", b.deuda_bancos, "0", "w-28")} deuda con bancos</div>
            <div className="flex items-center gap-1.5">US$ {numIn("bancos", "deuda_otros", b.deuda_otros, "0", "w-28")} deuda con otros proveedores</div>
            <select value={b.informconf || ""} onChange={(e) => set("bancos", "informconf", e.target.value)} className={inpA}>
              <option value="">Informconf…</option>
              {["Sin registros negativos", "Atrasos menores", "Atrasos importantes", "Inhabilitado / morosidad"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!b.embargos} onChange={(e) => set("bancos", "embargos", e.target.checked)} /> Tiene embargos o inhibiciones</label>
            <input value={b.nota || ""} onChange={(e) => set("bancos", "nota", e.target.value)} placeholder="Nota (ej.: refinanció con banco X en 2025)" className={inpA + " w-full"} />
          </div>
          <div className="space-y-1.5">
            <div className="font-semibold">🤝 Vendedor</div>
            <div className="flex items-center gap-1.5">{numIn("vendedor", "antiguedad", v.antiguedad, "5", "w-16")} años como cliente</div>
            <select value={v.recomienda || ""} onChange={(e) => set("vendedor", "recomienda", e.target.value)} className={inpA}>
              <option value="">¿El vendedor lo recomienda?</option>
              {["Sí, lo recomienda", "Con cuidado", "No lo recomienda"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="flex items-center gap-1.5">US$ {numIn("vendedor", "linea_pedida", v.linea_pedida, "0", "w-28")} línea que pide el comercial</div>
            <textarea value={v.opinion || ""} onChange={(e) => set("vendedor", "opinion", e.target.value)} rows={3}
              placeholder="Opinión del vendedor: cómo trabaja, cómo está la chacra, si cumple, familia, otros negocios…" className="w-full rounded border border-slate-200 px-2 py-1 text-xs" />
          </div>
        </div>
      )}
      {abierto && (
        <div className="mx-3 mb-3 rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-700 space-y-2" data-testid="ac-capacidad-in">
          <label className="flex items-center gap-2 font-semibold">
            <input type="checkbox" checked={!!cr.on} onChange={(e) => set("capacidad", "on", e.target.checked)} /> 🔎 Capacidad real vs. declarada
            <span className="font-normal text-slate-500">(si sospechás que declara menos de lo que vende; hacen falta 2 pruebas o más)</span>
          </label>
          {cr.on && (
            <div className="grid gap-2 md:grid-cols-2">
              <div className="flex items-center gap-1.5">US$ {numIn("capacidad", "compras_insumos", cr.compras_insumos, "0", "w-28")} compras de insumos en el año, a todos sus proveedores</div>
              <div className="flex items-center gap-1.5">US$ {numIn("capacidad", "ingresos_banco", cr.ingresos_banco, "0", "w-28")} ingresos en extractos bancarios (12 meses)</div>
              <div className="flex items-center gap-1.5">US$ {numIn("capacidad", "entregas_acopio", cr.entregas_acopio, "0", "w-28")} entregas de granos a acopio o cooperativa</div>
              <input value={cr.nota || ""} onChange={(e) => set("capacidad", "nota", e.target.value)} placeholder="De dónde sale cada dato (ej.: extractos Banco X ene–dic 2025)" className={inpA + " w-full"} />
              <p className="md:col-span-2 text-[11px] text-slate-500">También cuentan la zafra cargada arriba (hectáreas × rinde × precio) y lo que nos compra. Se toma la prueba más baja y el ajuste tiene tope (Referencias).</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnalistaIA({ ia, busy, res, onPedir }) {
  const cab = (
    <div className="flex items-center gap-3">
      <span className="acm-ia-icono"><Bot className="h-5 w-5" /></span>
      <div className="leading-tight"><div className="font-semibold">Analista IA</div><div className="text-xs opacity-75">Creado e instruido por Daniel Paiva</div></div>
    </div>
  );
  if (!ia) {
    return (
      <div className="acm-card acm-ia" data-testid="ac-ia">
        <div className="acm-ia-cab items-center">
          {cab}
          <p className="acm-ia-res">{busy ? "Está revisando el balance, cómo nos paga, la zafra y las garantías…" : "Pedile su opinión: veredicto, línea recomendada, por qué, riesgos y condiciones, en un resumen ejecutivo."}</p>
          <button type="button" disabled={busy} onClick={onPedir} className="acm-ia-btn" style={{ background: "#b5f23d", color: "#0e2a1c", border: 0, fontWeight: 600 }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />} {busy ? "Analizando…" : "Pedir opinión"}
          </button>
        </div>
        {busy && <div className="acm-ia-cuerpo"><div className="acm-barra"><div className="acm-barra-llena acm-barra-indef" /></div></div>}
      </div>
    );
  }
  const Lista = ({ t, items, cls }) => (items && items.length ? (
    <div><div className={"text-[11px] font-bold uppercase tracking-wide " + cls}>{t}</div>
      <ul className="mt-1 space-y-1 text-sm text-slate-700 list-disc pl-4">{items.map((x) => <li key={x}>{x}</li>)}</ul></div>
  ) : null);
  return (
    <div className="acm-card acm-ia" data-testid="ac-ia">
      <div className="acm-ia-cab">
        <div className="flex w-full flex-wrap items-center gap-3">
          {cab}
          <span className="acm-ia-chip ml-auto">Resumen ejecutivo{ia.fecha ? ` · ${ia.fecha}` : ""}</span>
          <button type="button" disabled={busy} onClick={onPedir} className="acm-ia-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Volver a analizar</button>
        </div>
        <div className="relative z-[1]">
          <div className="text-[11px] uppercase tracking-wider opacity-70">Veredicto</div>
          <div className="acm-ia-ver" style={{ color: VER_TONO[ia.veredicto] || "#b5f23d" }}>{ia.veredicto}</div>
        </div>
        <div className="relative z-[1]">
          <div className="text-[11px] uppercase tracking-wider opacity-70">Línea recomendada</div>
          <div className="acm-ia-linea">{usd(ia.linea_recomendada)}</div>
          <div className="text-[11px] opacity-70">confianza {ia.confianza}</div>
        </div>
        <p className="acm-ia-res">{ia.resumen}</p>
        {ia.garantias?.length > 0 && (
          <div className="relative z-[1] flex w-full flex-wrap gap-2">{ia.garantias.map((g) => (
            <span key={g.tipo} className="acm-ia-chip" title={g.por_que}><b>{g.tipo}</b>{g.monto ? ` · ${usd(g.monto)}` : ""}</span>))}</div>
        )}
      </div>
      <div className="acm-ia-cuerpo">
        <div className="grid gap-4 sm:grid-cols-2">
          <Lista t="Por qué" items={ia.razones} cls="text-[#0f5233]" />
          <Lista t="Riesgos" items={ia.riesgos} cls="text-red-700" />
          <Lista t="Condiciones" items={ia.condiciones} cls="text-amber-700" />
          <Lista t="Antes de decidir, saber" items={ia.falta_saber} cls="text-slate-500" />
        </div>
        {(ia.causas || ia.refinanciacion) && (
          <div className="grid gap-3 sm:grid-cols-2 text-sm text-slate-700 border-t border-slate-100 pt-2">
            {ia.causas && <div><b>¿Mala gestión o factores externos?</b> <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold">{ia.causas.principal}</span> {ia.causas.explicacion}</div>}
            {ia.refinanciacion && <div><b>¿Va a necesitar refinanciar?</b> <span className={"rounded-full px-2 py-0.5 text-xs font-semibold " + (REF_COLOR[ia.refinanciacion.probabilidad] || "bg-slate-100")}>probabilidad {ia.refinanciacion.probabilidad}</span> {ia.refinanciacion.por_que}</div>}
          </div>
        )}
        <div className="text-xs text-slate-500">
          {ia.coincide_con_calculo ? "✔ Coincide con el cálculo automático" : `⚠ Difiere del cálculo automático (${res.ev.dictamen.toLowerCase()}): ${ia.diferencia || ""}`}
          {" · La decisión final es de ustedes; la IA puede equivocarse."}
        </div>
      </div>
    </div>
  );
}

function Cronograma({ cr }) {
  if (!cr || !cr.meses?.length) return null;
  const mt = (m) => (m === "sin fecha" ? "Sin fecha" : `${m.slice(5, 7)}/${m.slice(0, 4)}`);
  const ic = { bien: "✔", atencion: "!", mal: "✖" };
  const cc = { bien: "text-emerald-700", atencion: "text-amber-700", mal: "text-red-700" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2" data-testid="ac-cronograma">
      <div className="text-xs font-semibold text-[#0f5233]">📅 Vencimientos: lo que debe y los pagarés que lo respaldan</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 text-slate-500"><th className="px-2 py-1 text-left">Vence</th><th className="px-2 py-1 text-right">Deuda abierta</th><th className="px-2 py-1 text-right" title="Todavía no entregados: el pagaré se emite al facturar">Pedidos (sin facturar)</th><th className="px-2 py-1 text-right">Total</th><th className="px-2 py-1 text-right">Pagarés nuestros</th><th className="px-2 py-1 text-right" title="Solo de lo ya facturado">Falta pagaré (facturado)</th><th className="px-2 py-1 text-right">Pagarés de terceros que vencen</th></tr></thead>
          <tbody>
            {cr.meses.map((m) => (
              <tr key={m.mes} className="border-t border-slate-100">
                <td className="px-2 py-1">{mt(m.mes)}{m.vencido && m.total > 0 ? <span className="ml-1 text-red-600">(vencido)</span> : null}</td>
                <td className="px-2 py-1 text-right tabular-nums">{m.deuda ? usd(m.deuda) : "—"}</td>
                <td className="px-2 py-1 text-right tabular-nums">{m.pedidos ? usd(m.pedidos) : "—"}</td>
                <td className="px-2 py-1 text-right tabular-nums font-semibold">{m.total ? usd(m.total) : "—"}</td>
                <td className="px-2 py-1 text-right tabular-nums">{m.pagares ? usd(m.pagares) : "—"}</td>
                <td className={"px-2 py-1 text-right tabular-nums " + (m.falta_pagare > 0 ? "font-semibold text-red-700" : "text-emerald-700")}>{m.total ? (m.falta_pagare > 0 ? usd(m.falta_pagare) : m.deuda > 0 ? "cubierto" : "al facturar") : "—"}</td>
                <td className="px-2 py-1 text-right tabular-nums">{m.terceros ? usd(m.terceros) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(cr.pagares_sin_fecha > 0 || cr.terceros_sin_fecha > 0) && <p className="text-[11px] text-slate-500">Sin vencimiento cargado: pagarés {usd(cr.pagares_sin_fecha)} · terceros {usd(cr.terceros_sin_fecha)}. Cargalos por vencimiento para compararlos mes a mes.</p>}
      {cr.senales.map((x) => <div key={x.texto} className="text-sm text-slate-700"><b className={cc[x.nivel]}>{ic[x.nivel]}</b> {x.texto}</div>)}
    </div>
  );
}

const REF_COLOR = { alta: "bg-red-100 text-red-800", media: "bg-amber-100 text-amber-800", baja: "bg-emerald-100 text-emerald-800" };
const CAL_COLOR = { bueno: "bg-emerald-600", regular: "bg-amber-500", malo: "bg-red-600" };
const CULT = [["soja", "Soja"], ["maiz", "Maíz"], ["trigo", "Trigo"], ["arroz", "Arroz"]];

/* Cómo fue cada año para el sector, el dólar, si puede necesitar refinanciar y si el balance está auditado */
function ContextoSector({ ctx, refin, auditado, auditor, zonas }) {
  const anios = Object.keys(ctx || {}).sort();
  if (!anios.length && !refin && !zonas) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="ac-contexto-sector">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-[#0f5233]">🌎 El cliente en su contexto</span>
        {auditado != null && (
          <span className={"rounded-full px-2 py-0.5 text-[11px] font-bold " + (auditado ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>
            {auditado ? `Balance auditado${auditor ? ` (${auditor})` : ""}` : "Balance NO auditado: lo dado en confianza se reduce"}
          </span>
        )}
        {refin && <span className={"rounded-full px-2 py-0.5 text-[11px] font-bold " + (REF_COLOR[refin.probabilidad] || "")}>Probabilidad de refinanciar: {refin.probabilidad}</span>}
      </div>
      {refin?.razones?.length > 0 && <ul className="text-sm text-slate-700 list-disc pl-4 space-y-0.5">{refin.razones.map((x) => <li key={x}>{x}</li>)}</ul>}
      {zonas?.zonas?.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-700" data-testid="ac-zonas-res">
          <div className="font-semibold text-slate-600 mb-1">📍 Zonas donde trabaja</div>
          {zonas.zonas.map((z) => (
            <div key={z.departamento + z.localidad}>
              <b>{z.departamento || "Sin departamento"}</b>{z.localidad ? ` — ${z.localidad}` : ""}{z.principal ? " (principal)" : ""}{z.pct ? ` · ${z.pct}% de sus ventas` : ""}:{" "}
              <span className={"rounded px-1.5 font-semibold " + (z.produccion === "núcleo" || z.produccion === "buena" ? "bg-emerald-100 text-emerald-800" : z.produccion === "marginal" ? "bg-red-100 text-red-800" : "bg-slate-200 text-slate-700")}>zona {z.produccion}</span>{" "}
              {z.descripcion}{z.riesgo ? ` Riesgo principal: ${z.riesgo}.` : ""}
            </div>
          ))}
          <div className={"mt-1 " + (zonas.una_sola_zona ? "text-amber-700" : "text-slate-600")}>{zonas.resumen}</div>
        </div>
      )}
      {anios.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {anios.map((a) => {
            const c = ctx[a];
            return (
              <div key={a} className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-700 space-y-1">
                <div className="flex items-center gap-2"><b className="text-sm">{a}</b><span className={"rounded-full px-2 py-0.5 text-[10px] font-bold text-white " + (CAL_COLOR[c.calificacion] || "bg-slate-500")}>año {c.calificacion}</span></div>
                <p>{c.resumen}</p>
                {c.var_precios_pct && Object.keys(c.var_precios_pct).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">{CULT.filter(([k]) => c.var_precios_pct[k] != null).map(([k, l]) => (
                    <span key={k} className={"rounded px-1.5 py-0.5 font-semibold " + (c.var_precios_pct[k] >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}>{l} {c.var_precios_pct[k] > 0 ? "+" : ""}{c.var_precios_pct[k]}%</span>
                  ))}<span className="text-slate-400">precio vs. año anterior</span></div>
                )}
                {c.dolar?.texto && <p className="text-slate-600">💵 {c.dolar.texto}</p>}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-slate-400">Resumen del sector cargado en el sistema (no se lee de los diarios en línea; se actualiza cada año). Fuentes: CAPECO, USDA, Banco Mundial, ABC, La Nación, Última Hora, InfoNegocios. La opinión de la gente que está en el campo pesa más que este resumen.</p>
    </div>
  );
}

const PERFIL_COLOR = { "Puntual": "#15803d", "Paga con la cosecha": "#2f9e5b", "Afectado por el año": "#1d4ed8", "En observación": "#a16207", "Especulador": "#c2410c", "Mal pagador": "#b91c1c" };

function ComoNosPaga({ cp, zf }) {
  if (!cp) return null;
  const color = cp.nivel === "bueno" ? "#15803d" : cp.nivel === "regular" ? "#a16207" : "#b91c1c";
  const ic = { bien: "✔", atencion: "!", mal: "✖" };
  const cc = { bien: "text-emerald-700", atencion: "text-amber-700", mal: "text-red-700" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 grid gap-4 md:grid-cols-[150px_1fr]" data-testid="ac-comportamiento">
      <div className="text-center">
        <div className="text-[11px] font-semibold text-slate-500">Cómo nos paga</div>
        <div className="text-4xl font-extrabold" style={{ color: cp.sin_datos ? "#64748b" : color }}>{cp.sin_datos ? "—" : cp.puntaje}</div>
        <div className="text-xs font-semibold" style={{ color: cp.sin_datos ? "#64748b" : color }}>{cp.sin_datos ? "sin historial (nota neutra)" : `${cp.nivel} · de 100`}</div>
        <div className="mt-2 h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full" style={{ width: `${cp.puntaje}%`, background: color }} /></div>
        {cp.perfil && <div className="mt-2 rounded-full px-2 py-1 text-[11px] font-bold text-white" style={{ background: PERFIL_COLOR[cp.perfil] || "#64748b" }}>{cp.perfil}</div>}
        {cp.componentes && (
          <div className="mt-2 space-y-1 text-left">
            {[["palabra", "Cumple su palabra"], ["puntualidad", "Puntualidad"], ["causa", "Causa del atraso"], ["termina", "Termina pagando"]].map(([k, l]) => (
              <div key={k} className="text-[10px] text-slate-500">{l} <span className="float-right font-semibold text-slate-700">{cp.componentes[k]}</span>
                <div className="h-1 rounded bg-slate-100"><div className="h-1 rounded" style={{ width: `${cp.componentes[k]}%`, background: color }} /></div></div>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        {cp.senales.map((x) => <div key={x.texto} className="text-sm text-slate-700"><b className={cc[x.nivel]}>{ic[x.nivel]}</b> {x.texto}</div>)}
        {cp.visitas?.length > 0 && <div className="text-xs text-slate-500 pt-1">Última visita ({cp.visitas[0].fecha}): {cp.visitas[0].conclusiones}</div>}
        {cp.historial?.por_anio?.length > 0 && (
          <div className="pt-1" data-testid="ac-hist-anios">
            <div className="text-[11px] font-semibold text-slate-500">📒 Historial de pagos por año (vencimiento) · {cp.historial.patron}</div>
            <table className="mt-0.5 text-xs text-slate-700">
              <thead><tr className="text-slate-400"><th className="pr-3 text-left font-medium">Año</th><th className="pr-3 text-right font-medium">Comprado</th><th className="pr-3 text-right font-medium">Facturas</th><th className="pr-3 text-right font-medium">Atraso prom.</th><th className="pr-3 text-right font-medium">Máximo</th><th className="text-right font-medium">Interés cobrado</th></tr></thead>
              <tbody>{cp.historial.por_anio.map((x) => (
                <tr key={x.anio}><td className="pr-3">{x.anio}</td><td className="pr-3 text-right">{usd(x.comprado)}</td><td className="pr-3 text-right">{x.facturas}</td>
                  <td className={"pr-3 text-right font-semibold " + (x.dias_prom <= 30 ? "text-emerald-700" : x.dias_prom <= 90 ? "text-amber-700" : "text-red-700")}>{x.dias_prom} días</td>
                  <td className="pr-3 text-right">{x.dias_max} días</td><td className="text-right">{x.interes ? usd(x.interes) : "—"}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {zf?.cargada && (
          <div className="text-xs text-slate-600 pt-1">🌾 Zafra: {num(zf.hectareas, 0)} ha × {num(zf.rendimiento, 1)} t/ha × {usd(zf.precio)}/t = <b>{usd(zf.ingreso_esperado)}</b> (libre {usd(zf.ingreso_libre)}){zf.deuda_sobre_zafra_pct != null ? ` · su deuda total equivale al ${num(zf.deuda_sobre_zafra_pct, 0)}% de la zafra libre` : ""}</div>
        )}
        {zf?.gestion?.clasificacion && (
          <div className="text-xs text-slate-600" data-testid="ac-gestion">
            👨‍🌾 <b>{zf.gestion.clasificacion}</b>
            {zf.gestion.vs_zona_pct != null && <span title={zf.gestion.rinde_fuente || ""}> · rinde {zf.gestion.vs_zona_pct > 0 ? "+" : ""}{num(zf.gestion.vs_zona_pct, 1)}% contra su zona ({num(zf.gestion.rinde_zona_kg, 0)} kg/ha)</span>}
            {zf.gestion.cubierto_pct != null && <> · {num(zf.gestion.cubierto_pct, 0)}% vendido por adelantado</>}
            {zf.gestion.rinde_indiferencia != null && <> · necesita {num(zf.gestion.rinde_indiferencia, 2)} t/ha para no perder (colchón {num(zf.gestion.colchon_pct, 0)}%)</>}
          </div>
        )}
      </div>
    </div>
  );
}

const SUGERIDAS = ["¿Le damos US$ 500.000? ¿Con qué garantía?", "¿Qué pasa si el precio de la soja baja 20%?", "¿Cuánto le podemos vender sin garantía real?", "Resumime el caso en 3 líneas para el directorio"];
function ChatIA({ chat, busy, onAsk, disabled }) {
  const [q, setQ] = useState("");
  const enviar = (t) => { onAsk(t ?? q); setQ(""); };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="ac-chat">
      <div className="text-xs font-semibold text-[#0f5233] flex items-center gap-1.5"><Bot className="h-4 w-4" /> Preguntale al analista</div>
      {chat.length === 0 && (
        <div className="flex flex-wrap gap-2">{SUGERIDAS.map((s) => <button key={s} disabled={disabled || busy} onClick={() => enviar(s)} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700 hover:bg-emerald-50">{s}</button>)}</div>
      )}
      {chat.length > 0 && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {chat.map((m, i) => (
            <div key={i} className={"max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-line " + (m.rol === "user" ? "ml-auto bg-emerald-50 text-slate-800" : "bg-slate-50 text-slate-800")}>{m.texto}</div>
          ))}
          {busy && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> pensando…</div>}
        </div>
      )}
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") enviar(); }} disabled={disabled || busy}
          placeholder="Ej.: ¿y si le damos la línea solo con pagaré con codeudoría?" className="flex-1 h-9 rounded border border-slate-200 px-3 text-sm" data-testid="ac-chat-input" />
        <Button size="sm" disabled={disabled || busy || !q.trim()} onClick={() => enviar()} className="bg-[#0f5233] hover:bg-[#0c3f27] gap-1.5"><Send className="h-4 w-4" /> Preguntar</Button>
      </div>
    </div>
  );
}

/* Reglas propias de la empresa que la IA respeta siempre */
function CriterioEmpresa({ externo = false }) {
  const [abierto, setAbierto] = useState(false);
  const [txt, setTxt] = useState("");
  const [cargado, setCargado] = useState(false);
  const abrir = async () => {
    setAbierto(!abierto);
    if (!cargado) {
      try { setTxt((await api.get("/analisis-cliente/criterio")).data.criterio || ""); setCargado(true); } catch { /* sin acceso */ }
    }
  };
  const guardar = async () => {
    try { await api.put("/analisis-cliente/criterio", { criterio: txt }); toast.success("Criterio guardado: la IA lo usará desde el próximo análisis"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="ac-criterio">
      <button onClick={abrir} aria-expanded={abierto} className="acm-plegable w-full min-h-[52px] px-4 py-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <ChevronRight className="acm-chev h-4 w-4 text-[#0f5233]" /> Criterio de crédito {externo ? "de la empresa" : "de Tecnomyl"} <span className="text-[11px] font-normal text-slate-500">(la IA lo respeta en cada análisis)</span>
      </button>
      {abierto && (
        <div className="px-4 pb-4 space-y-2">
          <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={7} maxLength={3000} className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
            placeholder={"Escriban sus reglas como se las explicarían a un analista nuevo. Ej.:\n• No financiamos más del 30% de la zafra libre esperada.\n• Cliente con juicio abierto con nosotros: solo contado.\n• Más de 60 días de atraso promedio: no se amplía la línea.\n• Productores pequeños: siempre pagaré con codeudoría.\n• Línea mayor a US$ 1 millón: requiere garantía real (granos, prenda o hipoteca)."} />
          <div className="flex items-center gap-2"><Button size="sm" onClick={guardar} className="bg-[#0f5233] hover:bg-[#0c3f27]">Guardar criterio</Button><span className="text-[11px] text-slate-400">{txt.length}/3000</span></div>
        </div>
      )}
    </div>
  );
}

/* Cómo redacta la IA: frases propias de la casa que debe imitar (la guía fija ya la trae el sistema) */
function EstiloRedaccion() {
  const [abierto, setAbierto] = useState(false);
  const [txt, setTxt] = useState("");
  const [guia, setGuia] = useState("");
  const [cargado, setCargado] = useState(false);
  const abrir = async () => {
    setAbierto(!abierto);
    if (!cargado) {
      try { const d = (await api.get("/analisis-cliente/estilo")).data; setTxt(d.estilo || ""); setGuia(d.guia_fija || ""); setCargado(true); } catch { /* sin acceso */ }
    }
  };
  const guardar = async () => {
    try { await api.put("/analisis-cliente/estilo", { estilo: txt }); toast.success("Estilo guardado: la IA lo imita desde el próximo dictamen"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="ac-estilo">
      <button onClick={abrir} aria-expanded={abierto} className="acm-plegable w-full min-h-[52px] px-4 py-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="ac-estilo-toggle">
        <ChevronRight className="acm-chev h-4 w-4 text-[#0f5233]" /> Cómo redacta la IA <span className="text-[11px] font-normal text-slate-500">(que suene a un analista de la casa, no a una máquina)</span>
      </button>
      {abierto && (
        <div className="px-4 pb-4 space-y-2">
          <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={7} maxLength={3000} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="ac-estilo-textarea"
            placeholder={"Pegá frases reales de sus dictámenes para que la IA las imite. Ej.:\n• Paga a la cosecha, pero paga. Nunca nos dejó un pagaré protestado.\n• Debe mucho a los bancos para lo que genera: 10 años de EBITDA. Eso no lo arregla una hipoteca.\n• Le daría 150 mil con codeudoría y entregas contra pagaré; más que eso, no.\n• Ojo con el stock: casi todo el activo corriente es sementera."} />
          <div className="flex items-center gap-2"><Button size="sm" onClick={guardar} className="bg-[#0f5233] hover:bg-[#0c3f27]" data-testid="ac-estilo-guardar">Guardar estilo</Button><span className="text-[11px] text-slate-400">{txt.length}/3000</span></div>
          {guia && <details className="text-[11px] text-slate-500"><summary className="cursor-pointer">Ver la guía fija que ya aplica el sistema</summary><p className="mt-1 whitespace-pre-wrap">{guia}</p></details>}
        </div>
      )}
    </div>
  );
}

/* Scoring de pago vs. zafra: métricas del extracto (deterministas) + resumen y política redactados por la IA */
const SZ_UI = {
  verde: "tm-tile-verde border-transparent", amarillo: "tm-tile-amarillo border-transparent",
  rojo: "tm-tile-rojo border-transparent", gris: "bg-[#FFFFFF] border-[#D9E3D6] text-[#24331F]",
};
const usd0 = (v) => `US$ ${Math.round(v || 0).toLocaleString("es-PY")}`;
function ScoringZafraCard({ res, payload, setExtra }) {
  const [out, setOut] = useState(res.scoring_zafra?.ia ? { ...res.scoring_zafra.ia } : null);
  const [busy, setBusy] = useState(false);
  const [verJson, setVerJson] = useState(false);
  const m = res.scoring_zafra?.metricas;
  const generar = useCallback(async () => {
    setBusy(true);
    try {
      const d = (await api.post("/analisis-cliente/scoring-zafra", payload(), { timeout: 120000 })).data;
      setOut(d);
      setExtra((x) => ({ ...x, scoring_zafra_ia: d }));   // viaja en el payload: el PDF usa el mismo texto
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar el scoring"); }
    finally { setBusy(false); }
  }, [payload, setExtra]);
  useEffect(() => { if (m && !out && !busy) generar(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.categoria]);
  if (!m) return null;
  const color = { "MOROSO TÉCNICO RENTABLE": "verde", "CUMPLIDOR PUNTUAL": "verde", "PAGADOR A COSECHA": "amarillo", "RIESGO DE INCOBRABILIDAD": "rojo", "HISTORIAL CORTO": "gris" }[m.categoria] || "gris";
  const dg = out?.diagnostico_crediticio; const pol = out?.politica_comercial_sugerida;
  const met = [["Tasa de recupero", `${m.tasa_recupero_pct}%`], ["Atraso ponderado (DPD)", `${m.dpd_ponderado} días`], ["Atraso en ventana de zafra", `${m.dpd_zafra} días`],
    ["Atrasos que caen en cosecha", m.pct_atraso_en_zafra == null ? "—" : `${m.pct_atraso_en_zafra}%`], ["Intereses moratorios cobrados", usd0(m.intereses_cobrados_usd)],
    ["Cobertura con pagarés", `${m.cobertura_documental_pct}%`], ["Antigüedad", `${m.antiguedad_anios} años`], ["Facturas analizadas", m.facturas]];
  return (
    <section className={`rounded-xl border p-4 ${SZ_UI[color]}`} data-testid="ac-zafra" data-categoria={m.categoria}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Comportamiento de pago vs. zafra</div>
          <h3 className="text-base font-semibold" data-testid="ac-zafra-categoria">{m.categoria}</h3>
          <p className="text-xs opacity-80">{m.categoria_detalle}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-full bg-white/80 px-2.5 py-1 font-semibold" data-testid="ac-zafra-riesgo-capital">Riesgo capital: {m.riesgo_capital}</span>
          <span className="rounded-full bg-white/80 px-2.5 py-1 font-semibold" data-testid="ac-zafra-riesgo-liquidez">Riesgo liquidez: {m.riesgo_liquidez}</span>
          <span className="rounded-full bg-white/80 px-2.5 py-1 font-semibold">Rentabilidad: {m.rentabilidad_global}</span>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4" data-testid="ac-zafra-metricas">
        {met.map(([k, v]) => <div key={k} className="rounded-md bg-white/70 px-2.5 py-1.5"><dt className="opacity-70">{k}</dt><dd className="font-semibold">{v}</dd></div>)}
      </dl>
      {busy && !out && <p className="mt-3 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> El analista está redactando el diagnóstico…</p>}
      {out && (
        <div className="mt-3 space-y-2 text-sm">
          <p data-testid="ac-zafra-resumen">{out.analisis_financiero_resumen}</p>
          {pol && (
            <div className="rounded-md bg-white/70 p-3" data-testid="ac-zafra-politica">
              <div className="font-semibold">Política comercial sugerida · {pol.habilitar_ventas ? "habilitar ventas" : "NO habilitar ventas"}{pol.limite_credito_sugerido_usd ? ` · límite ${usd0(pol.limite_credito_sugerido_usd)}` : ""}</div>
              {pol.condiciones_garantia?.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs">{pol.condiciones_garantia.map((c, i) => <li key={i}>{c}</li>)}</ul>}
              {pol.pauta_liberacion_insumos && <p className="mt-1 text-xs"><b>Liberación de insumos:</b> {pol.pauta_liberacion_insumos}</p>}
              {pol.accion_fechas_vencimiento && <p className="mt-1 text-xs"><b>Vencimientos:</b> {pol.accion_fechas_vencimiento}</p>}
            </div>
          )}
          {dg && <div className="text-[11px] opacity-70">Riesgo capital {dg.nivel_riesgo_capital} · liquidez {dg.nivel_riesgo_liquidez} · {dg.mora_tecnica ? "mora técnica (no incobrabilidad)" : "sin mora técnica"}</div>}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={generar} disabled={busy} className="bg-white/80" data-testid="ac-zafra-regenerar">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />}{out ? "Volver a redactar" : "Generar diagnóstico"}
        </Button>
        {out && <button type="button" className="text-[11px] underline opacity-70" onClick={() => setVerJson(!verJson)} data-testid="ac-zafra-json-toggle">{verJson ? "Ocultar JSON" : "Ver JSON"}</button>}
        {out && <button type="button" className="text-[11px] underline opacity-70" onClick={() => { navigator.clipboard.writeText(JSON.stringify(out, null, 2)); toast.success("JSON copiado"); }}>Copiar JSON</button>}
      </div>
      {verJson && out && <pre className="mt-2 max-h-72 overflow-auto rounded-[18px] tm-negra p-3 text-[11px]" data-testid="ac-zafra-json">{JSON.stringify(out, null, 2)}</pre>}
    </section>
  );
}

/* Ubicación en Catastro (SNC): pegás un link de Google Maps y trae padrón, finca, matrícula y hectáreas */
function UbicacionSNC({ onUsar }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const consultar = async () => {
    if (!txt.trim()) return;
    setBusy(true); setR(null);
    try {
      const d = (await api.post("/v1/inmuebles/procesar-ubicacion", { texto: txt }, { timeout: 60000 })).data;
      setR(d);
      if (d.estado !== "ok") toast.warning(d.mensaje);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo consultar Catastro"); }
    finally { setBusy(false); }
  };
  const c = r?.catastro;
  const campos = c ? [["Departamento", c.departamento ? `${c.departamento_letra} · ${c.departamento}` : "—"], ["Distrito", c.distrito ? `${c.distrito} (${c.codigo_distrito || ""})` : "—"],
    ["Padrón", c.padron || "—"], ["Cta. corriente", c.cuenta_corriente || "—"], ["Finca", c.finca || "—"], ["Matrícula", c.matricula || "—"],
    ["Superficie", c.superficie_hectareas ? `${c.superficie_hectareas} ha` : "sin dato"], ["Valor fiscal", c.valor_fiscal ? `Gs. ${Math.round(c.valor_fiscal).toLocaleString("es-PY")}` : "sin dato"]] : [];
  return (
    <div className="mt-3 rounded-md border border-current/20 bg-white/70 p-3" data-testid="ac-snc">
      <div className="flex flex-wrap items-center gap-2">
        <MapPin className="h-4 w-4 opacity-70" />
        <span className="text-xs font-semibold">Ubicar en Catastro (SNC)</span>
        <input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Pegá el link de Google Maps o «-25.4994259, -54.6778510»" className="h-8 min-w-[260px] flex-1 rounded border border-slate-300 bg-white px-2 text-xs text-slate-800" data-testid="ac-snc-input" onKeyDown={(e) => e.key === "Enter" && consultar()} />
        <Button size="sm" variant="outline" onClick={consultar} disabled={busy || !txt.trim()} className="bg-white" data-testid="ac-snc-buscar">{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <MapPin className="mr-1 h-3.5 w-3.5" />}Buscar padrón / finca</Button>
      </div>
      {r && (
        <div className="mt-2 text-xs" data-testid="ac-snc-resultado" data-estado={r.estado}>
          <p className={`mb-1 ${r.estado === "ok" ? "opacity-80" : "font-semibold text-amber-800"}`}>{r.mensaje}</p>
          {c && <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 sm:grid-cols-4">{campos.map(([k, v]) => <div key={k}><dt className="opacity-60">{k}</dt><dd className="font-semibold">{v}</dd></div>)}</dl>}
          {c && (c.finca || c.padron || c.cuenta_corriente) && (
            <button type="button" className="mt-2 text-[11px] underline" data-testid="ac-snc-usar"
              onClick={() => onUsar(`Finca ${c.finca || "s/d"} · Pad ${c.padron || c.cuenta_corriente || "s/d"} · ${c.distrito || ""}${c.superficie_hectareas ? ` · ${c.superficie_hectareas} ha` : ""} (SNC)`)}>
              Agregar como garantía vigente (hipoteca a verificar)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* Garantías reales: cobertura ejecutable después de los rangos preferentes, por escritura */
const SELLO_UI = { COBERTURA_TOTAL: ["CUBRE", "text-[#007336]"], COBERTURA_PARCIAL: ["PARCIAL", "text-[#8B5E3C]"], SUBORDINADA_FICTICIA: ["SUBORDINADA", "text-[#B8402A]"], SIN_TASACION: ["SIN TASACIÓN", "text-[#B8402A]"] };
const GR_UI = { COBERTURA_TOTAL: ["verde", "Cubre lo inscripto"], COBERTURA_PARCIAL: ["amarillo", "Cobertura parcial"], SUBORDINADA_FICTICIA: ["rojo", "Subordinada: sin cobertura de liquidación"], SIN_TASACION: ["amarillo", "Sin tasación — no se puede medir cobertura"] };

/* Inmuebles consolidados (informes + escrituras + carga manual) tal como salen en el PDF */
function InmueblesResumenCard({ d, onFijar, onGuardar, manuales = [], tc, busy }) {
  const n0 = (v) => Math.round(v || 0).toLocaleString("es-PY");
  const [modal, setModal] = useState(null);
  const [sat, setSat] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [constr, setConstr] = useState({});
  const mk = (m) => `${m.finca_matricula || ""}|${m.padron_catastro || ""}`;
  const conConstr = (i, k) => { const m = manuales.find((x) => mk(x) === mk(i)) || {}; return { ...i, construccion_m2: m.construccion_m2 || "", nivel: m.nivel || "", antiguedad: m.antiguedad || "", ...(constr[k] || {}) }; };
  const buscarRef = async (ks) => {
    if (!ks.length) return;
    setBuscando(true);
    const its = ks.map((k) => conConstr(d.items[k], k));
    try {
      const r = await api.post("/analisis-cliente/inmuebles/valor-referencia", { inmuebles: its, tc }, { timeout: 300000 });
      setModal({ lista: ks.map((k, j) => ({ idx: k, it: its[j], r: r.data.resultados[j] })) });
    } catch (e) { setModal({ lista: ks.map((k, j) => ({ idx: k, it: its[j], r: null })) }); }
    setBuscando(false);
  };
  const aplicar = () => { onGuardar(modal.lista.filter((x) => x.r).map((x) => ({ i: d.items[x.idx], it: aplicarRef({ construccion_m2: x.it.construccion_m2, nivel: x.it.nivel, antiguedad: x.it.antiguedad }, x.r) }))); setModal(null); };
  const verSat = async (k) => {
    setBuscando(true);
    try { const r = await api.post("/analisis-cliente/inmuebles/satelite", { inmuebles: [d.items[k]] }, { timeout: 150000 }); setSat({ idx: k, r: r.data.resultados[0] }); }
    catch (e) { setSat({ idx: k, r: { aviso: "No se pudo obtener la imagen satelital: " + (e?.response?.data?.detail || e?.message || "error de red") } }); }
    setBuscando(false);
  };
  const setC = (k, f, v) => setConstr((c) => ({ ...c, [k]: { ...(c[k] || {}), [f]: v } }));
  const sinTas = d.items.map((i, k) => (i.valor_origen !== "tasacion" && !(i.valor_usd > 0 && !i.valor_origen) ? k : -1)).filter((k) => k >= 0);
  const inp = "h-6 rounded border border-slate-300 bg-white px-1 text-[10px] text-slate-800";
  const met = [["Inmuebles", String(d.total_inmuebles)], ["Superficie", `${(d.total_ha || 0).toLocaleString("es-PY", { maximumFractionDigits: 1 })} ha`], ["Valor total", usd0(d.valor_total_usd) + (d.valor_referencia_usd ? ` (tasación ${usd0(d.valor_tasacion_usd)} · referencia ${usd0(d.valor_referencia_usd)})` : "")],
    ["Promedio US$/ha", d.usd_ha_promedio ? n0(d.usd_ha_promedio) : "—"], ["Respaldo libre", usd0(d.valor_libre_total_usd) + (d.valor_referencia_usd ? " (solo tasaciones)" : "")]];
  return (
    <section className="rounded-xl border border-emerald-900/15 bg-white p-4" data-testid="ac-inmuebles-resultado">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><MapPin className="h-4 w-4 text-[#0f5233]" />Inmuebles, catastro y ubicación geográfica · {d.verificados_snc || 0} verificado(s) en SNC</div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-5">{met.map(([k, v]) => <div key={k} className="rounded-md bg-slate-50 px-2.5 py-1.5"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-slate-800">{v}</dd></div>)}</dl>
      {d.alertas_tasacion?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs font-medium text-red-700" data-testid="ac-inmuebles-alertas">{d.alertas_tasacion.map((a, i) => <li key={i}>{a}</li>)}</ul>}
      {modal && <ReferenciaModal lista={modal.lista} onAplicar={aplicar} onCerrar={() => setModal(null)} />}
      {sat && <SateliteModal it={d.items[sat.idx]} r={sat.r} onUsar={(m2) => { setC(sat.idx, "construccion_m2", String(Math.round(m2))); setSat(null); }} onCerrar={() => setSat(null)} />}
      {onGuardar && <div className="mt-2 flex justify-end"><Button size="sm" variant="outline" className="h-7 bg-white text-[11px]" disabled={buscando || busy || !sinTas.length} onClick={() => buscarRef(sinTas)} data-testid="ac-inm-res-ref-todos">{buscando ? "Buscando…" : `Aplicar valor de referencia a todos los sin tasación (${sinTas.length})`}</Button></div>}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="ac-inmuebles-resultado-tabla">
          <thead><tr className="text-left text-slate-500"><th className="py-1 pr-2">Titular</th><th className="pr-2">Finca / Padrón</th><th className="pr-2">Distrito / Dpto. · cultivo</th><th className="pr-2 text-right">Sup. (ha)</th><th className="pr-2 text-right">Tasación · US$/ha</th><th className="pr-2">Gravamen (libre)</th><th>Ubicación</th></tr></thead>
          <tbody>
            {d.items.map((i, k) => (
              <tr key={k} className={`border-t border-slate-200 align-top ${i.alerta_tasacion === "Sobrevaluado vs. zona" ? "bg-red-50" : ""}`}>
                <td className="py-1 pr-2">{i.titular}<div className="text-[10px] text-slate-400">{i.origen}</div></td>
                <td className="pr-2">{[i.finca_matricula && `Mat. ${i.finca_matricula}`, i.cta_cte_catastral && `Cta. Cte. ${i.cta_cte_catastral}`, i.padron_rural && `Padrón ${i.padron_rural}`, !i.cta_cte_catastral && !i.padron_rural && i.padron_catastro && `Padrón/Cta. ${i.padron_catastro}`].filter(Boolean).join(" | ") || "s/d"}{i.catastro_verificado && <span className="ml-1 text-emerald-700">✓ SNC</span>}</td>
                <td className="pr-2">{[i.distrito, i.departamento].filter(Boolean).join(" · ") || "s/zona"}{i.uso_cultivo && <div className="text-slate-500">{i.uso_cultivo}</div>}</td>
                <td className="pr-2 text-right" data-testid={`ac-inm-res-sup-${k}`}>{i.urbano ? `${Math.round(i.superficie_m2 || 0).toLocaleString("es-PY")} m²` : (i.superficie_ha || 0).toLocaleString("es-PY", { maximumFractionDigits: 1 })}</td>
                <td className="pr-2 text-right">{i.valor_usd ? <>{usd0(i.valor_usd)} <span className={`rounded px-1 text-[10px] font-semibold ${i.valor_origen === "referencia" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`ac-inm-res-origen-${k}`}>{i.valor_origen === "referencia" ? "referencia" : "tasación"}</span><div className="text-slate-500">{i.urbano ? `${n0(i.usd_por_m2)} US$/m²` : `${n0(i.usd_por_ha)} US$/ha`}{i.valor_origen === "referencia" ? ` · ${i.valor_fecha || ""}${i.valor_ref?.metodo ? ` · ${i.valor_ref.metodo}` : ""} · no computa como garantía` : ""}</div></> : "sin tasación"}{i.alerta_tasacion && !i.urbano && <div className="font-semibold text-red-700">⚠ {i.alerta_tasacion}</div>}
                  {onGuardar && <TasacionResultado i={i} k={k} c={conConstr(i, k)} setC={setC} inp={inp} busy={buscando || busy} onTasar={(v) => { const cc = conConstr(i, k); onGuardar([{ i, it: aplicarTasacion({ construccion_m2: cc.construccion_m2, nivel: cc.nivel, antiguedad: cc.antiguedad }, v) }]); }} onRef={() => buscarRef([k])} />}</td>
                <td className="pr-2">{i.hipoteca_gravamen}{i.gravamen_usd ? ` ${usd0(i.gravamen_usd)}` : ""}{i.acreedor && <div className="text-slate-500">{i.acreedor}</div>}<div className="text-emerald-800">Libre: {usd0(i.valor_libre_usd)}</div></td>
                <td data-testid={`ac-inm-res-ubi-${k}`}>{i.texto ? <span className={`font-semibold ${i.exacta ? "text-emerald-700" : "text-orange-600"}`}>{i.texto}</span> : i.maps_url ? <a href={i.maps_url} target="_blank" rel="noreferrer" className={`font-semibold underline ${i.precision === "exacta" ? "text-emerald-700" : i.precision === "bloque" ? "text-sky-700" : "text-amber-700"}`}>{i.precision === "exacta" ? "🛰️ Parcela catastral exacta (ver satélite)" : i.precision === "bloque" ? `🛰️ ${i.precision_fuente || "Ubicación Satelital (bloque de padrones)"}` : `📍 Zona aprox. (${i.precision_fuente || "falta N° Padrón en informe"})`}</a> : <span className="text-slate-500">sin ubicación ({i.precision_fuente || "falta N° Padrón"})</span>}{(i.lat && (i.lon || i.lng)) ? <div className="text-[10px] text-slate-500">{i.lat}, {i.lon || i.lng}</div> : i.coordenadas && <div className="text-[10px] text-slate-500">{i.coordenadas}</div>}{i.ref_geo && !i.coordenadas && <div className="text-[10px] text-slate-500">{i.ref_geo}</div>}<div className="mt-0.5 flex flex-wrap gap-2 text-[10px]">{(i.link_maps || i.maps_url) && <a href={i.link_maps || i.maps_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">Ver en Google Maps</a>}<a href={linkVisorSNC(i)} target="_blank" rel="noreferrer" className="text-blue-700 underline" onClick={() => { try { navigator.clipboard?.writeText([i.cta_cte_catastral, i.padron_rural || i.padron_catastro, i.distrito].filter(Boolean).join(" ")); } catch (e) { /* sin portapapeles */ } }}>🔍 Ver en Visor Catastro SNC</a>{i.precision !== "exacta" && onFijar && <button type="button" className="text-emerald-700 underline" onClick={() => onFijar(i)}>Pegar pin / Padrón</button>}{onGuardar && <button type="button" disabled={buscando} className="text-sky-700 underline disabled:opacity-50" onClick={() => verSat(k)} data-testid={`ac-inm-res-sat-${k}`}>🛰️ Ver satélite</button>}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
const NIVELES_C = [["economica", "Económica"], ["estandar", "Estándar"], ["buena", "Buena"], ["alta", "Alta-moderna"], ["galpon", "Galpón"], ["tinglado", "Tinglado"]];
function TasacionResultado({ i, k, c, setC, inp, busy, onTasar, onRef }) {
  const [tas, setTas] = useState("");
  const [abierto, setAbierto] = useState(false);
  const guardar = () => { if (tas.trim() === "") return; onTasar(String(Math.round(aNumEs(tas) || 0))); setTas(""); };
  return (
    <div className="mt-1 flex flex-col items-end gap-0.5 text-[10px]">
      <div className="flex items-center gap-1">
        <input value={tas} onChange={(e) => setTas(e.target.value)} onKeyDown={(e) => e.key === "Enter" && guardar()} onBlur={guardar} placeholder="Tasación US$" className={inp + " w-24 text-right"} disabled={busy} data-testid={`ac-inm-res-tas-${k}`} />
      </div>
      {i.valor_origen !== "tasacion" && <button type="button" onClick={onRef} disabled={busy} className="font-semibold text-amber-700 underline disabled:opacity-50" data-testid={`ac-inm-res-ref-${k}`}>{i.valor_origen === "referencia" ? "Actualizar referencia" : "Valor de referencia"}</button>}
      <button type="button" onClick={() => setAbierto(!abierto)} className="text-slate-500 underline" data-testid={`ac-inm-res-constr-${k}`}>Construcción {c.construccion_m2 ? `(${c.construccion_m2} m²)` : ""} {abierto ? "▾" : "▸"}</button>
      {abierto && <div className="flex flex-wrap items-center justify-end gap-1">
        <input value={c.construccion_m2 || ""} onChange={(e) => setC(k, "construccion_m2", e.target.value)} placeholder="m² constr." className={inp + " w-20 text-right"} data-testid={`ac-inm-res-m2-${k}`} />
        <select value={c.nivel || ""} onChange={(e) => setC(k, "nivel", e.target.value)} className={inp} data-testid={`ac-inm-res-nivel-${k}`}><option value="">Nivel</option>{NIVELES_C.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input value={c.antiguedad || ""} onChange={(e) => setC(k, "antiguedad", e.target.value)} placeholder="Años" className={inp + " w-14 text-right"} data-testid={`ac-inm-res-antig-${k}`} />
      </div>}
    </div>
  );
}
function GarantiasRealesCard({ g, onUsarSNC }) {
  const [verJson, setVerJson] = useState(false);
  const rs = g.resumen_garantias_reales || {}; const d = g.dictamen_riesgo || {};
  const [color, titulo] = GR_UI[rs.calificacion_garantia] || ["gris", "Sin calificar"];
  const met = [["Tasación (sistema)", rs.sin_tasacion ? (rs.referencia_usd ? `Sin tasación · Ref. ${usd0(rs.referencia_usd)} (valor de referencia, no es tasación)` : "Sin tasación") : usd0(rs.total_tasacion_sistema_usd)], ["Inscripto a favor de Tecnomyl", usd0(rs.total_garantia_nominal_empresa_usd)],
    ["Acreedores preferentes", usd0(rs.total_deuda_acreedores_preferentes_usd) + (rs.acreedores_delante ? ` (${rs.acreedores_delante})` : "") + (rs.preferentes_peor_caso_usd > rs.total_deuda_acreedores_preferentes_usd ? ` · registro: ${usd0(rs.preferentes_peor_caso_usd)}` : "")],
    ["Ejecutable neto", rs.sin_tasacion ? (rs.referencia_usd ? `${usd0(rs.ejecutable_estimado_usd)} estimado con valor de referencia (cobertura Tecnomyl est. ${usd0(rs.cobertura_estimada_usd)})${rs.ejecutable_peor_caso_usd != null ? ` · peor caso registro ${usd0(rs.ejecutable_peor_caso_usd)}` : ""}` : "Sin tasación — no se puede medir cobertura") : usd0(rs.cobertura_ejecutable_neta_usd)]];
  const ord = (r) => ({ 1: "1er", 2: "2do", 3: "3er" }[r] || `${r}º`);
  const enc = `Garantías reales (hipotecas) · ${rs.bloques} escritura${rs.bloques !== 1 ? "s" : ""} · ${ord(rs.rango_empresa || 1)} rango${rs.total_deuda_acreedores_preferentes_usd ? ` (detrás de ${rs.acreedores_delante || "otro acreedor"} ${usd0(rs.total_deuda_acreedores_preferentes_usd)})` : ""}${rs.sin_tasacion ? ` · sin tasación${rs.referencia_usd ? ` (ref. ${usd0(rs.referencia_usd)})` : ""}` : ""} · ${rs.suma_a_linea_credito ? `suma a la línea ${usd0(rs.cobertura_ejecutable_neta_usd)}` : "no suma a la línea"}`;
  return (
    <section className="relative rounded-[30px] border border-[#D9E3D6] bg-[#FFFFFF] p-6 text-[#24331F] overflow-hidden" data-testid="ac-garantias-reales" data-calificacion={rs.calificacion_garantia} data-color={color}>
      <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-[#D9E3D6]">
        <div className="flex items-center gap-3 min-w-0"><img src="/logo-tecnomyl.png" alt="Tecnomyl" className="h-7 w-auto tm-logo" /><div className="min-w-0"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 leading-snug" data-testid="ac-garantias-encabezado">{enc}</div><h3 className="text-lg font-semibold leading-snug" data-testid="ac-garantias-titulo">{titulo}</h3></div></div>
        <div className="text-right shrink-0"><div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Garantía real</div><div className="text-sm font-bold whitespace-nowrap">{(g.bloques_inmuebles || []).length ? `Esc. N° ${(g.bloques_inmuebles || []).map((b) => b.escritura_nro).filter(Boolean).join(", ")}` : `${rs.bloques || 0} escritura(s)`}</div></div>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div><div className="text-slate-500">Fecha</div><div className="font-bold">{(g.bloques_inmuebles || []).map((b) => b.fecha).filter(Boolean).join(" · ") || "—"}</div></div>
        <div><div className="text-slate-500">Rango de Tecnomyl</div><div className="font-bold">{ord(rs.rango_empresa || 1)} rango</div></div>
        <div><div className="text-slate-500">Acreedor delante</div><div className="font-bold">{rs.acreedores_delante || "Ninguno"}</div></div>
      </div>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div className="relative min-h-[96px] min-w-[220px] flex-1">
          <div className={`tm-sello ${SELLO_UI[rs.calificacion_garantia]?.[1] || "text-slate-500"}`} data-testid="ac-garantias-sello">{SELLO_UI[rs.calificacion_garantia]?.[0] || "SIN CALIFICAR"}</div>
        </div>
        <dl className="grid grid-cols-1 gap-y-1.5 text-xs min-w-[260px] max-w-full" data-testid="ac-garantias-metricas">
          {met.map(([k, v], i) => <div key={k} className={`flex items-start justify-between gap-4 ${i === met.length - 1 ? "border-t-[3px] border-[#24331F] pt-2 mt-1" : ""}`}><dt className="text-slate-500 shrink-0">{k}</dt><dd className={`text-right ${i === met.length - 1 ? "text-base font-extrabold tabular-nums" : "font-semibold"}`}>{v}</dd></div>)}
        </dl>
      </div>
      <span className="sr-only" data-testid="ac-garantias-suma">{rs.suma_a_linea_credito ? `Suma a la línea: ${usd0(rs.cobertura_ejecutable_neta_usd)}` : "NO suma a la línea de crédito"}</span>
      <div className={`mt-4 inline-flex rounded-full px-3.5 py-1.5 text-xs font-bold ${rs.suma_a_linea_credito ? "bg-[#007336] text-[#F3F7F2]" : "tm-negra"}`}>{rs.suma_a_linea_credito ? `Suma a la línea: ${usd0(rs.cobertura_ejecutable_neta_usd)}` : "NO suma a la línea de crédito"}</div>
      {rs.alertas?.length > 0 && <ul className="mt-4 tm-tile tm-tile-rojo flex-col gap-1 text-xs font-medium list-disc pl-9" data-testid="ac-garantias-alertas">{rs.alertas.map((a, i) => <li key={i}>{a}</li>)}</ul>}
      <div className="mt-3 space-y-2 text-sm">
        <p data-testid="ac-garantias-motivo"><b>Dictamen:</b> {rs.sin_tasacion ? rs.sintesis : d.motivo_dictamen}</p>
        <p className="text-xs"><b>Utilidad remanente:</b> {d.utilidad_comercial_remanente}</p>
        <p className="text-xs"><b>Recomendación:</b> {d.recomendacion_operativa}</p>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="ac-garantias-bloques">
          <thead><tr className="text-left text-slate-500 uppercase text-[10px] tracking-wider"><th className="py-1 pr-2">Escritura</th><th className="pr-2">Distrito · superficie</th><th className="pr-2">Finca / padrón · inscripto</th><th className="pr-2 text-right">Tasación</th><th className="pr-2 text-right">Inscripto</th><th className="pr-2">Preferentes</th><th>Cobertura</th></tr></thead>
          <tbody>
            {(g.bloques_inmuebles || []).map((b, i) => (
              <tr key={i} className="border-t border-[#D9E3D6] align-top">
                <td className="py-1 pr-2 whitespace-nowrap">N° {b.escritura_nro} · {b.fecha}<div className="opacity-70">{b.escribana}</div></td>
                <td className="pr-2">{b.distrito}<div className="opacity-70">{b.superficie_total_ha} ha · {b.rango_empresa}º rango{b.nota_rango ? ` (${b.nota_rango})` : ""}</div></td>
                <td className="pr-2">{(b.fincas_padrones || []).join(", ")}</td>
                <td className="pr-2 text-right whitespace-nowrap">{b.tasacion_usd ? usd0(b.tasacion_usd) : "sin tasación"}{b.tasacion_fecha && <div className="opacity-70">{b.tasacion_fecha}</div>}</td>
                <td className="pr-2 text-right whitespace-nowrap">{usd0(b.monto_garantia_empresa_usd)}{(b.detalle_inscripto || []).length > 1 && b.detalle_inscripto.map((x, j) => <div key={j} className="opacity-70">{x}</div>)}</td>
                <td className="pr-2">{(b.acreedores_preferentes || []).map((a, j) => <div key={j}>{a.rango}R {a.acreedor}: {usd0(a.monto_usd)}</div>)}</td>
                <td>{b.estado_cobertura}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UbicacionSNC onUsar={onUsarSNC} />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" className="text-[11px] underline opacity-70" onClick={() => setVerJson(!verJson)} data-testid="ac-garantias-json-toggle">{verJson ? "Ocultar JSON" : "Ver JSON"}</button>
        <button type="button" className="text-[11px] underline opacity-70" onClick={() => { navigator.clipboard.writeText(JSON.stringify(g, null, 2)); toast.success("JSON copiado"); }}>Copiar JSON</button>
      </div>
      {verJson && <pre className="mt-2 max-h-72 overflow-auto rounded-[18px] tm-negra p-3 text-[11px]" data-testid="ac-garantias-json">{JSON.stringify(g, null, 2)}</pre>}
    </section>
  );
}

/* Auditoría reputacional y compliance: semáforo, resumen del modelo y enlaces revisados */
const COMP_UI = {
  BAJO: { color: "verde", titulo: "Sin antecedentes relevantes", cls: "bg-emerald-50 border-emerald-300 text-emerald-900", dot: "bg-emerald-500" },
  MEDIO: { color: "amarillo", titulo: "Controversias o menciones dudosas", cls: "bg-amber-50 border-amber-300 text-amber-900", dot: "bg-amber-500" },
  ALTO: { color: "rojo", titulo: "Antecedentes graves: revisión manual", cls: "bg-red-50 border-red-400 text-red-900", dot: "bg-red-600" },
};
function ComplianceCard({ comp, ajuste, busy, onVerificar, ruc, setRuc }) {
  const nivel = comp?.nivel_riesgo || null;
  const ui = COMP_UI[nivel] || { titulo: comp ? "Sin verificar" : "Verificando antecedentes…", cls: "bg-slate-50 border-slate-300 text-slate-700", dot: "bg-slate-400" };
  const [verTodos, setVerTodos] = useState(false);
  const enlaces = (comp?.enlaces || []).filter((e) => verTodos || e.relevante);
  return (
    <section className={`rounded-xl border p-4 ${ui.cls}`} data-testid="ac-compliance" data-nivel={nivel || "sin_verificar"}>
      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-block h-3.5 w-3.5 rounded-full ${ui.dot} ${!comp && busy ? "animate-pulse" : ""}`} data-testid="ac-compliance-badge" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Auditoría reputacional y compliance{nivel ? ` · riesgo ${nivel}` : ""}</div>
          <h3 className="text-base font-semibold" data-testid="ac-compliance-titulo">{ui.titulo}</h3>
        </div>
        <input value={ruc} onChange={(e) => setRuc(e.target.value)} placeholder="RUC" className="h-8 w-32 rounded border border-current/30 bg-white/70 px-2 text-xs text-slate-800" data-testid="ac-compliance-ruc" />
        <Button size="sm" variant="outline" onClick={onVerificar} disabled={busy} className="bg-white/80" data-testid="ac-compliance-reverificar">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />}{comp?.nivel_riesgo ? "Re-verificar" : "Verificar antecedentes"}
        </Button>
      </div>
      {comp?.resumen && <p className="mt-2 text-sm" data-testid="ac-compliance-resumen">{comp.resumen}</p>}
      {ajuste?.texto && <p className="mt-2 rounded-md bg-white/70 px-3 py-2 text-sm font-medium" data-testid="ac-compliance-ajuste">{ajuste.texto}</p>}
      {nivel === "ALTO" && <p className="mt-2 text-xs font-semibold" data-testid="ac-compliance-alerta">⚠ Alerta: no vender ni ampliar línea hasta que un responsable revise estos antecedentes.</p>}
      {(comp?.documentacion_sugerida || []).length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs" data-testid="ac-compliance-docs">{comp.documentacion_sugerida.map((d, i) => <li key={i}>{d}</li>)}</ul>
      )}
      {(comp?.enlaces || []).length > 0 && (
        <div className="mt-3 text-xs">
          <div className="mb-1 flex items-center gap-2 font-semibold opacity-80">Enlaces analizados ({comp.enlaces.length})
            {comp.enlaces.some((e) => !e.relevante) && <button type="button" className="underline opacity-70" onClick={() => setVerTodos(!verTodos)}>{verTodos ? "ocultar homónimos" : "ver también homónimos/descartados"}</button>}
          </div>
          <ul className="space-y-1" data-testid="ac-compliance-enlaces">
            {enlaces.map((e, i) => (
              <li key={i} className={e.relevante ? "" : "opacity-60"}>
                <a href={e.url} target="_blank" rel="noreferrer" className="font-medium underline">{e.titulo || e.url}</a>
                {e.motivo && <span className="opacity-80"> — {e.motivo}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {comp && <div className="mt-2 text-[11px] opacity-60">{comp.n_resultados != null && `${comp.n_resultados} resultados revisados · `}{comp.vigente_hasta && `vale hasta ${fmtFecha(comp.vigente_hasta)} · `}{comp.aviso_buscador || ""}</div>}
    </section>
  );
}

const fmtFecha = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const finVigencia = (iso) => {
  if (!iso || iso.length < 10) return "";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/* Miles con punto mientras se escribe (50000 → 50.000); la coma queda para los decimales (7300,5 → 7.300,5). */
const milesEs = (txt) => {
  const t = String(txt ?? "").replace(/[^\d,]/g, "");
  const i = t.indexOf(",");
  const ent = (i >= 0 ? t.slice(0, i) : t).replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return i >= 0 ? `${ent},${t.slice(i + 1).replace(/,/g, "").slice(0, 2)}` : ent;
};
const desdeNum = (v) => (v === null || v === undefined || v === "" ? "" : milesEs(String(v).replace(".", ",")));
const aNumEs = (t) => { const n = parseFloat(String(t ?? "").replace(/\./g, "").replace(",", ".")); return Number.isFinite(n) ? n : null; };

const numTxt = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toLocaleString("es-PY"));
const aNum = (t) => { const n = parseFloat(String(t).replace(/\./g, "").replace(",", ".")); return Number.isFinite(n) ? n : null; };

const inpSol = "h-8 rounded border border-slate-200 px-2 text-xs";
const DPTO_LETRA_SNC = { concepcion: "A", "san pedro": "B", cordillera: "C", guaira: "D", caaguazu: "E", itapua: "F", caazapa: "G", misiones: "H", paraguari: "I", "alto parana": "J", central: "K", neembucu: "L", amambay: "M", canindeyu: "N", "presidente hayes": "P", boqueron: "Q", "alto paraguay": "R", capital: "S", asuncion: "S" };
function linkVisorSNC(i) {
  if (i.link_visor) return i.link_visor;
  const m = String(i.finca_matricula || i.numero || "").toUpperCase().match(/([A-S])\s?0*(\d{1,2})\s*[/-]\s*\d+/);
  const cod = String(i.codigo_distrito_snc || "").match(/^([A-S])(\d{2})$/);
  const depto = String(i.departamento || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const dpto = m ? m[1] : cod ? cod[1] : Object.entries(DPTO_LETRA_SNC).find(([k]) => depto.includes(k))?.[1];
  const dist = m ? Number(m[2]) : cod ? Number(cod[2]) : null;
  const padron = String(i.padron_ref || i.padron_rural || i.padron || "").match(/\d+/)?.[0];
  const cta = String(i.cta_cte_catastral || i.padron_catastro || i.padron || "").match(/(\d{1,3})\s*-\s*(\d{1,4})\s*-\s*(\d{1,4})/);
  if (dpto && dist && cta) return `https://www.catastro.gov.py/visor/?dpto=${dpto}&dist=${dist}&zona=${Number(cta[1])}&manzana=${Number(cta[2])}&lote=${Number(cta[3])}`;
  if (dpto && dist && padron) return `https://www.catastro.gov.py/visor/?dpto=${dpto}&dist=${dist}&padron=${padron}`;
  return "https://www.catastro.gov.py/visor/?snc=geo";
}


function Chk({ on, onChange, children, id }) {
  return (
    <label className="flex items-start gap-2 text-sm text-slate-800"><input type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)} className="mt-1" data-testid={id} /><span>{children}</span></label>
  );
}
function Bien({ sol, setIn, k, nombre }) {
  const inp = inpSol;
  return (
    <div>
      <Chk on={sol[k]?.on} onChange={(v) => setIn(k, "on", v)} id={`ac-sol-${k}`}><b>{nombre}</b></Chk>
      {sol[k]?.on && (
        <div className="ml-6 mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          Importe activo con nosotros US$
          <input value={numTxt(sol[k]?.importe)} onChange={(e) => setIn(k, "importe", aNum(e.target.value))} inputMode="decimal" className={inp + " w-28 text-right"} />
          Rango
          <select value={sol[k]?.rango || 1} onChange={(e) => setIn(k, "rango", parseInt(e.target.value, 10))} className={inp}>
            <option value={1}>1°</option><option value={2}>2°</option><option value={3}>3°</option>
          </select>
          {(sol[k]?.rango || 1) > 1 && (<>Deudas anteriores sobre el bien US$ <input value={numTxt(sol[k]?.gravamenes)} onChange={(e) => setIn(k, "gravamenes", aNum(e.target.value))} inputMode="decimal" className={inp + " w-28 text-right"} /></>)}
        </div>
      )}
    </div>
  );
}

/* Casilla de garantías que solicitamos al cliente (se tildan; las de bien llevan importe activo con nosotros y rango) */
function SolicitadasPanel({ sol, setSol, esc, otorgada, onUsar, busy }) {
  const inp = inpSol;
  const set = (k, v) => setSol({ ...sol, [k]: v });
  const setIn = (k, campo, v) => setSol({ ...sol, [k]: { ...(sol[k] || {}), [campo]: v } });
  // la primera combinación (de la más simple a la más fuerte) que alcanza para la línea que se quiere dar
  const alcanza = otorgada > 0 && esc ? esc.filas.find((f) => f.linea >= Math.min(otorgada, esc.tope) - 0.5) : null;
  return (
    <div className="mt-3 grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
    <div className="space-y-2" data-testid="ac-solicitadas">
      <div className="text-xs font-semibold text-[#0f5233]">Garantías que solicitamos <span className="font-normal text-slate-500">(tildá las que pedimos; con ellas se calcula la línea recomendada)</span></div>
      <Chk on={sol.pagare_deudor} onChange={(v) => set("pagare_deudor", v)} id="ac-sol-deudor"><b>1. Pagaré a sola firma</b></Chk>
      <Chk on={sol.codeudoria} onChange={(v) => set("codeudoria", v)} id="ac-sol-codeudoria"><b>2. Pagaré con codeudoría</b></Chk>
      <div>
        <Chk on={sol.terceros?.on} onChange={(v) => setIn("terceros", "on", v)} id="ac-sol-terceros"><b>3. Pagarés de terceros</b></Chk>
        {sol.terceros?.on && (
          <div className="ml-6 mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
            Se exigen sobre lo que supere US$
            <input value={numTxt(sol.terceros?.desde)} onChange={(e) => setIn("terceros", "desde", aNum(e.target.value))} inputMode="decimal" placeholder="vacío = toda la línea" className={inp + " w-32 text-right"} data-testid="ac-sol-terceros-desde" />
            <span className="text-slate-400">(ej.: línea US$ 500.000 y se exigen desde US$ 200.000 → pagarés de terceros por US$ 300.000)</span>
          </div>
        )}
      </div>
      <Bien sol={sol} setIn={setIn} k="hipoteca" nombre="4. Hipoteca" />
      <Bien sol={sol} setIn={setIn} k="prenda" nombre="5. Prenda" />
      <div>
        <Chk on={sol.granos?.on} onChange={(v) => setIn("granos", "on", v)} id="ac-sol-granos"><b>6. Contrato de granos</b> <span className="text-[11px] font-normal text-slate-500">(no ejecutable: sirve para seguir la cosecha, no suma a la línea)</span></Chk>
        {sol.granos?.on && (
          <div className="ml-6 mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
            Importe estimado US$ <input value={numTxt(sol.granos?.importe)} onChange={(e) => setIn("granos", "importe", aNum(e.target.value))} inputMode="decimal" className={inp + " w-28 text-right"} />
          </div>
        )}
      </div>
    </div>
    <div className="rounded-md border border-slate-200 bg-white p-2.5" data-testid="ac-escenarios-sol">
      <div className="text-xs font-semibold text-[#0f5233]">Cuánta línea podemos dar con cada garantía <span className="font-normal text-slate-500">(de la más simple a la más fuerte)</span></div>
      {!esc ? (
        <p className="mt-2 text-xs text-slate-500">Tocá «Analizar» y acá aparece cuánta línea se puede dar solo con pagaré, con codeudoría, con pagarés de terceros y con cada bien.</p>
      ) : (
        <>
          <table className="mt-1 w-full text-xs">
            <tbody>
              {esc.filas.map((f) => {
                const es = alcanza && alcanza.garantia === f.garantia;
                return (
                  <tr key={f.garantia} className={"border-t border-slate-100 align-top " + (es ? "bg-emerald-50" : "")}>
                    <td className="py-1 pr-2">
                      <div className={"font-semibold " + (f.tipo === "bien" ? "text-slate-700" : "text-slate-800")}>{f.garantia}</div>
                      <div className="text-[10px] text-slate-500">{f.como}</div>
                      {es && <div className="text-[10px] font-semibold text-emerald-700">✔ Con esto alcanza para la línea de {usd(otorgada)}</div>}
                    </td>
                    <td className="py-1 pr-2 text-right whitespace-nowrap"><b className="text-[#0f5233]">{usd(f.linea)}</b>{f.al_tope && <div className="text-[10px] text-amber-700">máximo prudente</div>}</td>
                    <td className="py-1 text-right">
                      {f.marcar && Object.keys(f.marcar).length > 0 && (
                        <button type="button" disabled={busy} onClick={() => onUsar(f.marcar)} className="rounded border border-[#0f5233]/40 px-2 py-0.5 text-[10px] text-[#0f5233] hover:bg-emerald-50 disabled:opacity-50">Pedir esta</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {otorgada > esc.tope + 0.5 && <p className="mt-1 text-[10px] text-red-700">La línea de {usd(otorgada)} pasa el máximo prudente ({usd(esc.tope)}): ni con todas las garantías conviene dar tanto.</p>}
          {otorgada > 0 && !alcanza && otorgada <= esc.tope + 0.5 && <p className="mt-1 text-[10px] text-amber-700">Ninguna combinación llega a {usd(otorgada)}.</p>}
        </>
      )}
    </div>
    </div>
  );
}

function LineaCredito({ res, hist, setHist, guardarHist }) {
  const ln = res.linea;
  const v = LINEA_V[ln.veredicto] || { box: "border-slate-300 bg-slate-50", txt: "text-slate-700", t: "LÍNEA SUGERIDA" };
  const exBase = ln.exposicion;
  const lineaFinal = res.ia?.linea_recomendada || ln.plan?.objetivo || ln.sugerida;
  const ex = { ...exBase, disponible: (lineaFinal || 0) - (exBase.total || 0) };
  const upd = (i, k, val) => setHist(hist.map((h, j) => (j === i ? { ...h, [k]: val, origen: "manual" } : h)));
  const inp = "h-8 rounded border border-slate-200 px-2 text-xs";
  return (
    <div className="space-y-3" data-testid="ac-linea">
      <div className="text-sm font-semibold text-slate-800">Línea de crédito y garantías{ln.vigencia ? <span className="ml-2 text-xs font-normal text-slate-500">vigencia {ln.vigencia.texto}</span> : null}</div>
      <div className={`rounded-lg border-2 p-4 grid gap-3 md:grid-cols-[1fr_1fr_2fr] ${v.box}`}>
        <div><div className="text-[11px] text-slate-500">Línea que otorgamos hoy</div><div className="text-lg font-bold text-[#0f5233]">{ln.otorgada ? usd(ln.otorgada) : "Sin línea aún"}</div></div>
        <div><div className="text-[11px] text-slate-500">Línea que recomendamos</div><div className="text-lg font-bold text-[#0f5233]" data-testid="ac-sugerida"><Cuenta valor={ln.sugerida} fmt={usd} /></div></div>
        <div><div className={`text-sm font-bold ${v.txt}`}>{v.t}</div><p className="text-sm text-slate-700">{ln.consejo || "Cargá la línea otorgada o marcá «Sin línea aún»."}</p></div>
      </div>
      {ln.prudencia && ln.prudencia.cfg?.activa ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700" data-testid="ac-prudencia">
          <div className="font-semibold text-[#0f5233] mb-1">🛡️ Máximo prudente: {usd(ln.prudencia.tope)} <span className="font-normal text-slate-500">— el menor de estos topes (no queremos ser su proveedor principal)</span></div>
          <div className={"grid gap-2 " + (ln.prudencia.hectareas_det ? "md:grid-cols-4" : "md:grid-cols-3")}>
            {[["patrimonio", "Por patrimonio", `${ln.pct_tope}% de lo que tiene la empresa`],
              ["proveedores", "Por participación", ln.prudencia.proveedores != null ? `${ln.prudencia.cfg.prov_pct}% de lo que debe a todos sus proveedores` : "el balance no muestra deudas con proveedores"],
              ["necesidad", "Por lo que nos compra", ln.prudencia.base_necesidad ? `${ln.prudencia.base_txt} (${usd(ln.prudencia.base_necesidad)}) + ${ln.prudencia.cfg.crec_pct}%` : ln.prudencia.base_txt || "—"],
              ...(ln.prudencia.hectareas_det ? [["hectareas", "Por hectárea", `${ln.prudencia.hectareas_det.detalle.map((d) => `${d.nombre} ${Math.round(d.ha).toLocaleString("es-PY")} ha`).join(", ")}: gasta ${usd(ln.prudencia.hectareas_det.insumos_total)} en insumos; financiamos ${ln.prudencia.hectareas_det.part_pct}%`]] : [])].map(([k, t, d]) => (
              <div key={k} className={"rounded-md border p-2 " + (ln.prudencia.limita === k ? "border-[#0f5233] bg-emerald-50" : "border-slate-200")}>
                <div className="text-[11px] text-slate-500">{t}{ln.prudencia.limita === k ? " · manda este" : ""}</div>
                <div className="text-base font-bold text-[#0f5233]">{ln.prudencia[k] != null ? usd(ln.prudencia[k]) : "—"}</div>
                <div className="text-[10px] text-slate-500">{d}</div>
              </div>
            ))}
          </div>
          {!ln.prudencia.base_necesidad && <p className="mt-1 text-[10px] text-amber-700">Si ya nos compra, cargá el «Historial de pagos»: el tope se ajusta a lo que realmente nos compra.</p>}
          {ln.prudencia.penalizacion && <p className="mt-1 text-[11px] text-amber-700">⏳ Tiene {usd(ln.prudencia.penalizacion.monto_31_60)} con 31 a 60 días de atraso (liquidación esperando fijación): el máximo baja {ln.prudencia.penalizacion.pct}% (de {usd(ln.prudencia.penalizacion.antes)}), sin cambiar su calificación.</p>}
        </div>
      ) : null}
      {ln.cupos && <CuposCard cu={ln.cupos} />}

      {ln.escenarios && (
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="ac-escenarios">
          <div className="text-xs font-semibold text-[#0f5233] mb-2">Cuánta línea podemos dar según la garantía</div>
          <table className="w-full max-w-3xl text-sm">
            <tbody>
              {ln.escenarios.filas.map((f) => (
                <tr key={f.garantia} className="border-t border-slate-100 align-top">
                  <td className="py-1.5 pr-3"><div className="font-semibold text-slate-800">{f.garantia}</div><div className="text-[11px] text-slate-500">{f.como}</div></td>
                  <td className="py-1.5 text-right tabular-nums whitespace-nowrap"><b className="text-[#0f5233]">{usd(f.linea)}</b>{f.al_tope && <div className="text-[10px] text-amber-700">tope prudente</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ln.escenarios.bienes.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold text-slate-600">Con un bien en garantía (se suma a pagaré con codeudoría):</div>
              <ul className="mt-1 text-[12.5px] text-slate-700 space-y-1 list-disc pl-4">{ln.escenarios.bienes.map((b) => <li key={b.garantia}><b>{b.garantia}:</b> {b.texto.replace(/^Sobre la base[^.]*\. /, "")}</li>)}</ul>
            </div>
          )}
          <p className="mt-2 text-[11px] text-slate-500">{ln.escenarios.nota}</p>
        </div>
      )}

      {ln.plan && (
        <div className="rounded-lg border-2 border-[#0f5233]/40 bg-white p-3" data-testid="ac-plan">
          <div className="text-xs font-semibold text-[#0f5233] mb-1">Qué garantía pedir y por cuánto</div>
          <p className="text-sm text-slate-800">{ln.plan.recomendacion}</p>
          {ln.plan.pasos?.length > 0 && (
            <ol className="mt-2 flex flex-wrap items-center gap-1.5 text-xs" data-testid="ac-escalera">
              {ln.plan.pasos.map((p, i) => (
                <li key={p.garantia} className="flex items-center gap-1.5">
                  {i > 0 && <span className="font-bold text-[#0f5233]">+</span>}
                  <span className="rounded-full border border-[#0f5233]/40 bg-emerald-50 px-2.5 py-1 text-[#0f5233]"><b>{p.garantia}</b>{i > 0 ? ` · US$ ${Math.round(p.valor).toLocaleString("es-PY")}` : ""}</span>
                </li>
              ))}
            </ol>
          )}
          {ln.plan.faltante > 0 && ln.plan.opciones.length > 0 && (
            <div className="mt-2 text-[11px] text-slate-500">Alternativas para cubrir lo que falta después de pagaré + codeudoría:</div>
          )}
          {ln.plan.faltante > 0 && ln.plan.opciones.length > 0 && (
            <table className="mt-2 w-full max-w-2xl text-xs">
              <thead><tr className="bg-slate-50 text-slate-500"><th className="px-2 py-1 text-left">Garantía</th><th className="px-2 py-1 text-center">Cuenta</th><th className="px-2 py-1 text-right">Valor mínimo a pedir</th></tr></thead>
              <tbody>
                {ln.plan.opciones.map((o) => (
                  <tr key={o.tipo} className={"border-t border-slate-100 " + (o.recomendada ? "bg-emerald-50 font-semibold" : "")}>
                    <td className="px-2 py-1">{o.tipo}{o.recomendada ? " (recomendada)" : ""}{o.nota ? <span className="font-normal text-slate-500"> · {o.nota}</span> : null}</td><td className="px-2 py-1 text-center">{num(o.factor, 0)}%</td><td className="px-2 py-1 text-right tabular-nums">{usd(o.valor_a_pedir)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <ul className="mt-2 text-sm text-slate-700 space-y-1 list-disc pl-4">{ln.plan.razones.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="text-xs font-semibold text-[#0f5233] mb-2">Cómo llegamos a la línea recomendada</div>
        <table className="text-sm w-full max-w-xl">
          <tbody>
            <tr><td className="py-0.5">Confianza por balance ({ln.pct_confianza}% del patrimonio)</td><td className="text-right tabular-nums">{usd(ln.confianza)}</td></tr>
            <tr><td className="py-0.5">+ Garantías (lo que cuentan)</td><td className="text-right tabular-nums">{usd(ln.total_garantias_computable)}</td></tr>
            <tr><td className="py-0.5">= Suma</td><td className="text-right tabular-nums">{usd(ln.suma)}</td></tr>
            <tr><td className="py-0.5">Máximo prudente {ln.prudencia?.limita === "hectareas" ? "(por hectárea)" : ln.prudencia?.limita && ln.prudencia.limita !== "patrimonio" ? "(no ser su proveedor principal)" : `(${ln.pct_tope}% del patrimonio)`}</td><td className="text-right tabular-nums">{usd(ln.tope)}</td></tr>
            <tr className="font-bold text-[#0f5233] border-t border-slate-200"><td className="py-1">Línea recomendada (la menor de las dos)</td><td className="text-right tabular-nums">{usd(ln.sugerida)}</td></tr>
          </tbody>
        </table>
        <ul className="mt-2 text-sm text-slate-700 space-y-1 list-disc pl-4">{ln.razones.map((t) => <li key={t}>{t}</li>)}</ul>
      </div>

      {ex.total > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <div className="text-xs font-semibold text-[#0f5233] mb-1">Deuda vigente y uso de la línea</div>
          <div className="grid gap-x-6 grid-cols-2 sm:grid-cols-5 gap-y-1">
            <div><div className="text-[11px] text-slate-500">Nos debe hoy (facturado)</div><b>{usd(ex.deuda)}</b></div>
            <div><div className="text-[11px] text-slate-500">Pedidos sin facturar (no es deuda)</div><b>{usd(ex.pedidos)}</b>{(ex.pedidos_detalle || []).map((p) => <div key={p.cliente} className="text-[10px] text-slate-400">de «{p.cliente}» (Pedidos de Ventas)</div>)}</div>
            <div><div className="text-[11px] text-slate-500">Comprometido de la línea</div><b>{usd(ex.total)}</b></div>
            <div><div className="text-[11px] text-slate-500">De la deuda, vencido</div><b className={ex.vencido > 0 ? "text-red-600" : ""}>{usd(ex.vencido)}</b></div>
            <div><div className="text-[11px] text-slate-500">{ex.disponible >= 0 ? "Disponible de la línea" : "Excede la línea en"}</div><b className={ex.disponible >= 0 ? "text-emerald-700" : "text-red-600"}>{usd(Math.abs(ex.disponible))}</b></div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="text-xs font-semibold text-[#0f5233]">Cómo respaldan la línea las garantías que pedimos</div>
          <p className="text-[11px] text-slate-500">De cada garantía se cuenta solo una parte: si un día hay que cobrarla, se recupera menos que lo que dice el papel.</p>
        </div>
        {ln.garantias.length === 0 ? <p className="px-3 py-4 text-sm text-slate-400">No se marcó ninguna garantía.</p> : (
          <>
            <table className="w-full text-sm" data-testid="ac-garantias-claro">
              <thead><tr className="bg-slate-50 text-[11px] text-slate-500"><th className="px-3 py-1.5 text-left">Garantía</th><th className="px-3 py-1.5 text-right">Por cuánto</th><th className="px-3 py-1.5 text-right">Cuenta para la línea</th><th className="px-3 py-1.5 text-left">Por qué</th></tr></thead>
              <tbody>
                {ln.garantias.map((g, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2"><div className="font-semibold text-slate-800">{g.tipo}{g.vigente && <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">YA LA TENEMOS</span>}</div>{g.detalle && <div className="text-[11px] text-slate-500">{g.detalle}</div>}</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{usd(g.valor)}{g.gravamenes ? <div className="text-[10px] text-slate-500">ya debe {usd(g.gravamenes)} a otro</div> : null}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {g.computable > 0 ? <><b className="text-[#0f5233]">suma {usd(g.computable)}</b><div className="text-[10px] text-slate-500">el {num(g.factor, 0)}% de su valor</div></> : <span className="text-slate-500">no suma</span>}
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-600">{g.explica}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              Lo que damos en confianza por el balance <b>{usd(ln.confianza)}</b> + lo que suman las garantías <b>{usd(ln.total_garantias_computable)}</b> = <b>{usd(ln.suma)}</b>.
              {" "}{ln.suma > ln.tope + 0.5 ? <>Como no pasamos del máximo prudente, la línea queda en <b>{usd(ln.sugerida)}</b>.</> : <>La línea recomendada es <b>{usd(ln.sugerida)}</b>.</>}
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="ac-historial">
        <div className="text-xs font-semibold text-[#0f5233] mb-2">Líneas que dimos en vigencias anteriores y qué garantías nos dio <span className="font-normal text-slate-400">(cada línea dura 1 año, por ejemplo 01/10/2025 al 30/09/2026)</span></div>
        <div className="space-y-1.5">
          {hist.map((h, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-1.5">
              <input type="date" value={h.desde || ""} onChange={(e) => upd(i, "desde", e.target.value)} className={inp + " w-36"} title="Desde cuándo rigió la línea (dura 1 año)" />
              <span className="text-[11px] text-slate-500 w-24">{h.desde ? `hasta ${fmtFecha(finVigencia(h.desde))}` : ""}</span>
              <input type="number" value={h.linea} onChange={(e) => upd(i, "linea", e.target.value)} className={inp + " w-32 text-right"} placeholder="Línea US$" />
              <span className="text-[11px] text-slate-500 w-28">{res.M[String((h.desde || "").slice(0, 4) - 1)]?.pn ? `${num(h.linea / res.M[String((h.desde || "").slice(0, 4) - 1)].pn * 100, 1)}% del patrimonio` : ""}</span>
              <input value={h.comentario || ""} onChange={(e) => upd(i, "comentario", e.target.value)} className={inp + " flex-1 min-w-[160px]"} placeholder="Comentario (por qué se otorgó o ajustó)" />
              <span className={`rounded px-1.5 py-0.5 text-[10px] ${h.origen && h.origen !== "manual" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"}`} data-testid={`ac-hist-origen-${i}`}>{h.origen || "manual"}</span>
              <button onClick={() => setHist(hist.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              <div className="flex w-full flex-wrap items-center gap-1 pl-1 text-[11px] text-slate-500" data-testid={`ac-hist-gar-${i}`}>
                Garantías que dio:
                {GAR_HIST.map((g) => {
                  const on = (h.garantias || []).includes(g);
                  return (
                    <button key={g} type="button" onClick={() => upd(i, "garantias", on ? (h.garantias || []).filter((x) => x !== g) : [...(h.garantias || []), g])}
                      className={"rounded-full border px-2 py-0.5 " + (on ? "border-[#0f5233] bg-emerald-50 text-[#0f5233] font-semibold" : "border-slate-200 bg-white text-slate-500")}>{on ? "✔ " : ""}{g}</button>
                  );
                })}
                <span className="ml-1">valor US$</span>
                <input value={h.garantia_monto ? Math.round(h.garantia_monto).toLocaleString("es-PY") : ""} onChange={(e) => upd(i, "garantia_monto", Number(String(e.target.value).replace(/\D/g, "")) || 0)}
                  inputMode="numeric" placeholder="0" className="h-7 w-24 rounded border border-slate-200 px-2 text-right text-[11px]" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="outline" onClick={() => setHist([...hist, { desde: `${new Date().getFullYear() - 1}-10-01`, linea: "", comentario: "" }])} className="gap-1"><Plus className="h-4 w-4" /> Agregar vigencia</Button>
          <Button size="sm" onClick={guardarHist} className="bg-[#0f5233] hover:bg-[#0c3f27]">Guardar historial</Button>
        </div>
      </div>
    </div>
  );
}

/* Sector elegido: umbrales (editables por empresa), fuente de capacidad de pago, plazo y garantías típicas. */
function SectorInfo({ s, abierto, setAbierto, onGuardado }) {
  const inp = "h-8 rounded border border-slate-200 px-2 text-xs";
  const [u, setU] = useState(s.umbrales);
  useEffect(() => { setU(s.umbrales); }, [s]);
  const guardar = async () => { try { await api.put("/analisis-cliente/sectores", { sector: s.k, umbrales: u }); toast.success("Umbrales del sector guardados"); onGuardado?.(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  return (
    <div className="text-[11px] text-slate-600 mt-1 space-y-1" data-testid="ac-sector-info">
      <p><b>Capacidad de pago:</b> {s.capacidad} <b>Plazo:</b> {s.plazo} <b>Garantías típicas:</b> {s.garantias.join(", ")}. <i>{s.estacionalidad?.texto}</i></p>
      <button type="button" className="underline text-emerald-800" onClick={() => setAbierto(!abierto)} data-testid="ac-sector-umbrales-btn">{abierto ? "Ocultar umbrales" : "Ver / editar umbrales del sector"}</button>
      {abierto && (
        <div className="flex flex-wrap items-end gap-2 rounded bg-slate-50 p-2" data-testid="ac-sector-umbrales">
          {Object.entries(s.umbrales_txt || {}).filter(([k]) => u[k] !== undefined).map(([k, t]) => (
            <label key={k} className="text-[10px] text-slate-500">{t}<input type="number" step="0.1" value={u[k]} onChange={(e) => setU({ ...u, [k]: e.target.value })} className={inp + " block w-24"} data-testid={`ac-sector-u-${k}`} /></label>
          ))}
          <Button size="sm" className="h-8 bg-[#0f5233] text-white" onClick={guardar} data-testid="ac-sector-u-guardar">Guardar</Button>
        </div>
      )}
    </div>
  );
}
